import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import JobPriorities from './JobPriorities';
import { 
  Users, 
  Settings, 
  Shield, 
  Edit, 
  Trash2, 
  Plus, 
  X,
  Check,
  AlertTriangle,
  UserPlus,
  Mail,
  Phone,
  Briefcase,
  MapPin,
  User,
  Search,
  ChevronLeft,
  ChevronRight,
  Upload,
  Download,
  Bell,
  ToggleLeft,
  ToggleRight,
  Globe,
  Key,
  Copy,
  Coffee,
  Clock,
  CheckSquare
} from 'lucide-react';

const DEFAULT_GLOBAL_CUTOFF_TIMES = ['05:30', '08:30', '12:00', '15:00', '17:30'];
const FIXED_CUTOFF_TIMEZONE = 'Asia/Kolkata';

// Cafeteria meals. Must match MEALS in cafeteria_routes.py.
const MEAL_KEYS = ['lunch', 'dinner', 'snacks'];
const MEAL_LABELS = { lunch: 'Lunch', dinner: 'Dinner', snacks: 'Snacks' };

/**
 * Coerce a stored per-meal opt-out into a full {meal: bool} map, where true
 * means the meal is switched off. Absent/partial data means "takes the meal",
 * so a user created before this feature keeps all three.
 */
const normaliseMealsOff = (value) => {
  const src = value && typeof value === 'object' ? value : {};
  return MEAL_KEYS.reduce((acc, m) => ({ ...acc, [m]: src[m] === true }), {});
};

const AdminPanel = () => {
  const { token, user } = useAuth();
  const isSuperAdmin = user?.super_admin === true;
  // Tabs only a Super Admin may open. Normal admins never see or reach these.
  const SUPER_ADMIN_ONLY_TABS = [2, 3, 5, 8, 13, 9, 10, 12, 14, 15, 17];
  const [activeTab, setActiveTab] = useState(0);
  const [resourcesSubTab, setResourcesSubTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [clients, setClients] = useState([]);
  const [productionLocations, setProductionLocations] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openTeamDialog, setOpenTeamDialog] = useState(false);
  const [openClientDialog, setOpenClientDialog] = useState(false);
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;
  const [currentUser, setCurrentUser] = useState({
    name: '',
    role: 'preflight',
    email: '',
    ad_username: '',
    phone: '',
    status: 'active',
    password: ''
  });
  const [currentTeam, setCurrentTeam] = useState({
    name: '',
    type: 'preflight',
    lead_ids: [],
    members: [],
    location_ids: []
  });
  const [currentClient, setCurrentClient] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    team_id: '',
    preflight_enabled: true,
    single_app_approval_enabled: true,
    epson_enabled: false
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingTeam, setIsEditingTeam] = useState(false);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [isEditingLocation, setIsEditingLocation] = useState(false);

  const [currentLocation, setCurrentLocation] = useState({
    id: '',
    name: ''
  });

  // Error Categories Management
  const [errorCategories, setErrorCategories] = useState([]);
  const [newErrorCategory, setNewErrorCategory] = useState('');
  const [newErrorCategoryType, setNewErrorCategoryType] = useState('internal');
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Job Nature Management
  const [jobNatureList, setJobNatureList] = useState([]);
  const [openJobNatureDialog, setOpenJobNatureDialog] = useState(false);
  const [currentJobNature, setCurrentJobNature] = useState({ name: '' });
  const [isEditingJobNature, setIsEditingJobNature] = useState(false);
  const [jobNatureLoading, setJobNatureLoading] = useState(false);

  // Job Type Management
  const [jobTypeList, setJobTypeList] = useState([]);
  const [openJobTypeDialog, setOpenJobTypeDialog] = useState(false);
  const [currentJobType, setCurrentJobType] = useState({ name: '' });
  const [isEditingJobType, setIsEditingJobType] = useState(false);
  const [jobTypeLoading, setJobTypeLoading] = useState(false);

  // Task completion API request configuration
  const [apiRequestsConfig, setApiRequestsConfig] = useState({});
  const [apiRequestsLoading, setApiRequestsLoading] = useState(false);
  const [apiRequestsDirty, setApiRequestsDirty] = useState(false);
  const [integrationConfig, setIntegrationConfig] = useState({
    api_base_url: '',
  });
  const [workflowAutomationConfig, setWorkflowAutomationConfig] = useState({
    global_cutoff: {
      enabled: false,
      cutoff_times: DEFAULT_GLOBAL_CUTOFF_TIMES,
      timezone: 'Asia/Kolkata',
      allow_manual_priority_ranking: true,
      enable_cutoff_jobs_tab: true,
      active_job_badge_countdown: true
    },
    client_cutoff_rules: [],
    alerts: {
      cutoff_completed_before_deadline: true,
      auto_priority_changed: true,
      non_priority_started_while_priority_waiting: true,
      priority_job_hold_or_paused: true,
      cutoff_missed: true,
      delayed_past_deadline: true
    }
  });
  const [skillAutoAssignConfig, setSkillAutoAssignConfig] = useState({
    auto_assign_enabled: false,
    minimum_proficiency: 'intermediate',
    scoring_weights: {
      proficiency: 50,
      workload: 30,
      cutoff_urgency: 20
    },
    skill_definitions: [],
    user_skill_mappings: [],
    task_skill_mappings: [],
    user_complexity_mappings: [], // legacy, retained for backwards compatibility
    // Department-segregated skill matrix (operators x department skills).
    // Each cell is a boolean: true = operator can do that skill.
    skill_matrix: {}, // { [user_id]: { [skill_id]: true } }
    request_job_enabled: true,
    request_queue_enabled: true,
    cutoff_aware_matching: true,
    fallback_alert_notify_tl_pm: true,
    audit_log_enabled: true
  });
  const [advancedOptionsSaving, setAdvancedOptionsSaving] = useState(false);
  const [advancedOptionsSaved, setAdvancedOptionsSaved] = useState(false);
  const [newGlobalCutoffTime, setNewGlobalCutoffTime] = useState('');
  // Skill matrix view filters
  const [skillDeptFilter, setSkillDeptFilter] = useState('all');
  const [skillRoleFilter, setSkillRoleFilter] = useState('all');
  const [skillNameFilter, setSkillNameFilter] = useState('');
  // Result of the last skill-matrix CSV upload, shown until it is saved.
  const [skillCsvSummary, setSkillCsvSummary] = useState(null);
  const skillCsvInputRef = useRef(null);
  // Client cutoff table: filters + CSV upload result.
  const [cutoffClientFilter, setCutoffClientFilter] = useState('');
  const [cutoffStatusFilter, setCutoffStatusFilter] = useState('all');
  const [cutoffCsvSummary, setCutoffCsvSummary] = useState(null);
  const cutoffCsvInputRef = useRef(null);
  const [newUserSkillMapping, setNewUserSkillMapping] = useState({ user_id: '', skill_name: '', proficiency: 'beginner' });
  const [newTaskSkillMapping, setNewTaskSkillMapping] = useState({ task_name: '', skill_name: '', minimum_proficiency: 'intermediate' });
  const [selectedApiTask, setSelectedApiTask] = useState('Preflight');
  const [selectedApiAction, setSelectedApiAction] = useState('completed');
  const [newApiUrl, setNewApiUrl] = useState('');

  // Single global API key for all outbound webhooks (X-API-Key).
  const [integrationApiKey, setIntegrationApiKey] = useState('');
  const [apiKeyBusy, setApiKeyBusy] = useState(false);

  // Entries may be a plain URL string (legacy) or {url}. Normalise to the URL.
  const apiEntryUrl = (e) => (typeof e === 'string' ? e : (e?.url || ''));

  // Shift timings (templates) state
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [shiftTemplatesSaving, setShiftTemplatesSaving] = useState(false);
  const [shiftTemplatesSaved, setShiftTemplatesSaved] = useState(false);

  // Meal cutoffs + vendor email state (cafeteria)
  const [mealSettings, setMealSettings] = useState({
    cutoffs: { lunch: '11:00', dinner: '18:00', snacks: '16:00' },
    enabled: true,
    meal_emails_enabled: false,
    meal_emails: {
      lunch: { enabled: true, recipients: '', send_time: '' },
      dinner: { enabled: true, recipients: '', send_time: '' },
      snacks: { enabled: true, recipients: '', send_time: '' },
    },
    collection_reports_enabled: false,
    collection_report_offset_hours: 2,
    collection_reports: {
      lunch: { enabled: true, recipients: '', send_time: '' },
      dinner: { enabled: true, recipients: '', send_time: '' },
      snacks: { enabled: true, recipients: '', send_time: '' },
    },
  });
  const [mealSettingsSaving, setMealSettingsSaving] = useState(false);
  const [mealSettingsSaved, setMealSettingsSaved] = useState(false);
  // Break allowances (minutes per break type) — drives the break-time block.
  const [breakLimits, setBreakLimits] = useState({ lunch: 47, dinner: 17, snacks: 17 });
  // Shift length past which the worker is asked for a reason at clock-out.
  // 615 = 10h 15m.
  const [longShiftMinutes, setLongShiftMinutes] = useState(615);
  const [breakLimitsSaving, setBreakLimitsSaving] = useState(false);
  const [breakLimitsSaved, setBreakLimitsSaved] = useState(false);
  const [mealEmailTest, setMealEmailTest] = useState('');

  // Bulk-complete projects state
  const [bulkJobText, setBulkJobText] = useState('');
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const parseJobCodes = (text) =>
    Array.from(new Set(
      (text || '')
        .split(/[\s,;\n\r\t]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    ));

  const runBulkComplete = async () => {
    const codes = parseJobCodes(bulkJobText);
    if (codes.length === 0) { alert('Paste or upload at least one job number.'); return; }
    if (!window.confirm(`Force-complete ${codes.length} project(s)? This moves them to Completed Projects and cannot be undone.`)) return;
    try {
      setBulkRunning(true);
      setBulkResult(null);
      const res = await api.post('/admin/projects/bulk-complete',
        { job_codes: codes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBulkResult(res.data);
    } catch (e) {
      alert(e.response?.data?.error || 'Bulk complete failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const onBulkCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setBulkJobText(String(ev.target?.result || ''));
    reader.readAsText(file);
    e.target.value = '';
  };

  // Leave types state
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [newLeaveType, setNewLeaveType] = useState('');
  const [leaveTypesSaving, setLeaveTypesSaving] = useState(false);
  const [leaveTypesSaved, setLeaveTypesSaved] = useState(false);

  // Leave entitlements (categories + per-user assignment) state
  const [leaveCategories, setLeaveCategories] = useState([]);
  const [leaveCategoriesSaving, setLeaveCategoriesSaving] = useState(false);
  const [leaveCategoriesSaved, setLeaveCategoriesSaved] = useState(false);
  const [usersLeaveCat, setUsersLeaveCat] = useState([]);

  // Client CSV import state
  const [openClientCsvDialog, setOpenClientCsvDialog] = useState(false);
  const [clientCsvText, setClientCsvText] = useState('');
  const [clientCsvProcessing, setClientCsvProcessing] = useState(false);
  const [clientCsvResult, setClientCsvResult] = useState(null);

  // User CSV import state
  const [openUserCsvDialog, setOpenUserCsvDialog] = useState(false);
  const [userCsvText, setUserCsvText] = useState('');
  const [userCsvProcessing, setUserCsvProcessing] = useState(false);
  const [userCsvResult, setUserCsvResult] = useState(null);

  // SMTP Settings state
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'VP Prepress Reports',
    smtp_use_tls: true,
    weekly_report_day: 'monday',
    weekly_report_time: '08:00',
    weekly_report_enabled: true,
    daily_user_time_tracking_report_enabled: false,
    daily_user_time_tracking_report_time: '08:00',
    daily_user_time_tracking_report_subject: 'Daily All Users Time Tracking Report',
    daily_user_time_tracking_report_recipients: '',
    break_override_email_enabled: false,
    break_override_email_subject: 'Break Block Cleared',
    break_override_email_recipients: '',
    late_login_email_enabled: false,
    late_login_email_subject: 'Late Shift Start',
    late_login_email_recipients: '',
    attendance_report_enabled: false,
    attendance_report_time: '09:00',
    attendance_report_subject: 'Daily Late & Break Overrun Report',
    attendance_report_recipients: '',
    attendance_report_send_when_empty: false,
    non_efficient_users_report_enabled: false,
    non_efficient_users_report_time: '08:10',
    non_efficient_users_report_subject: 'Daily Non Efficient Users Report',
    non_efficient_users_report_recipients: '',
    daily_report_enabled: false,
    daily_report_time: '08:00',
    monthly_report_enabled: false,
    monthly_report_day: 1,
    monthly_report_time: '08:00',
    // Today Job Information email defaults (times are entered in IST)
    today_job_info_email_enabled: false,
    today_job_info_email_time: '08:30',
    today_job_info_email_subject: 'Daily Job Information',
    today_job_info_email_recipients: '',
    // Daily User Completed Task Details email defaults (last business day)
    task_details_email_enabled: false,
    task_details_email_time: '08:35',
    task_details_email_subject: 'User Completed Task Details',
    task_details_email_recipients: '',
    external_error_report_enabled: false,
    external_error_report_time: '08:45',
    external_error_report_subject: 'Daily External Rejection Report',
    external_error_report_recipients: '',
    pending_jobs_email_enabled: false,
    pending_jobs_email_time: '08:30',
    pending_jobs_email_subject: 'Pending Jobs Report (Yesterday & Day Before)',
    pending_jobs_email_recipients: '',
    email_service_enabled: false
  });
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpTestEmail, setSmtpTestEmail] = useState('');
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  // Configuration state
  const [complexityConfig, setComplexityConfig] = useState({
    'simple_simple': {
      'Preflight': 15,
      'Single-up approval': 0,
      'Die creation': 5,
      'Recreation': 10,
      'CMD': 15,
      'CMD QC': 10,
      'CMD Path': 10,
      'Prepress , QC (self QC)': 15,
      'Layout approval': 0,
      'Image Export': 10,
      'Image Duplication': 10,
      'Mounting or RIP & Send QC': 10,
      'RIP QC': 10,
      'Epson Printout': 10,
      'Plate and Despatch': 10
    },
    'simple': {
      'Preflight': 15,
      'Single-up approval': 0,
      'Die creation': 5,
      'Recreation': 15,
      'CMD': 15,
      'CMD QC': 10,
      'CMD Path': 10,
      'Prepress , QC (self QC)': 20,
      'Layout approval': 0,
      'Image Export': 10,
      'Image Duplication': 10,
      'Mounting or RIP & Send QC': 15,
      'RIP QC': 15,
      'Epson Printout': 15,
      'Plate and Despatch': 15
    },
    'medium_simple': {
      'Preflight': 20,
      'Single-up approval': 0,
      'Die creation': 5,
      'Recreation': 20,
      'CMD': 20,
      'CMD QC': 15,
      'CMD Path': 15,
      'Prepress , QC (self QC)': 30,
      'Layout approval': 0,
      'Image Export': 15,
      'Image Duplication': 15,
      'Mounting or RIP & Send QC': 20,
      'RIP QC': 20,
      'Epson Printout': 15,
      'Plate and Despatch': 10
    },
    'medium': {
      'Preflight': 20,
      'Single-up approval': 0,
      'Die creation': 5,
      'Recreation': 20,
      'CMD': 30,
      'CMD QC': 20,
      'CMD Path': 20,
      'Prepress , QC (self QC)': 35,
      'Layout approval': 0,
      'Image Export': 20,
      'Image Duplication': 20,
      'Mounting or RIP & Send QC': 25,
      'RIP QC': 25,
      'Epson Printout': 20,
      'Plate and Despatch': 10
    },
    'complex': {
      'Preflight': 25,
      'Single-up approval': 0,
      'Die creation': 10,
      'Recreation': 25,
      'CMD': 45,
      'CMD QC': 30,
      'CMD Path': 30,
      'Prepress , QC (self QC)': 45,
      'Layout approval': 0,
      'Image Export': 30,
      'Image Duplication': 30,
      'Mounting or RIP & Send QC': 30,
      'RIP QC': 30,
      'Epson Printout': 40,
      'Plate and Despatch': 10
    },
    'super_complex': {
      'Preflight': 30,
      'Single-up approval': 0,
      'Die creation': 15,
      'Recreation': 45,
      'CMD': 60,
      'CMD QC': 40,
      'CMD Path': 40,
      'Prepress , QC (self QC)': 80,
      'Layout approval': 0,
      'Image Export': 40,
      'Image Duplication': 40,
      'Mounting or RIP & Send QC': 45,
      'RIP QC': 45,
      'Epson Printout': 45,
      'Plate and Despatch': 10
    },
    'combined_job': {
      'Preflight': 35,
      'Single-up approval': 0,
      'Die creation': 15,
      'Recreation': 40,
      'CMD': 75,
      'CMD QC': 45,
      'CMD Path': 45,
      'Prepress , QC (self QC)': 120,
      'Layout approval': 0,
      'Image Export': 45,
      'Image Duplication': 45,
      'Mounting or RIP & Send QC': 75,
      'RIP QC': 75,
      'Epson Printout': 45,
      'Plate and Despatch': 10
    },
    'rotary': {
      'Preflight': 0,
      'Single-up approval': 0,
      'Die creation': 0,
      'Recreation': 0,
      'CMD': 0,
      'CMD QC': 0,
      'CMD Path': 0,
      'Prepress , QC (self QC)': 5,
      'Layout approval': 0,
      'Image Export': 0,
      'Image Duplication': 0,
      'Mounting or RIP & Send QC': 0,
      'RIP QC': 5,
      'Epson Printout': 0,
      'Plate and Despatch': 0
    }
  });
  const [configLoading, setConfigLoading] = useState(false);

  const triggerDailyTimeTrackingReport = async () => {
    const confirmMsg = smtpTestEmail 
      ? `Send test Daily User Time Tracking report to ${smtpTestEmail}?`
      : 'Are you sure you want to send the Daily User Time Tracking report to ALL configured recipients?';
    if (!window.confirm(confirmMsg)) return;

    try {
      setSmtpLoading(true);
      setSmtpTestResult(null);
      const payload = smtpTestEmail ? { test_email: smtpTestEmail } : {};
      const response = await api.post('/reports/send-daily-user-time-tracking-report', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmtpTestResult({ success: true, message: response.data.message });
    } catch (error) {
      console.error('Error sending daily time tracking report:', error);
      setSmtpTestResult({ success: false, message: error.response?.data?.error || 'Failed to send report' });
    } finally {
      setSmtpLoading(false);
    }
  };

  const COMPLEXITY_ORDER = [
    'simple_simple',
    'simple',
    'medium_simple',
    'medium',
    'complex',
    'super_complex',
    'combined_job',
    'rotary'
  ];

  // Must stay aligned with backend /admin/config/complexity validation
  const VALID_TASK_NAMES = [
    'Preflight',
    'Single-up approval',
    'Die creation',
    'Recreation',
    'CMD',
    'CMD QC',
    'CMD Path',
    'Prepress , QC (self QC)',
    'Layout approval',
    'Image Export',
    'Image Duplication',
    'Mounting or RIP & Send QC',
    'RIP QC',
    'Epson Printout',
    'Plate and Despatch'
  ];

  // Legacy/alias keys that may exist in DB from older versions
  const TASK_NAME_ALIASES = {
    'Approval': 'Single-up approval',
    'Single App Approval': 'Single-up approval',
    'Single-up Approval': 'Single-up approval',
    'Layout Approval': 'Layout approval'
  };

  const normalizeTaskName = (taskName) => TASK_NAME_ALIASES[taskName] || taskName;

  const normalizeComplexityConfig = (rawConfig, fallbackConfig) => {
    const normalized = {};

    const baseKeys = COMPLEXITY_ORDER;
    const rawKeys = rawConfig && typeof rawConfig === 'object' && !Array.isArray(rawConfig)
      ? Object.keys(rawConfig)
      : [];

    // Preserve existing/known complexities in the configured display order,
    // but also keep any extra complexity levels that may exist in the config.
    // Exclude metadata fields that are not complexity rows.
    const META_KEYS = new Set(['updated_at', 'created_at', '_id']);
    const extraKeys = rawKeys.filter((key) => !baseKeys.includes(key) && !META_KEYS.has(key));
    const allKeys = [...baseKeys, ...extraKeys];

    for (const complexity of allKeys) {
      const rawRow = rawConfig?.[complexity];
      const fallbackRow = fallbackConfig?.[complexity] || {};
      const row = (rawRow && typeof rawRow === 'object' && !Array.isArray(rawRow)) ? rawRow : fallbackRow;

      const cleanedRow = {};
      for (const [taskName, minutes] of Object.entries(row || {})) {
        const normalizedName = normalizeTaskName(taskName);
        if (!VALID_TASK_NAMES.includes(normalizedName)) continue;
        const minutesInt = parseInt(minutes, 10);
        cleanedRow[normalizedName] = Number.isFinite(minutesInt) ? Math.max(0, minutesInt) : 0;
      }

      // Ensure all known tasks exist so the edit dialog can safely render
      for (const taskName of VALID_TASK_NAMES) {
        if (cleanedRow[taskName] === undefined) {
          const fallbackMinutes = fallbackRow?.[taskName];
          const minutesInt = parseInt(fallbackMinutes, 10);
          cleanedRow[taskName] = Number.isFinite(minutesInt) ? Math.max(0, minutesInt) : 0;
        }
      }

      normalized[complexity] = cleanedRow;
    }

    return normalized;
  };

  // New state for complexity row editing
  const [complexityRowEditDialog, setComplexityRowEditDialog] = useState({
    isOpen: false,
    complexity: null,
    values: {}
  });

  // New complexity name input for adding extra complexity levels
  const [newComplexityName, setNewComplexityName] = useState('');

  // Notification Alert Settings state
  const [notificationAlertSettings, setNotificationAlertSettings] = useState({
    manual: true,
    task_hold: true,
    task_pause: true,
    no_jobs: true,
    approval_rejected: true,
    approval_approved: true,
    task_overtime: true,
  });
  const [notifSettingsLoading, setNotifSettingsLoading] = useState(false);
  const [notifSettingsSaved, setNotifSettingsSaved] = useState(false);

  // Customer Accounts state
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState({
    name: '',
    email: '',
    password: '',
    client_name: '',
    secondary_emails: [],
    active: true,
    notification_preferences: {
      stage_change: true,
      completed: true,
      on_hold: true,
      approval_waiting: true,
      approval_result: true,
    }
  });
  const [customerNewSecEmail, setCustomerNewSecEmail] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerSaveError, setCustomerSaveError] = useState('');
  // Portal settings state
  const [portalSettings, setPortalSettings] = useState({
    portal_enabled: true,
    reminder_interval_minutes: 30,
    status_colors: {
      not_started: '#6b7280',
      in_progress: '#3b82f6',
      on_hold: '#f59e0b',
      completed: '#10b981',
      rejected: '#ef4444',
    }
  });
  const [portalSettingsSaving, setPortalSettingsSaving] = useState(false);
  const [portalSettingsSaved, setPortalSettingsSaved] = useState(false);

  useEffect(() => {
    if (token) {
      fetchUsers();
      fetchTeams();
      fetchClients();
      fetchComplexityConfig();
      fetchApiRequestsConfig();
      fetchIntegrationConfig();
      fetchProductionLocations();
      fetchErrorCategories();
      fetchJobNature();
      fetchJobType();
      fetchSmtpSettings();
      fetchNotificationAlertSettings();
      fetchCustomerAccounts();
      fetchPortalSettings();
      fetchShiftTemplates();
      fetchMealSettings();
      fetchBreakLimits();
      fetchLeaveTypes();
      fetchLeaveCategories();
      fetchUsersLeaveCat();
    }
  }, [token]);

  // Bounce a normal admin off any tab they're not allowed to open.
  useEffect(() => {
    if (!isSuperAdmin && SUPER_ADMIN_ONLY_TABS.includes(activeTab)) {
      setActiveTab(0);
    }
    // eslint-disable-next-line
  }, [activeTab, isSuperAdmin]);

  const fetchLeaveCategories = async () => {
    try {
      const response = await api.get('/admin/leave-categories', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveCategories(response.data?.categories || []);
    } catch (e) { /* leave empty */ }
  };

  const fetchUsersLeaveCat = async () => {
    try {
      const response = await api.get('/admin/users-leave-categories', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsersLeaveCat(response.data || []);
    } catch (e) { /* leave empty */ }
  };

  const addLeaveCategory = () => {
    setLeaveCategories(prev => [...prev, { id: '', name: '', quotas: {} }]);
    setLeaveCategoriesSaved(false);
  };

  const updateLeaveCategoryName = (idx, name) => {
    setLeaveCategories(prev => prev.map((c, i) => i === idx ? { ...c, name } : c));
    setLeaveCategoriesSaved(false);
  };

  const updateLeaveCategoryQuota = (idx, type, value) => {
    const num = value === '' ? 0 : Number(value);
    setLeaveCategories(prev => prev.map((c, i) =>
      i === idx ? { ...c, quotas: { ...(c.quotas || {}), [type]: num } } : c
    ));
    setLeaveCategoriesSaved(false);
  };

  const removeLeaveCategory = (idx) => {
    setLeaveCategories(prev => prev.filter((_, i) => i !== idx));
    setLeaveCategoriesSaved(false);
  };

  const saveLeaveCategories = async () => {
    for (const c of leaveCategories) {
      if (!(c.name || '').trim()) { alert('Every category needs a name.'); return; }
    }
    try {
      setLeaveCategoriesSaving(true);
      const response = await api.put('/admin/leave-categories',
        { categories: leaveCategories },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeaveCategories(response.data?.categories || leaveCategories);
      setLeaveCategoriesSaved(true);
      setTimeout(() => setLeaveCategoriesSaved(false), 2500);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to save categories');
    } finally {
      setLeaveCategoriesSaving(false);
    }
  };

  const assignUserLeaveCategory = async (userId, categoryId) => {
    setUsersLeaveCat(prev => prev.map(u => u.id === userId ? { ...u, leave_category: categoryId } : u));
    try {
      await api.put(`/admin/users/${userId}/leave-category`,
        { category_id: categoryId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to assign category');
      fetchUsersLeaveCat();
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const response = await api.get('/admin/leave-types', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveTypes(response.data?.types || []);
    } catch (e) {
      // leave empty on failure
    }
  };

  const addLeaveType = () => {
    const name = (newLeaveType || '').trim();
    if (!name) return;
    if (leaveTypes.some(t => t.toLowerCase() === name.toLowerCase())) {
      setNewLeaveType('');
      return;
    }
    setLeaveTypes(prev => [...prev, name]);
    setNewLeaveType('');
    setLeaveTypesSaved(false);
  };

  const removeLeaveType = (name) => {
    setLeaveTypes(prev => prev.filter(t => t !== name));
    setLeaveTypesSaved(false);
  };

  const saveLeaveTypes = async () => {
    if (leaveTypes.length === 0) { alert('Add at least one leave type.'); return; }
    try {
      setLeaveTypesSaving(true);
      const response = await api.put('/admin/leave-types',
        { types: leaveTypes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeaveTypes(response.data?.types || leaveTypes);
      setLeaveTypesSaved(true);
      setTimeout(() => setLeaveTypesSaved(false), 2500);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to save leave types');
    } finally {
      setLeaveTypesSaving(false);
    }
  };

  const fetchShiftTemplates = async () => {
    try {
      const response = await api.get('/admin/shift-templates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShiftTemplates(response.data?.templates || []);
    } catch (e) {
      // leave empty on failure
    }
  };

  const fetchMealSettings = async () => {
    try {
      const response = await api.get('/admin/meal-settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) setMealSettings(response.data);
    } catch (e) { /* keep defaults */ }
  };

  const fetchBreakLimits = async () => {
    try {
      const response = await api.get('/admin/break-settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.limits) setBreakLimits(response.data.limits);
      if (response.data?.long_shift_minutes) {
        setLongShiftMinutes(response.data.long_shift_minutes);
      }
    } catch (e) { /* keep defaults */ }
  };

  const saveBreakLimits = async () => {
    // Validate here so a typo is caught before it reaches the block logic.
    const limits = {};
    for (const bt of ['lunch', 'dinner', 'snacks']) {
      const v = parseFloat(breakLimits[bt]);
      if (!Number.isFinite(v) || v <= 0 || v > 480) {
        alert(`${bt.charAt(0).toUpperCase() + bt.slice(1)} allowance must be between 1 and 480 minutes.`);
        return;
      }
      limits[bt] = v;
    }
    const lsm = parseFloat(longShiftMinutes);
    if (!Number.isFinite(lsm) || lsm < 60 || lsm > 1440) {
      alert('Long shift limit must be between 1 and 24 hours.');
      return;
    }
    try {
      setBreakLimitsSaving(true);
      const res = await api.put('/admin/break-settings',
        { limits, long_shift_minutes: lsm }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      if (res.data?.limits) setBreakLimits(res.data.limits);
      if (res.data?.long_shift_minutes) setLongShiftMinutes(res.data.long_shift_minutes);
      setBreakLimitsSaved(true);
      setTimeout(() => setBreakLimitsSaved(false), 2500);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to save break allowances');
    } finally {
      setBreakLimitsSaving(false);
    }
  };

  const updateMealCutoff = (meal, value) => {
    setMealSettings(prev => ({ ...prev, cutoffs: { ...(prev.cutoffs || {}), [meal]: value } }));
    setMealSettingsSaved(false);
  };

  const saveMealSettings = async () => {
    try {
      setMealSettingsSaving(true);
      await api.put('/admin/meal-settings', mealSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMealSettingsSaved(true);
      setTimeout(() => setMealSettingsSaved(false), 2500);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to save meal settings');
    } finally {
      setMealSettingsSaving(false);
    }
  };

  const updateMealEmail = (meal, field, value) => {
    setMealSettings(prev => ({
      ...prev,
      meal_emails: {
        ...(prev.meal_emails || {}),
        [meal]: { ...((prev.meal_emails || {})[meal] || {}), [field]: value },
      },
    }));
    setMealSettingsSaved(false);
  };

  const updateCollectionReport = (meal, field, value) => {
    setMealSettings(prev => ({
      ...prev,
      collection_reports: {
        ...(prev.collection_reports || {}),
        [meal]: { ...((prev.collection_reports || {})[meal] || {}), [field]: value },
      },
    }));
    setMealSettingsSaved(false);
  };

  const sendMealTest = async (meal) => {
    if (!mealEmailTest) { alert('Enter a test email address.'); return; }
    try {
      const res = await api.post('/admin/meal-settings/send-test',
        { meal, test_email: mealEmailTest },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(res.data?.ok ? `Test ${meal} email sent to ${mealEmailTest}` : 'Email not sent (check SMTP settings / no recipients).');
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to send test email');
    }
  };

  const addShiftTemplate = () => {
    setShiftTemplates(prev => [
      ...prev,
      { id: '', name: '', start: '09:00', end: '18:00', color: '#93c5fd', meals: { lunch: false, dinner: false, snacks: false } }
    ]);
    setShiftTemplatesSaved(false);
  };

  const updateShiftTemplate = (idx, field, value) => {
    setShiftTemplates(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
    setShiftTemplatesSaved(false);
  };

  const toggleTemplateMeal = (idx, meal) => {
    setShiftTemplates(prev => prev.map((t, i) =>
      i === idx ? { ...t, meals: { ...(t.meals || {}), [meal]: !(t.meals || {})[meal] } } : t
    ));
    setShiftTemplatesSaved(false);
  };

  const removeShiftTemplate = (idx) => {
    setShiftTemplates(prev => prev.filter((_, i) => i !== idx));
    setShiftTemplatesSaved(false);
  };

  const saveShiftTemplates = async () => {
    // basic client-side validation
    for (const t of shiftTemplates) {
      if (!(t.name || '').trim()) { alert('Every shift needs a name.'); return; }
      const ok = (v) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v || '');
      if (!ok(t.start) || !ok(t.end)) { alert(`Invalid time on "${t.name}". Use HH:MM (24h).`); return; }
    }
    try {
      setShiftTemplatesSaving(true);
      const response = await api.put('/admin/shift-templates',
        { templates: shiftTemplates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShiftTemplates(response.data?.templates || shiftTemplates);
      setShiftTemplatesSaved(true);
      setTimeout(() => setShiftTemplatesSaved(false), 2500);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to save shift timings');
    } finally {
      setShiftTemplatesSaving(false);
    }
  };

  const fetchErrorCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await api.get('/admin/config/error-categories', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const items = Array.isArray(response.data) ? response.data : [];
      // Ensure each category has a type (legacy records default to internal)
      setErrorCategories(items.map(cat => ({
        ...cat,
        type: cat.type || 'internal'
      })));
    } catch (err) {
      console.error('Failed to fetch error categories:', err);
      setErrorCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleAddErrorCategory = async () => {
    const name = (newErrorCategory || '').trim();
    if (!name) {
      alert('Category name is required');
      return;
    }

    try {
      setCategoriesLoading(true);
      await api.post('/admin/config/error-categories', { name, type: newErrorCategoryType }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewErrorCategory('');
      setNewErrorCategoryType('internal');
      await fetchErrorCategories();
      alert('Error category added successfully!');
    } catch (error) {
      console.error('Error adding error category:', error);
      alert(error.response?.data?.error || 'Failed to add error category');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleEditErrorCategory = (category) => {
    setIsEditingCategory(true);
    setEditingCategoryId(category.id || category._id);
    setEditingCategoryName(category.name);
  };

  const handleSaveErrorCategory = async () => {
    const name = (editingCategoryName || '').trim();
    if (!name) {
      alert('Category name is required');
      return;
    }

    try {
      setCategoriesLoading(true);
      await api.put(`/admin/config/error-categories/${editingCategoryId}`, { name }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsEditingCategory(false);
      setEditingCategoryId(null);
      setEditingCategoryName('');
      await fetchErrorCategories();
      alert('Error category updated successfully!');
    } catch (error) {
      console.error('Error updating error category:', error);
      alert(error.response?.data?.error || 'Failed to update error category');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleDeleteErrorCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this error category?')) return;

    try {
      setCategoriesLoading(true);
      await api.delete(`/admin/config/error-categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchErrorCategories();
      alert('Error category deleted successfully!');
    } catch (error) {
      console.error('Error deleting error category:', error);
      alert(error.response?.data?.error || 'Failed to delete error category');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchJobNature = async () => {
    try {
      setJobNatureLoading(true);
      const response = await api.get('/admin/job-nature', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobNatureList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching job nature list:', error);
      setJobNatureList([]);
    } finally {
      setJobNatureLoading(false);
    }
  };

  const fetchJobType = async () => {
    try {
      setJobTypeLoading(true);
      const response = await api.get('/admin/job-type', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobTypeList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching job type list:', error);
      setJobTypeList([]);
    } finally {
      setJobTypeLoading(false);
    }
  };

  const handleOpenJobNatureDialog = (item = null) => {
    if (item) {
      setCurrentJobNature({ id: item.id || item._id, name: item.name });
      setIsEditingJobNature(true);
    } else {
      setCurrentJobNature({ name: '' });
      setIsEditingJobNature(false);
    }
    setOpenJobNatureDialog(true);
  };

  const handleCloseJobNatureDialog = () => {
    setOpenJobNatureDialog(false);
    setCurrentJobNature({ name: '' });
    setIsEditingJobNature(false);
  };

  const handleSaveJobNature = async () => {
    const name = (currentJobNature.name || '').trim();
    if (!name) {
      alert('Name is required');
      return;
    }

    try {
      setJobNatureLoading(true);
      if (isEditingJobNature) {
        await api.put(`/admin/job-nature/${currentJobNature.id}`, { name }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Job nature updated successfully!');
      } else {
        await api.post('/admin/job-nature', { name }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Job nature added successfully!');
      }
      await fetchJobNature();
      handleCloseJobNatureDialog();
    } catch (error) {
      console.error('Error saving job nature:', error);
      alert(error.response?.data?.error || 'Failed to save job nature');
    } finally {
      setJobNatureLoading(false);
    }
  };

  const handleDeleteJobNature = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job nature?')) return;

    try {
      setJobNatureLoading(true);
      await api.delete(`/admin/job-nature/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchJobNature();
      alert('Job nature deleted successfully!');
    } catch (error) {
      console.error('Error deleting job nature:', error);
      alert(error.response?.data?.error || 'Failed to delete job nature');
    } finally {
      setJobNatureLoading(false);
    }
  };

  const handleOpenJobTypeDialog = (item = null) => {
    if (item) {
      setCurrentJobType({ id: item.id || item._id, name: item.name });
      setIsEditingJobType(true);
    } else {
      setCurrentJobType({ name: '' });
      setIsEditingJobType(false);
    }
    setOpenJobTypeDialog(true);
  };

  const handleCloseJobTypeDialog = () => {
    setOpenJobTypeDialog(false);
    setCurrentJobType({ name: '' });
    setIsEditingJobType(false);
  };

  const handleSaveJobType = async () => {
    const name = (currentJobType.name || '').trim();
    if (!name) {
      alert('Name is required');
      return;
    }

    try {
      setJobTypeLoading(true);
      if (isEditingJobType) {
        await api.put(`/admin/job-type/${currentJobType.id}`, { name }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Job type updated successfully!');
      } else {
        await api.post('/admin/job-type', { name }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Job type added successfully!');
      }
      await fetchJobType();
      handleCloseJobTypeDialog();
    } catch (error) {
      console.error('Error saving job type:', error);
      alert(error.response?.data?.error || 'Failed to save job type');
    } finally {
      setJobTypeLoading(false);
    }
  };

  const handleDeleteJobType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job type?')) return;

    try {
      setJobTypeLoading(true);
      await api.delete(`/admin/job-type/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchJobType();
      alert('Job type deleted successfully!');
    } catch (error) {
      console.error('Error deleting job type:', error);
      alert(error.response?.data?.error || 'Failed to delete job type');
    } finally {
      setJobTypeLoading(false);
    }
  };

  // "12m over (lunch +12m)" style summary of how far past the break limit a user is.
  const formatBreakOverage = (u) => {
    const details = Array.isArray(u?.break_exceeded_details) ? u.break_exceeded_details : [];
    const fmt = (m) => {
      const mins = Math.round(Number(m) || 0);
      if (mins >= 60) {
        const h = Math.floor(mins / 60);
        const r = mins % 60;
        return r ? `${h}h ${r}m` : `${h}h`;
      }
      return `${mins}m`;
    };
    if (details.length === 0) {
      const total = Number(u?.break_over_minutes) || 0;
      return total > 0 ? `${fmt(total)} over the break limit` : '';
    }
    const parts = details.map((d) => `${d.type} +${fmt(d.over_min)} (${fmt(d.taken_min)}/${fmt(d.limit_min)})`);
    return `${fmt(u.break_over_minutes)} over — ${parts.join(', ')}`;
  };

  // "Lunch 40m, Snacks 10m, Dinner 30m (total 1h 20m)" — today's break usage.
  const formatBreakBreakdown = (u) => {
    const bd = u?.break_breakdown || {};
    const fmt = (m) => {
      const mins = Math.round(Number(m) || 0);
      if (mins >= 60) {
        const h = Math.floor(mins / 60);
        const r = mins % 60;
        return r ? `${h}h ${r}m` : `${h}h`;
      }
      return `${mins}m`;
    };
    const order = [['lunch', 'Lunch'], ['snacks', 'Snacks'], ['dinner', 'Dinner']];
    const parts = order.map(([k, label]) => `${label} ${fmt(bd[k] || 0)}`);
    const total = Number(u?.total_break_minutes) || 0;
    return `${parts.join(', ')} (total ${fmt(total)})`;
  };

  const clearBreakOverride = async (userId, userName, overageText) => {
    const extra = overageText ? `\n\n${overageText}` : '';
    if (!window.confirm(`Clear break overtime block for ${userName}?${extra}\n\nThey will be able to resume work immediately.`)) return;
    try {
      await api.post(`/admin/users/${userId}/clear-break-override`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Optimistically update local state so the icon changes instantly
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, break_blocked: false, break_override_cleared_today: true } : u));
      alert(`Break override cleared for ${userName}.`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to clear break override');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Map backend users to frontend structure
      const mappedUsers = response.data.map(user => ({
        _id: user.id,
        name: user.name,
        email: user.email,
        ad_username: user.ad_username || '',
        role: user.role,
        phone: user.phone || '',
        status: user.status || 'active',
        break_blocked: user.break_blocked || false,
        break_exceeded_details: user.break_exceeded_details || [],
        break_over_minutes: user.break_over_minutes || 0,
        break_breakdown: user.break_breakdown || { lunch: 0, dinner: 0, snacks: 0 },
        total_break_minutes: user.total_break_minutes || 0,
        break_override_cleared_today: user.break_override_cleared_today || false,
        break_block_exempt: user.break_block_exempt || false,
        cafeteria_meals_disabled: normaliseMealsOff(user.cafeteria_meals_disabled),
        auto_job_assign_enabled: user.auto_job_assign_enabled || false,
        repeat_orders_disabled: user.repeat_orders_disabled || false,
        super_admin: user.super_admin || false,
      }));
      setUsers(mappedUsers);
    } catch (err) {
      console.error('Failed to fetch users');
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await api.get('/admin/teams', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
    } catch (err) {
      console.error('Failed to fetch teams');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/admin/clients', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (err) {
      console.error('Failed to fetch clients');
    }
  };

  const fetchProductionLocations = async () => {
    try {
      const response = await api.get('/admin/production-locations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductionLocations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch production locations');
      setProductionLocations([]);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setCurrentUser({ ...user, password: '' });
      setIsEditing(true);
    } else {
      setCurrentUser({
        name: '',
        role: 'preflight',
        email: '',
        ad_username: '',
        phone: '',
        status: 'active',
        password: '',
        daily_report_enabled: true,
        // New users take all three meals until someone says otherwise.
        cafeteria_meals_disabled: normaliseMealsOff(null)
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };



  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSaveUser = async () => {
    try {
      // Validate required fields
      if (!currentUser.name || !currentUser.name.trim()) {
        alert('Name is required');
        return;
      }
      if (!isEditing && (!currentUser.password || !currentUser.password.trim())) {
        alert('Password is required for new users');
        return;
      }
      if (!currentUser.email?.trim() && !currentUser.ad_username?.trim()) {
        alert('Either Email or AD Username is required (Email is used for login by non-AD users)');
        return;
      }

      const payload = {
        name: currentUser.name,
        email: currentUser.email,
        ad_username: currentUser.ad_username,
        role: currentUser.role,
        phone: currentUser.phone || '',
        status: currentUser.status,
        daily_report_enabled: currentUser.daily_report_enabled !== false,
        break_block_exempt: currentUser.break_block_exempt === true,
        cafeteria_meals_disabled: normaliseMealsOff(currentUser.cafeteria_meals_disabled),
        auto_job_assign_enabled: currentUser.auto_job_assign_enabled === true,
        repeat_orders_disabled: currentUser.repeat_orders_disabled === true,
        super_admin: currentUser.role === 'admin' && currentUser.super_admin === true
      };

      if (currentUser.password) {
        payload.password = currentUser.password;
      }

      console.log('Saving user with payload:', payload);

      if (isEditing) {
        await api.put(`/admin/users/${currentUser._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await api.post('/admin/users', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      alert('User saved successfully!');
      fetchUsers();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving user:', error);
      console.error('Error details:', error.response?.data);
      
      // Format error message
      let errorMsg = 'Failed to save user';
      if (error.response?.data?.details) {
        const details = error.response.data.details;
        errorMsg = Object.values(details).join(', ');
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      }
      alert(errorMsg);
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/admin/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user');
      }
    }
  };

  // Team Management Functions
  const handleOpenTeamDialog = (team = null) => {
    if (team) {
      // Convert single lead_id to array format if needed for backwards compatibility
      const teamData = {
        ...team,
        lead_ids: team.lead_ids || (team.lead_id ? [team.lead_id] : []),
        location_ids: Array.isArray(team.location_ids) ? team.location_ids : []
      };
      setCurrentTeam(teamData);
      setIsEditingTeam(true);
    } else {
      setCurrentTeam({
        name: '',
        type: 'preflight',
        lead_ids: [],
        members: [],
        location_ids: []
      });
      setIsEditingTeam(false);
    }
    setOpenTeamDialog(true);
  };

  const handleCloseTeamDialog = () => {
    setOpenTeamDialog(false);
  };

  const handleSaveTeam = async () => {
    try {
      if (isEditingTeam) {
        await api.put(`/admin/teams/${currentTeam.id}`, currentTeam, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await api.post('/admin/teams', currentTeam, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      fetchTeams();
      handleCloseTeamDialog();
    } catch (error) {
      console.error('Error saving team:', error);
      alert(error.response?.data?.error || 'Failed to save team');
    }
  };

  const handleDeleteTeam = async (id) => {
    if (window.confirm('Are you sure you want to delete this team?')) {
      try {
        await api.delete(`/admin/teams/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchTeams();
      } catch (error) {
        console.error('Error deleting team:', error);
        alert('Failed to delete team');
      }
    }
  };

  // Client Management Functions
  const handleOpenClientDialog = (client = null) => {
    if (client) {
      setCurrentClient({
        ...client,
        // Default to enabled for legacy clients that don't have these flags yet
        preflight_enabled: client.preflight_enabled !== false,
        single_app_approval_enabled: client.single_app_approval_enabled !== false,
        // Epson is the opposite — off unless explicitly turned on.
        epson_enabled: client.epson_enabled === true
      });
      setIsEditingClient(true);
    } else {
      setCurrentClient({
        name: '',
        email: '',
        phone: '',
        address: '',
        team_id: '',
        preflight_enabled: true,
        single_app_approval_enabled: true,
        epson_enabled: false
      });
      setIsEditingClient(false);
    }
    setOpenClientDialog(true);
  };

  const handleCloseClientDialog = () => {
    setOpenClientDialog(false);
  };

  const handleSaveClient = async () => {
    try {
      if (isEditingClient) {
        await api.put(`/admin/clients/${currentClient.id}`, currentClient, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await api.post('/admin/clients', currentClient, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      fetchClients();
      handleCloseClientDialog();
    } catch (error) {
      console.error('Error saving client:', error);
      alert(error.response?.data?.error || 'Failed to save customer');
    }
  };

  const handleDeleteClient = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await api.delete(`/admin/clients/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
        alert('Failed to delete customer');
      }
    }
  };

  // Production Location Management
  const handleOpenLocationDialog = (location = null) => {
    if (location) {
      setCurrentLocation({
        id: location.id || location._id || '',
        name: location.name || ''
      });
      setIsEditingLocation(true);
    } else {
      setCurrentLocation({ id: '', name: '' });
      setIsEditingLocation(false);
    }
    setOpenLocationDialog(true);
  };

  const handleCloseLocationDialog = () => {
    setOpenLocationDialog(false);
  };

  const handleSaveLocation = async () => {
    try {
      const name = (currentLocation.name || '').trim();
      if (!name) {
        alert('Location name is required');
        return;
      }

      if (isEditingLocation && currentLocation.id) {
        await api.put(`/admin/production-locations/${currentLocation.id}`, { name }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await api.post('/admin/production-locations', { name }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      fetchProductionLocations();
      handleCloseLocationDialog();
    } catch (error) {
      console.error('Error saving production location:', error);
      alert(error.response?.data?.error || 'Failed to save location');
    }
  };

  const handleDeleteLocation = async (id) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await api.delete(`/admin/production-locations/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchProductionLocations();
      } catch (error) {
        console.error('Error deleting production location:', error);
        alert(error.response?.data?.error || 'Failed to delete location');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'busy': return 'bg-red-100 text-red-800';
      case 'offline': return 'bg-gray-100 text-gray-800';
      case 'resigned': return 'bg-amber-100 text-amber-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'pm': return 'Project Manager';
      case 'admin': return 'Admin';
      case 'preflight': return 'Preflight';
      case 'preflight_lead': return 'Preflight Lead';
      case 'preflight_prepress': return 'Preflight & Prepress';
      case 'cmd': return 'CMD';
      case 'cmd_lead': return 'CMD Lead';
      case 'cmd_qc': return 'CMD QC';
      case 'prepress': return 'Prepress';
      case 'prepress_lead': return 'Prepress Lead';
      case 'qc': return 'QC';
      case 'qc_lead': return 'QC Lead';
      case 'epson_lead': return 'Epson Lead';
      case 'production': return 'Production';
      case 'production_lead': return 'Production Lead';
      case 'epson': return 'Epson';
      case 'cafeteria': return 'Cafeteria Staff';
      case 'accounts': return 'Accounts';
      case 'support': return 'Support Team';
      case 'it': return 'IT';
      case 'dispatch': return 'Dispatch/Delivery';
      case 'receptionist': return 'Receptionist';
      default: return 'Unknown Role';
    }
  };

  // Get available team members based on team type
  const getAvailableMembers = (teamType) => {
    const roleMap = {
      'preflight': ['preflight', 'preflight_lead', 'preflight_prepress'],
      'prepress': ['prepress', 'prepress_lead', 'preflight_prepress'], 
      'qc': ['qc', 'qc_lead'],
      'cmd': ['cmd', 'cmd_lead', 'cmd_qc'],
      'epson': ['epson_lead'],
      'production': ['production', 'production_lead']
    };
    
    return users.filter(user => roleMap[teamType]?.includes(user.role));
  };

  // Get available team leads based on team type
  const getAvailableLeads = (teamType) => {
    const leadRoles = {
      'preflight': 'preflight_lead',
      'cmd': 'cmd_lead',
      'prepress': 'prepress_lead',
      'qc': 'qc_lead',
      'epson': 'epson_lead',
      'production': 'production_lead'
    };
    
    return users.filter(user => user.role === leadRoles[teamType]);
  };

  const fetchApiRequestsConfig = async () => {
    try {
      const response = await api.get('/admin/config/api-requests', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data && typeof response.data === 'object' ? response.data : {};
      setApiRequestsConfig(data);
    } catch (err) {
      console.error('Failed to fetch API requests config:', err);
      setApiRequestsConfig({});
    }
    // Also load the single global API key.
    try {
      const keyRes = await api.get('/admin/config/integration-api-key', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrationApiKey(keyRes.data?.key || '');
    } catch (e) { /* ignore */ }
  };

  const generateIntegrationApiKey = async () => {
    if (integrationApiKey && !window.confirm('Replace the existing API key? Systems using the old key will stop authenticating until updated.')) return;
    try {
      setApiKeyBusy(true);
      const res = await api.post('/admin/config/integration-api-key/generate', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrationApiKey(res.data?.key || '');
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to generate key');
    } finally {
      setApiKeyBusy(false);
    }
  };

  const clearIntegrationApiKey = async () => {
    if (!window.confirm('Remove the API key? Outbound requests will be sent without authentication.')) return;
    try {
      setApiKeyBusy(true);
      await api.delete('/admin/config/integration-api-key', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrationApiKey('');
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to clear key');
    } finally {
      setApiKeyBusy(false);
    }
  };

  const fetchIntegrationConfig = async () => {
    try {
      const response = await api.get('/admin/config/integration', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && typeof response.data === 'object') {
        const data = response.data;
        setIntegrationConfig(prev => ({ ...prev, ...data }));
        if (data.workflow_automation_config && typeof data.workflow_automation_config === 'object') {
          const incoming = data.workflow_automation_config;
          const incomingGlobal = incoming.global_cutoff || {};
          const incomingTimes = Array.isArray(incomingGlobal.cutoff_times)
            ? incomingGlobal.cutoff_times.filter(Boolean)
            : [];
          const normalizedTimes = incomingTimes.length > 0
            ? incomingTimes
            : (incomingGlobal.cutoff_time ? [incomingGlobal.cutoff_time] : DEFAULT_GLOBAL_CUTOFF_TIMES);

          setWorkflowAutomationConfig(prev => ({
            ...prev,
            ...incoming,
            global_cutoff: {
              ...prev.global_cutoff,
              ...incomingGlobal,
              cutoff_times: normalizedTimes,
            },
            alerts: {
              ...prev.alerts,
              ...(incoming.alerts || {}),
            },
            client_cutoff_rules: Array.isArray(incoming.client_cutoff_rules)
              ? incoming.client_cutoff_rules
              : prev.client_cutoff_rules,
          }));

          setNewClientCutoffRule(prev => ({
            ...prev,
            cutoff_time: normalizedTimes.includes(prev.cutoff_time)
              ? prev.cutoff_time
              : normalizedTimes[0],
          }));
        }
        if (data.skill_auto_assign_config && typeof data.skill_auto_assign_config === 'object') {
          setSkillAutoAssignConfig(prev => ({ ...prev, ...data.skill_auto_assign_config }));
        }
      }
    } catch (err) {
      // Not yet saved — use defaults
    }
  };

  const saveAdvancedOptions = async () => {
    try {
      setAdvancedOptionsSaving(true);
      const payload = {
        ...integrationConfig,
        workflow_automation_config: workflowAutomationConfig,
        skill_auto_assign_config: skillAutoAssignConfig
      };
      await api.put('/admin/config/integration', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrationConfig(payload);
      setAdvancedOptionsSaved(true);
      setTimeout(() => setAdvancedOptionsSaved(false), 2500);
    } catch (error) {
      console.error('Error saving advanced options:', error);
      alert(`Failed to save advanced options: ${error.response?.data?.error || error.message}`);
    } finally {
      setAdvancedOptionsSaving(false);
    }
  };

  const addGlobalCutoffTime = () => {
    const t = (newGlobalCutoffTime || '').trim();
    if (!t) {
      return;
    }
    setWorkflowAutomationConfig(prev => {
      const existing = Array.isArray(prev.global_cutoff?.cutoff_times)
        ? prev.global_cutoff.cutoff_times
        : [];
      if (existing.includes(t)) {
        return prev;
      }
      const updatedTimes = [...existing, t].sort();
      return {
        ...prev,
        global_cutoff: {
          ...prev.global_cutoff,
          cutoff_times: updatedTimes,
        }
      };
    });
    setNewGlobalCutoffTime('');
  };

  const removeGlobalCutoffTime = (timeVal) => {
    const affected = (workflowAutomationConfig.client_cutoff_rules || [])
      .filter(r => r.cutoff_time === timeVal);
    if (affected.length > 0) {
      const names = affected.map(r => r.client_name).slice(0, 8).join(', ');
      const more = affected.length > 8 ? ` and ${affected.length - 8} more` : '';
      if (!window.confirm(
        `${affected.length} client rule${affected.length !== 1 ? 's use' : ' uses'} ${timeVal}: ${names}${more}.\n\n`
        + `Removing this time will also remove those client cutoff rules. Continue?`
      )) return;
    }
    setWorkflowAutomationConfig(prev => ({
      ...prev,
      global_cutoff: {
        ...prev.global_cutoff,
        cutoff_times: (Array.isArray(prev.global_cutoff?.cutoff_times)
          ? prev.global_cutoff.cutoff_times : []).filter(t => t !== timeVal),
      },
      // Drop rules pointing at a time that no longer exists — otherwise the row
      // shows a dropdown with a value that is not an option, which reads as blank
      // while the backend still applies the old cutoff.
      client_cutoff_rules: (prev.client_cutoff_rules || []).filter(r => r.cutoff_time !== timeVal),
    }));
  };

  // ── Client cutoff table ─────────────────────────────────────────────
  // Every client gets a row whether or not it has a rule yet, so the whole
  // customer list can be reviewed at a glance instead of only the configured few.

  const cutoffTimes = () => workflowAutomationConfig.global_cutoff?.cutoff_times || [];

  const findClientRule = (clientName) =>
    (workflowAutomationConfig.client_cutoff_rules || []).find(
      r => (r.client_name || '').trim().toLowerCase() === (clientName || '').trim().toLowerCase()
    ) || null;

  // Create, update or delete a client's rule in one place. Passing a null/blank
  // cutoff_time removes the rule, which is how "no cutoff" is expressed.
  const upsertClientRule = (clientName, patch) => {
    const name = (clientName || '').trim();
    if (!name) return;
    setWorkflowAutomationConfig(prev => {
      const rules = [...(prev.client_cutoff_rules || [])];
      const i = rules.findIndex(
        r => (r.client_name || '').trim().toLowerCase() === name.toLowerCase()
      );
      if (patch === null) {
        return { ...prev, client_cutoff_rules: rules.filter((_, x) => x !== i) };
      }
      if (i >= 0) {
        rules[i] = { ...rules[i], ...patch, client_name: rules[i].client_name || name };
      } else {
        rules.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          client_name: name,
          cutoff_time: '',
          grace_minutes: 15,
          enabled: true,
          timezone: FIXED_CUTOFF_TIMEZONE,
          ...patch,
        });
      }
      return { ...prev, client_cutoff_rules: rules };
    });
  };

  // All clients, plus any rule pointing at a client that is no longer in the
  // customer list — those would otherwise be invisible and unfixable here.
  const getCutoffRows = () => {
    const rows = (clients || []).map(c => ({
      name: c.name,
      id: c.id || c._id,
      rule: findClientRule(c.name),
      orphan: false,
    }));
    const known = new Set(rows.map(r => (r.name || '').trim().toLowerCase()));
    (workflowAutomationConfig.client_cutoff_rules || []).forEach(r => {
      const n = (r.client_name || '').trim();
      if (n && !known.has(n.toLowerCase())) {
        rows.push({ name: n, id: r.id, rule: r, orphan: true });
      }
    });

    const q = (cutoffClientFilter || '').trim().toLowerCase();
    return rows
      .filter(r => !q || (r.name || '').toLowerCase().includes(q))
      .filter(r => {
        if (cutoffStatusFilter === 'configured') return !!r.rule?.cutoff_time;
        if (cutoffStatusFilter === 'unconfigured') return !r.rule?.cutoff_time;
        if (cutoffStatusFilter === 'disabled') return !!r.rule?.cutoff_time && r.rule?.enabled === false;
        return true;
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  };

  const downloadCutoffCsv = () => {
    const rows = (clients || []).map(c => ({ name: c.name, rule: findClientRule(c.name) }));
    (workflowAutomationConfig.client_cutoff_rules || []).forEach(r => {
      const n = (r.client_name || '').trim();
      if (n && !rows.some(x => (x.name || '').trim().toLowerCase() === n.toLowerCase())) {
        rows.push({ name: n, rule: r });
      }
    });
    if (rows.length === 0) { alert('No clients to export yet.'); return; }

    const header = ['client_name', 'cutoff_time', 'grace_minutes', 'enabled'];
    const lines = [header.map(csvEscape).join(',')];
    rows
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .forEach(({ name, rule }) => {
        lines.push([
          name,
          rule?.cutoff_time || '',
          rule?.cutoff_time ? (rule.grace_minutes ?? 0) : '',
          rule?.cutoff_time ? (rule.enabled === false ? 'No' : 'Yes') : '',
        ].map(csvEscape).join(','));
      });
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `client-cutoff-rules-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCutoffCsvFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setCutoffCsvSummary(null);

    let text = '';
    try { text = await file.text(); } catch (err) { alert('Could not read that file.'); return; }
    if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

    const rows = splitCsvRows(text);
    if (rows.length < 2) { alert('That CSV has no data rows.'); return; }

    const header = parseCsvLine(rows[0]).map(h => h.toLowerCase());
    const nameIdx = header.indexOf('client_name');
    const timeIdx = header.indexOf('cutoff_time');
    const graceIdx = header.indexOf('grace_minutes');
    const enabledIdx = header.indexOf('enabled');
    if (nameIdx === -1 || timeIdx === -1) {
      alert('The CSV needs client_name and cutoff_time columns. Download the current CSV to get the right format.');
      return;
    }

    const valid = cutoffTimes();
    const nextRules = [...(workflowAutomationConfig.client_cutoff_rules || [])];
    const idxOf = (n) => nextRules.findIndex(
      r => (r.client_name || '').trim().toLowerCase() === n.trim().toLowerCase()
    );

    let created = 0; let updated = 0; let cleared = 0; let unchanged = 0;
    const badTimes = []; const blankNames = [];

    for (let r = 1; r < rows.length; r += 1) {
      const cells = parseCsvLine(rows[r]);
      const name = (cells[nameIdx] || '').trim();
      if (!name) { blankNames.push(r + 1); continue; }
      const time = (cells[timeIdx] || '').trim();
      const i = idxOf(name);

      // Blank cutoff time means "this client has no cutoff" — remove any rule.
      if (!time) {
        if (i >= 0) { nextRules.splice(i, 1); cleared += 1; } else unchanged += 1;
        continue;
      }
      // Times must be one of the configured Global Cutoff Times, so the table
      // dropdown and the imported data can never disagree.
      if (!valid.includes(time)) { badTimes.push(`${name}: ${time}`); continue; }

      const grace = graceIdx >= 0 ? Math.max(0, parseInt(cells[graceIdx] || '0', 10) || 0) : 15;
      const enabled = enabledIdx >= 0 ? !['no', 'n', '0', 'false', 'off'].includes(String(cells[enabledIdx] || '').trim().toLowerCase()) : true;

      if (i >= 0) {
        const cur = nextRules[i];
        if (cur.cutoff_time === time && (cur.grace_minutes ?? 0) === grace && (cur.enabled !== false) === enabled) {
          unchanged += 1;
        } else {
          nextRules[i] = { ...cur, cutoff_time: time, grace_minutes: grace, enabled };
          updated += 1;
        }
      } else {
        nextRules.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${r}`,
          client_name: name,
          cutoff_time: time,
          grace_minutes: grace,
          enabled,
          timezone: FIXED_CUTOFF_TIMEZONE,
        });
        created += 1;
      }
    }

    setWorkflowAutomationConfig(prev => ({ ...prev, client_cutoff_rules: nextRules }));
    setCutoffCsvSummary({
      fileName: file.name,
      rows: rows.length - 1,
      created, updated, cleared, unchanged,
      badTimes: badTimes.slice(0, 8),
      badTimesTotal: badTimes.length,
      blankNames: blankNames.length,
      validTimes: valid,
    });
  };

  const addUserSkillMapping = () => {
    if (!newUserSkillMapping.user_id || !newUserSkillMapping.skill_name) {
      alert('User and skill are required');
      return;
    }
    const selectedSkill = (skillAutoAssignConfig.skill_definitions || []).find(
      s => s.name === newUserSkillMapping.skill_name
    );
    const item = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      user_id: newUserSkillMapping.user_id,
      skill_id: selectedSkill?.id || '',
      skill_name: newUserSkillMapping.skill_name,
      proficiency: newUserSkillMapping.proficiency
    };
    setSkillAutoAssignConfig(prev => ({
      ...prev,
      user_skill_mappings: [...(prev.user_skill_mappings || []), item]
    }));
    setNewUserSkillMapping({ user_id: '', skill_name: '', proficiency: 'beginner' });
  };

  const removeUserSkillMapping = (id) => {
    setSkillAutoAssignConfig(prev => ({
      ...prev,
      user_skill_mappings: (prev.user_skill_mappings || []).filter(map => map.id !== id)
    }));
  };

  const addTaskSkillMapping = () => {
    const taskName = (newTaskSkillMapping.task_name || '').trim();
    if (!taskName || !newTaskSkillMapping.skill_name) {
      alert('Task and skill are required');
      return;
    }
    const selectedSkill = (skillAutoAssignConfig.skill_definitions || []).find(
      s => s.name === newTaskSkillMapping.skill_name
    );
    const item = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      task_name: taskName,
      skill_id: selectedSkill?.id || '',
      skill_name: newTaskSkillMapping.skill_name,
      minimum_proficiency: newTaskSkillMapping.minimum_proficiency
    };
    setSkillAutoAssignConfig(prev => ({
      ...prev,
      task_skill_mappings: [...(prev.task_skill_mappings || []), item]
    }));
    setNewTaskSkillMapping({ task_name: '', skill_name: '', minimum_proficiency: 'intermediate' });
  };

  const removeTaskSkillMapping = (id) => {
    setSkillAutoAssignConfig(prev => ({
      ...prev,
      task_skill_mappings: (prev.task_skill_mappings || []).filter(map => map.id !== id)
    }));
  };

  // ── Skill matrix helpers ─────────────────────────────────────────────
  // ── Skill matrix is derived from Complexity Config ──
  // Departments  = task names (TASK_NAMES, columns of Complexity Config)
  // Skill columns = complexity levels (rows of Complexity Config)
  // A matrix cell key is `${taskName}::${complexityKey}`.

  // Complexity levels, in config order, including any custom ones in the config.
  const getMatrixComplexities = () => {
    const keys = [...COMPLEXITY_ORDER];
    Object.keys(complexityConfig || {}).forEach(k => {
      if (!keys.includes(k)) keys.push(k);
    });
    return keys.filter(k => complexityConfig && complexityConfig[k]);
  };

  const prettyComplexity = (key) =>
    (key || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  // Departments = the task names from Complexity Config.
  const getSkillDepartments = () => TASK_NAMES;

  // Each department's columns are the complexity levels (as {id,name} objects,
  // where id is the cell key and name is the display label).
  const getSkillsForDept = (dept) =>
    getMatrixComplexities().map(cx => ({
      id: `${dept}::${cx}`,
      name: prettyComplexity(cx),
    }));

  // Matrix cells are simple booleans: checked = the operator can do that skill.
  // Stored as true; anything falsy (false / undefined / legacy "-") means "no".
  const isMatrixCellChecked = (userId, skillId) => {
    const v = ((skillAutoAssignConfig.skill_matrix || {})[userId] || {})[skillId];
    return v === true || v === 'Yes' || v === 'yes';
  };

  const setMatrixCell = (userId, skillId, checked) => {
    setSkillAutoAssignConfig(prev => {
      const matrix = { ...(prev.skill_matrix || {}) };
      const row = { ...(matrix[userId] || {}) };
      if (checked) row[skillId] = true;
      else delete row[skillId];
      matrix[userId] = row;
      return { ...prev, skill_matrix: matrix };
    });
  };

  const toggleMatrixCell = (userId, skillId) =>
    setMatrixCell(userId, skillId, !isMatrixCellChecked(userId, skillId));

  // Quick-fill: set every skill in a department for one user on/off at once.
  const setUserDeptSkills = (userId, dept, checked) => {
    const skillIds = getSkillsForDept(dept).map(s => s.id);
    setSkillAutoAssignConfig(prev => {
      const matrix = { ...(prev.skill_matrix || {}) };
      const row = { ...(matrix[userId] || {}) };
      skillIds.forEach(id => {
        if (checked) row[id] = true;
        else delete row[id];
      });
      matrix[userId] = row;
      return { ...prev, skill_matrix: matrix };
    });
  };

  // Quick-fill: set every visible skill for one user on/off at once.
  const setUserAllSkills = (userId, checked) => {
    const depts = getSkillDepartments().filter(d => skillDeptFilter === 'all' || d === skillDeptFilter);
    const skillIds = depts.flatMap(d => getSkillsForDept(d).map(s => s.id));
    setSkillAutoAssignConfig(prev => {
      const matrix = { ...(prev.skill_matrix || {}) };
      const row = { ...(matrix[userId] || {}) };
      skillIds.forEach(id => {
        if (checked) row[id] = true;
        else delete row[id];
      });
      matrix[userId] = row;
      return { ...prev, skill_matrix: matrix };
    });
  };

  // ── Skill matrix CSV export / import ────────────────────────────────
  // Task names contain commas ("Prepress , QC (self QC)"), so both directions
  // need real RFC-4180 quoting. A naive split(',') corrupts those columns.

  const csvEscape = (value) => {
    const s = value === null || value === undefined ? '' : String(value);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  // Split one CSV line, honouring quoted fields and doubled quotes inside them.
  const parseCsvLine = (line) => {
    const out = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const c = line[i];
      if (inQuotes) {
        if (c === '"') {
          if (line[i + 1] === '"') { field += '"'; i += 1; } else { inQuotes = false; }
        } else field += c;
      } else if (c === '"') inQuotes = true;
      else if (c === ',') { out.push(field); field = ''; }
      else field += c;
    }
    out.push(field);
    return out.map((f) => f.trim());
  };

  // Split a whole file into lines, keeping newlines that sit inside quotes.
  const splitCsvRows = (text) => {
    const rows = [];
    let row = '';
    let inQuotes = false;
    const src = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    for (let i = 0; i < src.length; i += 1) {
      const c = src[i];
      if (c === '"') { inQuotes = !inQuotes; row += c; } else if (c === '\n' && !inQuotes) {
        if (row.trim()) rows.push(row);
        row = '';
      } else row += c;
    }
    if (row.trim()) rows.push(row);
    return rows;
  };

  const TRUTHY_CSV = ['yes', 'y', '1', 'true', 'x', '✓', 'tick', 'ticked'];
  const isTruthyCsv = (v) => TRUTHY_CSV.includes(String(v || '').trim().toLowerCase());

  // Every matrix column, in the same order the on-screen table shows them.
  const allMatrixSkillIds = () =>
    getSkillDepartments().flatMap((d) => getSkillsForDept(d).map((s) => s.id));

  const downloadSkillMatrixCsv = () => {
    const skillIds = allMatrixSkillIds();
    if (skillIds.length === 0) {
      alert('No complexity levels configured yet. Set them up in Settings → Complexity Config first.');
      return;
    }
    // Deliberately every user and every column, regardless of the on-screen
    // filters — this is a full backup that can be edited and sent back.
    const header = ['user_id', 'name', 'role', ...skillIds];
    const lines = [header.map(csvEscape).join(',')];
    (users || []).forEach((u) => {
      const row = [u._id, u.name || '', u.role || ''];
      skillIds.forEach((id) => row.push(isMatrixCellChecked(u._id, id) ? 'Yes' : ''));
      lines.push(row.map(csvEscape).join(','));
    });
    // BOM so Excel opens UTF-8 names correctly.
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `skill-mapping-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSkillCsvFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';           // allow re-picking the same file
    if (!file) return;
    setSkillCsvSummary(null);

    let text = '';
    try {
      text = await file.text();
    } catch (err) {
      alert('Could not read that file.');
      return;
    }
    if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

    const rows = splitCsvRows(text);
    if (rows.length < 2) { alert('That CSV has no data rows.'); return; }

    const header = parseCsvLine(rows[0]);
    const idIdx = header.findIndex((h) => h.toLowerCase() === 'user_id');
    const nameIdx = header.findIndex((h) => h.toLowerCase() === 'name');
    if (idIdx === -1 && nameIdx === -1) {
      alert('The CSV needs a user_id column (or at least a name column). Download the current CSV to get the right format.');
      return;
    }

    const known = new Set(allMatrixSkillIds());
    const skillCols = [];
    const unknownCols = [];
    header.forEach((h, i) => {
      if (i === idIdx || i === nameIdx) return;
      if (['role'].includes(h.toLowerCase())) return;
      if (known.has(h)) skillCols.push({ key: h, idx: i });
      else if (h) unknownCols.push(h);
    });
    if (skillCols.length === 0) {
      alert('No recognisable skill columns found. Column headers must look like "Preflight::complex".');
      return;
    }

    const byId = {};
    const byName = {};
    (users || []).forEach((u) => {
      byId[String(u._id)] = u;
      byName[(u.name || '').trim().toLowerCase()] = u;
    });

    const nextMatrix = { ...(skillAutoAssignConfig.skill_matrix || {}) };
    let added = 0; let removed = 0; let matched = 0;
    const notFound = [];

    for (let r = 1; r < rows.length; r += 1) {
      const cells = parseCsvLine(rows[r]);
      const rawId = idIdx >= 0 ? cells[idIdx] : '';
      const rawName = nameIdx >= 0 ? cells[nameIdx] : '';
      // user_id is authoritative; the name is only a fallback for hand-made files.
      const user = byId[rawId] || byName[(rawName || '').trim().toLowerCase()];
      if (!user) {
        if (rawId || rawName) notFound.push(rawName || rawId);
        continue;
      }
      matched += 1;
      const row = { ...(nextMatrix[user._id] || {}) };
      skillCols.forEach(({ key, idx }) => {
        const want = isTruthyCsv(cells[idx]);
        const have = row[key] === true || row[key] === 'Yes' || row[key] === 'yes';
        if (want && !have) { row[key] = true; added += 1; }
        else if (!want && have) { delete row[key]; removed += 1; }
      });
      nextMatrix[user._id] = row;
    }

    // Only the users and columns present in the file are touched, so a partial
    // or filtered sheet can be uploaded without wiping anything else.
    setSkillAutoAssignConfig((prev) => ({ ...prev, skill_matrix: nextMatrix }));
    setSkillCsvSummary({
      fileName: file.name,
      rows: rows.length - 1,
      matched,
      added,
      removed,
      columns: skillCols.length,
      notFound: notFound.slice(0, 10),
      notFoundTotal: notFound.length,
      unknownCols: unknownCols.slice(0, 5),
      unknownColsTotal: unknownCols.length,
    });
  };

  // Distinct user roles present, for the row filter.
  const getUserRoles = () => {
    const seen = [];
    (users || []).forEach(u => {
      const r = (u.role || '').trim();
      if (r && !seen.includes(r)) seen.push(r);
    });
    return seen;
  };

  const getMatrixUsers = () => {
    const q = (skillNameFilter || '').trim().toLowerCase();
    return (users || []).filter(u =>
      (skillRoleFilter === 'all' || u.role === skillRoleFilter) &&
      (!q || (u.name || '').toLowerCase().includes(q))
    );
  };

  const prettyRole = (r) =>
    (r || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  const saveApiRequestsConfig = async () => {
    try {
      setApiRequestsLoading(true);

      const payload = {};
      // Save all composite keys (taskName::action) as-is
      Object.entries(apiRequestsConfig || {}).forEach(([key, urls]) => {
        payload[key] = Array.isArray(urls) ? urls : [];
      });

      await api.put('/admin/config/api-requests', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await api.put('/admin/config/integration', {
        ...integrationConfig,
        workflow_automation_config: workflowAutomationConfig,
        skill_auto_assign_config: skillAutoAssignConfig
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setApiRequestsDirty(false);
      alert('API request configuration updated successfully!');
    } catch (error) {
      console.error('Error updating API request configuration:', error);
      alert(`Failed to update API request configuration: ${error.response?.data?.error || error.message}`);
    } finally {
      setApiRequestsLoading(false);
    }
  };

  // Notification Alert Settings Functions
  const fetchNotificationAlertSettings = async () => {
    try {
      const res = await api.get('/admin/notification-settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationAlertSettings(res.data);
    } catch (err) {
      // Use defaults if not yet saved
    }
  };

  const saveNotificationAlertSettings = async () => {
    try {
      setNotifSettingsLoading(true);
      const res = await api.put('/admin/notification-settings', notificationAlertSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationAlertSettings(res.data.settings);
      setNotifSettingsSaved(true);
      setTimeout(() => setNotifSettingsSaved(false), 2500);
    } catch (err) {
      alert('Failed to save notification settings');
    } finally {
      setNotifSettingsLoading(false);
    }
  };

  // Customer Accounts Functions
  const fetchCustomerAccounts = async () => {
    try {
      setCustomerLoading(true);
      const res = await api.get('/admin/customers', { headers: { Authorization: `Bearer ${token}` } });
      setCustomerAccounts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch customer accounts', err);
    } finally {
      setCustomerLoading(false);
    }
  };

  const fetchPortalSettings = async () => {
    try {
      const res = await api.get('/admin/portal-settings', { headers: { Authorization: `Bearer ${token}` } });
      if (res.data) setPortalSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch portal settings', err);
    }
  };

  const savePortalSettings = async () => {
    try {
      setPortalSettingsSaving(true);
      await api.put('/admin/portal-settings', portalSettings, { headers: { Authorization: `Bearer ${token}` } });
      setPortalSettingsSaved(true);
      setTimeout(() => setPortalSettingsSaved(false), 2500);
    } catch (err) {
      alert('Failed to save portal settings');
    } finally {
      setPortalSettingsSaving(false);
    }
  };

  const handleOpenCustomerDialog = (customer = null) => {
    if (customer) {
      setCurrentCustomer({
        ...customer,
        password: '',
        secondary_emails: customer.secondary_emails || [],
        notification_preferences: {
          stage_change: true, completed: true, on_hold: true, approval_waiting: true, approval_result: true,
          ...(customer.notification_preferences || {})
        }
      });
      setIsEditingCustomer(true);
    } else {
      setCurrentCustomer({
        name: '', email: '', password: '', client_name: '',
        secondary_emails: [],
        active: true,
        notification_preferences: { stage_change: true, completed: true, on_hold: true, approval_waiting: true, approval_result: true }
      });
      setIsEditingCustomer(false);
    }
    setCustomerNewSecEmail('');
    setCustomerSaveError('');
    setOpenCustomerDialog(true);
  };

  const handleSaveCustomer = async () => {
    setCustomerSaveError('');
    if (!currentCustomer.name.trim() || !currentCustomer.email.trim()) {
      setCustomerSaveError('Name and email are required.');
      return;
    }
    if (!isEditingCustomer && !currentCustomer.password.trim()) {
      setCustomerSaveError('Password is required when creating a new account.');
      return;
    }
    const payload = { ...currentCustomer };
    if (!payload.password) delete payload.password;
    try {
      if (isEditingCustomer) {
        await api.put(`/admin/customers/${currentCustomer.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await api.post('/admin/customers', payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      setOpenCustomerDialog(false);
      fetchCustomerAccounts();
    } catch (err) {
      setCustomerSaveError(err.response?.data?.error || 'Failed to save customer account.');
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (!window.confirm('Delete this customer account? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/customers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchCustomerAccounts();
    } catch (err) {
      alert('Failed to delete customer account.');
    }
  };

  // SMTP Settings Functions
  const fetchSmtpSettings = async () => {
    try {
      const response = await api.get('/admin/config/smtp-settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSmtpSettings(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch SMTP settings:', err);
    }
  };

  const saveSmtpSettings = async () => {
    try {
      setSmtpLoading(true);
      await api.put('/admin/config/smtp-settings', smtpSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('SMTP settings saved successfully!');
    } catch (error) {
      console.error('Error saving SMTP settings:', error);
      alert(`Failed to save SMTP settings: ${error.response?.data?.error || error.message}`);
    } finally {
      setSmtpLoading(false);
    }
  };

  const testSmtpSettings = async () => {
    if (!smtpTestEmail) {
      alert('Please enter a test email address');
      return;
    }
    try {
      setSmtpLoading(true);
      setSmtpTestResult(null);
      const response = await api.post('/admin/config/smtp-settings/test', 
        { test_email: smtpTestEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSmtpTestResult({ success: true, message: response.data.message });
    } catch (error) {
      console.error('Error testing SMTP:', error);
      setSmtpTestResult({ success: false, message: error.response?.data?.error || error.message });
    } finally {
      setSmtpLoading(false);
    }
  };

  // Configuration Management Functions
  const fetchComplexityConfig = async () => {
    try {
      const response = await api.get('/admin/config/complexity', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Fetched config from server:', response.data);
      
      // Validate that the fetched data has the correct nested structure
      const isValidStructure = (data) => {
        if (!data || typeof data !== 'object') return false;
        
        const requiredComplexities = ['simple_simple', 'simple', 'medium_simple', 'medium', 'complex', 'super_complex', 'combined_job'];
        
        // Check if all complexities are objects (not primitives)
        return requiredComplexities.every(complexity => {
          return data[complexity] && typeof data[complexity] === 'object' && !Array.isArray(data[complexity]);
        });
      };
      
      if (isValidStructure(response.data)) {
        setComplexityConfig(normalizeComplexityConfig(response.data, complexityConfig));
        console.log('Config loaded successfully');
      } else {
        console.warn('Invalid config structure from server, keeping defaults');
        // Keep the default state
      }
    } catch (err) {
      console.error('Failed to fetch complexity config, using defaults:', err);
      // Keep default values if fetch fails
    }
  };

  const updateComplexityConfig = async () => {
    try {
      setConfigLoading(true);

      const payload = normalizeComplexityConfig(complexityConfig, complexityConfig);
      // Keep local state clean as well (removes legacy keys like "Approval")
      setComplexityConfig(payload);
      
      // Validate the structure before sending
      const keys = Object.keys(payload || {});

      // All configured complexity rows must be plain objects
      const allObjectsValid = keys.every((key) => {
        const item = payload[key];
        return item && typeof item === 'object' && !Array.isArray(item);
      });

      // And the known base complexities must still exist
      const baseKeysPresent = COMPLEXITY_ORDER.every((c) => {
        const item = payload[c];
        return item && typeof item === 'object' && !Array.isArray(item);
      });

      const isValid = allObjectsValid && baseKeysPresent;
      
      if (!isValid) {
        alert('Error: Configuration structure is invalid. Please refresh the page and try again.');
        console.error('Invalid configuration structure:', payload);
        setConfigLoading(false);
        return;
      }
      
      console.log('Sending valid complexity config:', payload);
      const response = await api.put('/admin/config/complexity', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Success response:', response.data);
      alert('Configuration updated successfully!');
    } catch (error) {
      console.error('Error updating configuration:', error);
      console.error('Error response:', error.response?.data);
      alert(`Failed to update configuration: ${error.response?.data?.error || error.message}`);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleComplexityTimeChange = (complexity, taskName, minutes) => {
    setComplexityConfig(prev => ({
      ...prev,
      [complexity]: {
        ...prev[complexity],
        [taskName]: parseInt(minutes) || 0
      }
    }));
  };

  const TASK_NAMES = [
    'Preflight',
    'Single-up approval',
    'Die creation',
    'Recreation',
    'CMD',
    'CMD QC',
    'CMD Path',
    'Prepress , QC (self QC)',
    'Layout approval',
    'Image Export',
    'Image Duplication',
    'Mounting or RIP & Send QC',
    'RIP QC',
    'Epson Printout',
    'Plate and Despatch'
  ];

  const TASK_ACTIONS = [
    { key: 'started',   label: 'Started' },
    { key: 'paused',    label: 'Paused' },
    { key: 'resumed',   label: 'Resumed' },
    { key: 'on_hold',   label: 'On Hold' },
    { key: 'queried',   label: 'Queried' },
    { key: 'rejected',  label: 'Rejected' },
    { key: 'completed', label: 'Completed' },
  ];

  const parseBooleanCell = (value, defaultValue) => {
    if (value === undefined || value === null || String(value).trim() === '') {
      return defaultValue;
    }
    const v = String(value).trim().toLowerCase();
    return ['1', 'true', 'yes', 'y'].includes(v);
  };

  const mapRoleFromCsv = (rawRole) => {
    if (!rawRole) return null;
    const v = String(rawRole).trim().toLowerCase();
    const map = {
      'preflight': 'preflight',
      'preflight lead': 'preflight_lead',
      'preflight_lead': 'preflight_lead',
      'prepress': 'prepress',
      'prepress lead': 'prepress_lead',
      'prepress_lead': 'prepress_lead',
      'qc': 'qc',
      'qc lead': 'qc_lead',
      'qc_lead': 'qc_lead',
      'production': 'production',
      'production lead': 'production_lead',
      'production_lead': 'production_lead',
      'pm': 'pm',
      'project manager': 'pm',
      'admin': 'admin',
      // Map Data Entry staff to Prepress by default
      'data entry': 'prepress',
      'data_entry': 'prepress'
    };
    return map[v] || null;
  };

  const mapStatusFromCsv = (rawStatus) => {
    if (!rawStatus) return 'active';
    const v = String(rawStatus).trim().toLowerCase();
    if (['active', 'busy', 'offline'].includes(v)) return v;
    return 'active';
  };

  const handleImportClientsFromCsv = async () => {
    const raw = (clientCsvText || '').trim();
    if (!raw) {
      alert('Please paste CSV data before importing.');
      return;
    }

    const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
    if (lines.length < 2) {
      alert('CSV must include a header row and at least one data row.');
      return;
    }

    const headerCells = lines[0].split(',').map((h) => h.trim().toLowerCase());
    const requiredHeaders = ['name', 'email'];
    const missing = requiredHeaders.filter((h) => !headerCells.includes(h));
    if (missing.length > 0) {
      alert(`Missing required columns in header: ${missing.join(', ')}`);
      return;
    }

    const idx = (name) => headerCells.indexOf(name);
    const nameIdx = idx('name');
    const emailIdx = idx('email');
    const phoneIdx = idx('phone');
    const addressIdx = idx('address');
    const teamIdx = idx('team');
    const preflightIdx = idx('preflight_enabled');
    const singleAppIdx = idx('single_app_approval_enabled');
    const epsonIdx = idx('epson_enabled');

    const results = [];
    setClientCsvProcessing(true);

    try {
      for (let i = 1; i < lines.length; i += 1) {
        const line = lines[i];
        if (!line) continue;
        const cells = line.split(',');
        const name = (cells[nameIdx] || '').trim();
        const email = (cells[emailIdx] || '').trim();

        if (!name || !email) {
          results.push({ row: i + 1, status: 'skipped', reason: 'Name and Email are required' });
          continue;
        }

        const phone = phoneIdx >= 0 ? (cells[phoneIdx] || '').trim() : '';
        const address = addressIdx >= 0 ? (cells[addressIdx] || '').trim() : '';
        const teamName = teamIdx >= 0 ? (cells[teamIdx] || '').trim() : '';
        const preflightEnabled = preflightIdx >= 0
          ? parseBooleanCell(cells[preflightIdx], true)
          : true;
        const singleAppEnabled = singleAppIdx >= 0
          ? parseBooleanCell(cells[singleAppIdx], true)
          : true;
        // Epson defaults OFF, unlike the other two flags.
        const epsonEnabled = epsonIdx >= 0
          ? parseBooleanCell(cells[epsonIdx], false)
          : false;

        let teamId = '';
        if (teamName) {
          const team = teams.find((t) => String(t.name || '').toLowerCase() === teamName.toLowerCase());
          if (team) {
            teamId = team.id;
          } else {
            results.push({ row: i + 1, status: 'skipped', reason: `Team not found: ${teamName}` });
            continue;
          }
        }

        const payload = {
          name,
          email,
          phone,
          address,
          team_id: teamId,
          preflight_enabled: preflightEnabled,
          single_app_approval_enabled: singleAppEnabled,
          epson_enabled: epsonEnabled
        };

        try {
          await api.post('/admin/clients', payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
          results.push({ row: i + 1, status: 'success' });
        } catch (err) {
          // Try to surface backend validation error if available
          const msg = err?.response?.data?.error || 'Unknown error';
          results.push({ row: i + 1, status: 'error', reason: msg });
        }
      }

      setClientCsvResult(results);

      const successCount = results.filter((r) => r.status === 'success').length;
      const errorCount = results.length - successCount;
      alert(`CSV import completed. Success: ${successCount}, Failed/Skipped: ${errorCount}`);

      if (successCount > 0) {
        fetchClients();
      }
    } finally {
      setClientCsvProcessing(false);
    }
  };

  const downloadSampleClientsCsv = () => {
    const header = 'name,email,phone,address,team,preflight_enabled,single_app_approval_enabled,epson_enabled';
    const sampleRows = [
      'Client A,clienta@example.com,+1 555 111 1111,"123 Main St City",Prepress Team,true,true,false',
      'Client B,clientb@example.com,+1 555 222 2222,"456 Second St City",,true,false,true'
    ];
    const csvContent = [header, ...sampleRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'clients-sample.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportUsersFromCsv = async () => {
    const raw = (userCsvText || '').trim();
    if (!raw) {
      alert('Please paste CSV data before importing.');
      return;
    }

    const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
    if (lines.length < 2) {
      alert('CSV must include a header row and at least one data row.');
      return;
    }

    const headerCells = lines[0].split(',').map((h) => h.trim().toLowerCase());

    const findIndex = (candidates) => {
      for (const name of candidates) {
        const idx = headerCells.indexOf(name);
        if (idx !== -1) return idx;
      }
      return -1;
    };

    const nameIdx = findIndex(['name', 'full name', 'full_name']);
    const emailIdx = findIndex(['email', 'e-mail']);
    const phoneIdx = findIndex(['phone', 'mobile', 'contact']);
    const roleIdx = findIndex(['role']);
    const statusIdx = findIndex(['status']);
    const adUserIdx = findIndex(['ad_username', 'ad user name', 'ad user', 'ad user name ']);

    if (nameIdx === -1) {
      alert('CSV header must include a Name or Full Name column.');
      return;
    }
    if (emailIdx === -1 && adUserIdx === -1) {
      alert('CSV header must include at least Email or AD User Name column.');
      return;
    }

    const results = [];
    const defaultPassword = 'Temp@123';
    setUserCsvProcessing(true);

    try {
      for (let i = 1; i < lines.length; i += 1) {
        const line = lines[i];
        if (!line) continue;
        const cells = line.split(',');

        const name = (cells[nameIdx] || '').trim();
        const email = emailIdx >= 0 ? (cells[emailIdx] || '').trim() : '';
        const adUser = adUserIdx >= 0 ? (cells[adUserIdx] || '').trim() : '';

        if (!name) {
          results.push({ row: i + 1, status: 'skipped', reason: 'Name is required' });
          continue;
        }

        if (!email && !adUser) {
          results.push({ row: i + 1, status: 'skipped', reason: 'Either Email or AD User Name is required' });
          continue;
        }

        const phone = phoneIdx >= 0 ? (cells[phoneIdx] || '').trim() : '';
        const roleRaw = roleIdx >= 0 ? (cells[roleIdx] || '').trim() : '';
        const statusRaw = statusIdx >= 0 ? (cells[statusIdx] || '').trim() : '';

        const role = mapRoleFromCsv(roleRaw) || 'preflight';
        const status = mapStatusFromCsv(statusRaw);

        const payload = {
          name,
          email,
          ad_username: adUser,
          phone,
          role,
          status,
          password: defaultPassword
        };

        try {
          await api.post('/admin/users', payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
          results.push({ row: i + 1, status: 'success' });
        } catch (err) {
          const msg = err?.response?.data?.error || 'Unknown error';
          results.push({ row: i + 1, status: 'error', reason: msg });
        }
      }

      setUserCsvResult(results);

      const successCount = results.filter((r) => r.status === 'success').length;
      const errorCount = results.length - successCount;
      alert(`User CSV import completed. Success: ${successCount}, Failed/Skipped: ${errorCount}`);

      if (successCount > 0) {
        fetchUsers();
      }
    } finally {
      setUserCsvProcessing(false);
    }
  };

  const downloadSampleUsersCsv = () => {
    const header = 'Full Name,Email,Phone,Role,Status,AD User Name';
    const sampleRows = [
      'Abitha,abitha@veepeegraphics.com,080-46450500,QC,active,Abitha',
      'Ajay B,ajay.b@veepeegraphics.com,080-46450500,Data Entry,active,Ajay B'
    ];
    const csvContent = [header, ...sampleRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'users-sample.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Filter and Pagination Logic
  const filteredUsers = users
    .filter(user => {
      const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === '' || user.role === roleFilter;
      const matchesStatus = statusFilter === '' || user.status === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    })
    // Active users first, everyone else after; alphabetical (by name) within each group.
    .sort((a, b) => {
      const aActive = a.status === 'active' ? 0 : 1;
      const bActive = b.status === 'active' ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      return (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' });
    });
  
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  // Ensure current page is valid for filtered results
  const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages || 1);
  const indexOfLastUser = validCurrentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const allApiRequestRows = (() => {
    const rows = [];
    Object.entries(apiRequestsConfig || {}).forEach(([key, urls]) => {
      if (!Array.isArray(urls) || urls.length === 0) return;
      const [taskName, action] = key.includes('::') ? key.split('::') : [key, 'completed'];
      const actionLabel = TASK_ACTIONS.find(a => a.key === action)?.label || action;
      urls.forEach((entry, index) => {
        const url = typeof entry === 'string' ? entry : (entry?.url || '');
        rows.push({ key: `${key}-${index}`, taskName, actionLabel, url });
      });
    });
    return rows;
  })();

  return (
    <div className="p-6 overflow-x-hidden">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Admin Panel</h1>

      <div className="flex gap-6 items-start max-w-full">
        {/* ── Vertical sidebar nav ── */}
        <nav className="w-52 shrink-0 bg-white rounded-xl border border-gray-200 shadow-sm p-2 sticky top-4">
          {[
            { Icon: Users,         label: 'User Management',      id: 0 },
            { Icon: Briefcase,     label: 'Teams',                id: 1 },
            { Icon: UserPlus,      label: 'Customers',            id: 2 },
            { Icon: Globe,         label: 'Customer Accounts',    id: 3 },
            { Icon: Settings,      label: 'Complexity Config',    id: 4 },
            { Icon: Shield,        label: 'API Requests',         id: 5 },
            { Icon: MapPin,        label: 'Locations & Resources', id: 6 },
            { Icon: AlertTriangle, label: 'Error Categories',     id: 7 },
            { Icon: Mail,          label: 'SMTP Settings',        id: 8 },
            { Icon: Clock,         label: 'Email & Schedule',     id: 13 },
            { Icon: Bell,          label: 'Notification Alerts',  id: 9 },
            { Icon: Coffee,        label: 'Cutoff Workflow',      id: 10 },
            { Icon: Coffee,        label: 'User Skill Mapping',   id: 11 },
            { Icon: Coffee,        label: 'Job Auto-Assign',      id: 12 },
            { Icon: Clock,         label: 'Shift Timings',        id: 14 },
            { Icon: Coffee,        label: 'Leaves',               id: 15 },
            { Icon: CheckSquare,   label: 'Complete Projects',    id: 17 },
          ].filter(({ id }) => isSuperAdmin || !SUPER_ADMIN_ONLY_TABS.includes(id))
           .map(({ Icon, label, id }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-left transition-colors mb-0.5
                ${ activeTab === id
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800' }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </button>
          ))}
        </nav>

        {/* ── Tab content ── */}
        <div className="flex-1 w-0 min-w-0">
      {activeTab === 0 && (
        <div>
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <h2 className="text-lg font-semibold text-gray-800">Users</h2>
            
            <div className="flex flex-1 max-w-4xl w-full gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Roles</option>
                <option value="preflight">Preflight</option>
                <option value="preflight_lead">Preflight Lead</option>
                <option value="cmd">CMD</option>
                <option value="cmd_lead">CMD Lead</option>
                <option value="cmd_qc">CMD QC</option>
                <option value="prepress">Prepress</option>
                <option value="prepress_lead">Prepress Lead</option>
                <option value="qc">QC</option>
                <option value="qc_lead">QC Lead</option>
                <option value="production">Production</option>
                <option value="production_lead">Production Lead</option>
                <option value="epson">Epson</option>
                <option value="pm">Project Manager</option>
                <option value="admin">Admin</option>
                <option value="cafeteria">Cafeteria Staff</option>
                <option value="accounts">Accounts</option>
                <option value="support">Support Team</option>
                <option value="it">IT</option>
                <option value="dispatch">Dispatch/Delivery</option>
                <option value="receptionist">Receptionist</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="busy">Busy</option>
                <option value="offline">Offline</option>
                <option value="resigned">Resigned</option>
              </select>

              {(searchTerm || roleFilter || statusFilter) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setRoleFilter('');
                    setStatusFilter('');
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300 transition-colors"
                  title="Clear filters"
                >
                  Clear
                </button>
              )}
              
              <div className="flex gap-2">
                <button
                  onClick={() => setOpenUserCsvDialog(true)}
                  className="flex items-center px-3 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  <Upload className="w-4 h-4 mr-1" />
                  Import CSV
                </button>
                <button
                  onClick={downloadSampleUsersCsv}
                  className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Sample CSV
                </button>
                <button
                  onClick={() => handleOpenDialog()}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add User
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider">
                    <th className="px-6 py-3 font-medium">User</th>
                    <th className="px-6 py-3 font-medium">Role</th>
                    <th className="px-6 py-3 font-medium">Contact</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentUsers.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm mr-3">
                            {user.name.charAt(0)}
                          </div>
                          <span className="font-medium text-gray-900">{user.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {user.role === 'admin' && user.super_admin ? 'Super Admin' : getRoleDisplay(user.role)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <div className="flex flex-col">
                          <span className="flex items-center"><Mail className="w-3 h-3 mr-1" /> {user.email}</span>
                          {user.phone && <span className="flex items-center mt-1"><Phone className="w-3 h-3 mr-1" /> {user.phone}</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => user.break_blocked && clearBreakOverride(user._id, user.name, `${formatBreakOverage(user)}\nBreaks today: ${formatBreakBreakdown(user)}`)}
                            className={`flex items-center gap-1 px-2 py-1.5 rounded-full transition-colors ${
                              user.break_blocked
                                ? 'text-white bg-red-500 hover:bg-red-600 shadow-sm'
                                : user.break_override_cleared_today
                                  ? 'text-green-400 cursor-default'
                                  : 'text-gray-300 cursor-default'
                            }`}
                            title={
                              user.break_blocked
                                ? `Break exceeded by ${formatBreakOverage(user)}\nBreaks today: ${formatBreakBreakdown(user)}\nClick to unblock`
                                : user.break_override_cleared_today
                                  ? `Break block cleared today\nBreaks today: ${formatBreakBreakdown(user)}`
                                  : (user.total_break_minutes > 0
                                      ? `Breaks today: ${formatBreakBreakdown(user)}`
                                      : 'No break issue')
                            }
                          >
                            <Coffee className="w-4 h-4" />
                            {user.break_blocked && (user.break_over_minutes > 0) && (
                              <span className="text-[10px] font-semibold leading-none">
                                +{Math.round(user.break_over_minutes)}m
                              </span>
                            )}
                          </button>
                          <button
                            onClick={() => handleOpenDialog(user)}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user._id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {currentUsers.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        No users found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                <div className="text-sm text-gray-500">
                  Showing {indexOfFirstUser + 1} to {Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} users
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-300 rounded-md bg-white text-gray-600"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                    <button
                      key={number}
                      onClick={() => setCurrentPage(number)}
                      className={`w-8 h-8 flex items-center justify-center rounded-md border ${
                        currentPage === number
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {number}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-gray-300 rounded-md bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 1 && (
        <div>
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <h2 className="text-lg font-semibold text-gray-800">Teams</h2>
            
            <button
              onClick={() => handleOpenTeamDialog()}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Team
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider">
                    <th className="px-6 py-3 font-medium">Team Name</th>
                    <th className="px-6 py-3 font-medium">Type</th>
                    <th className="px-6 py-3 font-medium">Team Leads</th>
                    <th className="px-6 py-3 font-medium">Members</th>
                    <th className="px-6 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {teams.map((team) => (
                    <tr key={team.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{team.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                          {team.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {(() => {
                          if (team.lead_names && team.lead_names.length > 0) {
                            return (
                              <div className="flex flex-col space-y-1">
                                {team.lead_names.map((leadName, index) => (
                                  <span key={index} className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium w-fit">
                                    {leadName}
                                  </span>
                                ))}
                              </div>
                            );
                          } else if (team.lead_name) {
                            // Backwards compatibility for single lead
                            return (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium w-fit">
                                {team.lead_name}
                              </span>
                            );
                          } else {
                            return <span className="text-gray-400">No Leads Assigned</span>;
                          }
                        })()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {team.member_count || 0} members
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleOpenTeamDialog(team)}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTeam(team.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {teams.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        No teams found. Create your first team to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 2 && (
        <div>
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <h2 className="text-lg font-semibold text-gray-800">Customers</h2>
            
            <div className="flex flex-1 max-w-4xl w-full gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {clientSearchTerm && (
                <button
                  onClick={() => setClientSearchTerm('')}
                  className="px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300 transition-colors"
                  title="Clear search"
                >
                  Clear
                </button>
              )}
              
              <div className="flex gap-2">
                <button
                  onClick={() => setOpenClientCsvDialog(true)}
                  className="flex items-center px-3 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  <Upload className="w-4 h-4 mr-1" />
                  Import CSV
                </button>
                <button
                  onClick={downloadSampleClientsCsv}
                  className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Sample CSV
                </button>
                <button
                  onClick={() => handleOpenClientDialog()}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Customer
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider">
                    <th className="px-6 py-3 font-medium">Customer Name</th>
                    <th className="px-6 py-3 font-medium">Contact Info</th>
                    <th className="px-6 py-3 font-medium">Assigned Team</th>
                    <th className="px-6 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {clients
                    .filter(client => {
                      if (!clientSearchTerm) return true;
                      const searchLower = clientSearchTerm.toLowerCase();
                      return (
                        client.name?.toLowerCase().includes(searchLower) ||
                        client.email?.toLowerCase().includes(searchLower) ||
                        client.team_name?.toLowerCase().includes(searchLower)
                      );
                    })
                    .map((client) => (
                    <tr key={client.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{client.name}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <div className="flex flex-col">
                          <span className="flex items-center"><Mail className="w-3 h-3 mr-1" /> {client.email}</span>
                          {client.phone && <span className="flex items-center mt-1"><Phone className="w-3 h-3 mr-1" /> {client.phone}</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {client.team_name || 'No Team Assigned'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleOpenClientDialog(client)}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClient(client.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {clients.filter(client => {
                      if (!clientSearchTerm) return true;
                      const searchLower = clientSearchTerm.toLowerCase();
                      return (
                        client.name?.toLowerCase().includes(searchLower) ||
                        client.email?.toLowerCase().includes(searchLower) ||
                        client.team_name?.toLowerCase().includes(searchLower)
                      );
                    }).length === 0 && (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                        {clientSearchTerm ? 'No customers match your search.' : 'No customers found. Add your first customer to get started.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 4 && (
        <div>
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <h2 className="text-lg font-semibold text-gray-800">Configuration</h2>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (window.confirm('Reset all configuration values to defaults?')) {
                    window.location.reload();
                  }
                }}
                className="flex items-center px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors"
              >
                Reset to Defaults
              </button>
              <button
                onClick={updateComplexityConfig}
                disabled={configLoading}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {configLoading ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Task Time Settings by Complexity</h3>
            <p className="text-sm text-gray-600 mb-6">
              Configure the estimated time (in minutes) for each task based on project complexity level.
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                    <th className="px-6 py-4 font-semibold text-gray-800 text-sm border-r border-gray-200">Complexity</th>
                    {TASK_NAMES.map((taskName) => (
                      <th 
                        key={taskName} 
                        className="px-4 py-4 font-semibold text-gray-800 text-sm text-center whitespace-nowrap border-r border-gray-200"
                      >
                        {taskName}
                      </th>
                    ))}
                    <th className="px-6 py-4 font-semibold text-gray-800 text-sm text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(() => {
                    // Start with the known base order, then append any extra
                    // custom complexities that exist in the config.
                    const allKeys = [...COMPLEXITY_ORDER];
                    Object.keys(complexityConfig || {}).forEach((key) => {
                      if (!allKeys.includes(key)) allKeys.push(key);
                    });

                    return allKeys
                      .filter((complexity) => complexityConfig[complexity])
                      .map((complexity) => (
                        <tr key={complexity} className="hover:bg-gray-50">
                          <td className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-r border-gray-200 capitalize whitespace-nowrap">
                            {complexity.replace(/_/g, ' ')}
                          </td>
                          {TASK_NAMES.map((taskName) => {
                            const minutes = complexityConfig[complexity]?.[taskName] || 0;
                            const hours = Math.floor(minutes / 60);
                            const remainingMins = minutes % 60;
                            const durationDisplay = hours > 0 && remainingMins > 0 
                              ? `${hours}h ${remainingMins}m`
                              : hours > 0 
                              ? `${hours}h`
                              : `${remainingMins}m`;

                            return (
                              <td 
                                key={`${complexity}-${taskName}`} 
                                className="px-4 py-4 text-center border-r border-gray-200"
                              >
                                <div className="flex flex-col items-center justify-center">
                                  <div className="text-sm font-semibold text-gray-900">{minutes}</div>
                                  <div className="text-xs text-gray-500 font-mono">{durationDisplay}</div>
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => setComplexityRowEditDialog({
                                isOpen: true,
                                complexity: complexity,
                                values: { ...complexityConfig[complexity] }
                              })}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors inline-flex items-center"
                              title="Edit all tasks for this complexity"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ));
                  })()}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-2">
                <input
                  type="text"
                  value={newComplexityName}
                  onChange={(e) => setNewComplexityName(e.target.value)}
                  placeholder="Add new complexity (e.g. Ultra Complex)"
                  className="w-full sm:max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => {
                    const name = (newComplexityName || '').trim();
                    if (!name) return;
                    const key = name.toLowerCase().replace(/\s+/g, '_');
                    if (complexityConfig[key]) {
                      alert('A complexity with this name already exists.');
                      return;
                    }

                    const baseRow = {};
                    VALID_TASK_NAMES.forEach((taskName) => {
                      baseRow[taskName] = 0;
                    });

                    setComplexityConfig((prev) => ({
                      ...prev,
                      [key]: baseRow
                    }));
                    setNewComplexityName('');
                  }}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Add Complexity
                </button>
              </div>
              <p className="text-xs text-gray-500 max-w-md">
                New complexities will appear as additional rows. The name is stored as a lowercase key
                (e.g. "Ultra Complex" → "ultra_complex"). Set minutes for each task, then click
                <span className="font-semibold"> Save Configuration</span> to apply.
              </p>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Note:</p>
                  <p>These time estimates are used for project planning and resource allocation. Each complexity level has different expected times per task. Changes will apply to new projects created after saving.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 5 && (
        <div>
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <h2 className="text-lg font-semibold text-gray-800">API Requests</h2>
            <button
              onClick={saveApiRequestsConfig}
              disabled={apiRequestsLoading}
              className={`flex items-center px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${
                apiRequestsDirty
                  ? 'bg-red-600 hover:bg-red-700 animate-pulse'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {apiRequestsLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {apiRequestsDirty ? 'Unsaved Changes — Save Now' : 'Save'}
            </button>
          </div>

          {/* ── Global API Key (one key for ALL outbound webhooks) ── */}
          <div className="mb-6 rounded-lg border border-indigo-200 bg-indigo-50/40 p-5">
            <h3 className="text-sm font-semibold text-gray-800">API Key (X-API-Key)</h3>
            <p className="text-xs text-gray-500 mt-0.5 mb-3">
              A single key sent on <span className="font-medium">all</span> outbound requests — Task Action Webhooks and Integration API Endpoints. Receivers should verify the <code>X-API-Key</code> header.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <code className="text-sm bg-white border border-gray-200 rounded px-3 py-2 font-mono break-all flex-1 min-w-[240px]">
                {integrationApiKey || <span className="text-gray-400">No key set — requests are sent without authentication</span>}
              </code>
              {integrationApiKey && (
                <button
                  onClick={() => navigator.clipboard?.writeText(integrationApiKey)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >Copy</button>
              )}
              <button
                onClick={generateIntegrationApiKey}
                disabled={apiKeyBusy}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >{apiKeyBusy ? 'Working…' : integrationApiKey ? 'Generate New Key' : 'Generate Key'}</button>
              {integrationApiKey && (
                <button
                  onClick={clearIntegrationApiKey}
                  disabled={apiKeyBusy}
                  className="px-3 py-2 text-sm text-red-600 hover:underline disabled:opacity-50"
                >Remove</button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">Generating a new key takes effect immediately — update any external systems with the new value.</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Task Action Webhooks</h3>
            <p className="text-sm text-gray-600 mb-6">
              Configure webhook URLs per task and action. When the selected action occurs on that task, the system will send a POST request with XML project details.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task</label>
                <select
                  value={selectedApiTask}
                  onChange={(e) => setSelectedApiTask(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {TASK_NAMES.map((taskName) => (
                    <option key={taskName} value={taskName}>{taskName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                <select
                  value={selectedApiAction}
                  onChange={(e) => setSelectedApiAction(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {TASK_ACTIONS.map(({ key, label }) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">API URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newApiUrl}
                    onChange={(e) => setNewApiUrl(e.target.value)}
                    placeholder="https://example.com/webhook"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => {
                      const url = (newApiUrl || '').trim();
                      if (!url) return;
                      if (!(url.startsWith('http://') || url.startsWith('https://'))) {
                        alert('URL must start with http:// or https://');
                        return;
                      }
                      const configKey = `${selectedApiTask}::${selectedApiAction}`;
                      setApiRequestsConfig(prev => {
                        const current = Array.isArray(prev?.[configKey]) ? prev[configKey] : [];
                        if (current.some(e => apiEntryUrl(e) === url)) return prev;
                        return { ...prev, [configKey]: [...current, url] };
                      });
                      setApiRequestsDirty(true);
                      setNewApiUrl('');
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-gray-800">
                  URLs for: {selectedApiTask} — {TASK_ACTIONS.find(a => a.key === selectedApiAction)?.label}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={async () => {
                      const configKey = `${selectedApiTask}::${selectedApiAction}`;
                      const hasConfigured = Array.isArray(apiRequestsConfig?.[configKey])
                        && apiRequestsConfig[configKey].some(e => apiEntryUrl(e).trim());
                      // If nothing configured, let the admin paste a one-off test URL.
                      let testUrl = '';
                      if (!hasConfigured) {
                        testUrl = (window.prompt('No URL configured for this task/action. Enter a URL to send a one-off test (e.g. your webhook.site URL):') || '').trim();
                        if (!testUrl) return;
                      }
                      try {
                        const res = await api.post('/admin/config/api-requests/test',
                          { task_name: selectedApiTask, action: selectedApiAction, test_url: testUrl || undefined },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        const r = res.data?.results || [];
                        const lines = r.map(x => `${x.url} → ${x.http_status || 'ERR'}${x.error ? ' (' + x.error + ')' : ''}`).join('\n');
                        alert(`Sent ${res.data?.sent_to || 0} test webhook(s):\n${lines}`);
                      } catch (e) {
                        alert(e.response?.data?.error || 'Failed to send test webhook');
                      }
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800"
                    title="Send a sample webhook (to configured URLs, or a one-off URL you enter)"
                  >
                    Send Test
                  </button>
                  <button
                    onClick={() => {
                      const configKey = `${selectedApiTask}::${selectedApiAction}`;
                      setApiRequestsConfig(prev => ({ ...prev, [configKey]: [] }));
                      setApiRequestsDirty(true);
                    }}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="divide-y divide-gray-200">
                {(() => {
                  const configKey = `${selectedApiTask}::${selectedApiAction}`;
                  const entries = Array.isArray(apiRequestsConfig?.[configKey]) ? apiRequestsConfig[configKey] : [];
                  return entries.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-500">No URLs configured for this task / action.</div>
                  ) : entries.map((e) => {
                    const url = apiEntryUrl(e);
                    return (
                      <div key={url} className="px-4 py-3 flex items-center justify-between gap-2">
                        <div className="text-sm text-gray-700 break-all">{url}</div>
                        <button
                          onClick={() => {
                            setApiRequestsConfig(prev => ({
                              ...prev,
                              [configKey]: (Array.isArray(prev?.[configKey]) ? prev[configKey] : []).filter(x => apiEntryUrl(x) !== url)
                            }));
                            setApiRequestsDirty(true);
                          }}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors shrink-0"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            <div className="mt-6 border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="text-sm font-semibold text-gray-800">All Configured Webhooks</div>
                <p className="text-xs text-gray-500 mt-1">
                  Summary of all tasks that have one or more API URLs configured.
                </p>
              </div>
              {allApiRequestRows.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                        <th className="px-4 py-2 font-medium">Task</th>
                        <th className="px-4 py-2 font-medium">Action</th>
                        <th className="px-4 py-2 font-medium">API URL</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {allApiRequestRows.map((row) => (
                        <tr key={row.key} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-800 whitespace-nowrap">{row.taskName}</td>
                          <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">{row.actionLabel}</td>
                          <td className="px-4 py-2 text-sm text-gray-700 break-all">{row.url}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-4 py-6 text-center text-sm text-gray-500">
                  No API Requests configured yet.
                </div>
              )}
            </div>
          </div>

          {/* ── Integration API Reference ── */}
          <div className="bg-white rounded-lg shadow-md p-6 mt-2 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">Integration API Endpoints</h3>
            <p className="text-sm text-gray-500 mb-5">
              Use these endpoints from your external system to query job status or trigger batch task completion.
            </p>

            {/* Base URL config */}
            <div className="mb-6">
              <label className="block text-xs font-medium text-gray-600 mb-1">Server Base URL</label>
              <input
                type="text"
                value={integrationConfig.api_base_url}
                onChange={e => { setIntegrationConfig(p => ({ ...p, api_base_url: e.target.value })); setApiRequestsDirty(true); }}
                placeholder={`${window.location.origin}`}
                className="w-full max-w-lg px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400 mt-1">Used to build the full endpoint URLs below.</p>
            </div>

            {/* Endpoint 1: Job Status */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">GET</span>
                  <h4 className="font-medium text-gray-800 text-sm">Job Status</h4>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(`${integrationConfig.api_base_url || window.location.origin}/job_status/{job_number}`)}
                  className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1"
                  title="Copy URL"
                >
                  <Copy size={13} /> Copy
                </button>
              </div>

              {/* URL */}
              <code className="block bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm text-blue-800 font-mono mb-3">
                {(integrationConfig.api_base_url || window.location.origin)}/job_status/<span className="text-orange-600">&#123;job_number&#125;</span>
              </code>

              {/* Required auth header */}
              <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded p-2">
                <p className="text-xs font-semibold text-indigo-800 mb-1">Required header</p>
                <code className="text-xs text-indigo-900 font-mono break-all">
                  X-API-Key: {integrationApiKey || '<your API key>'}
                </code>
                <p className="text-[11px] text-indigo-700 mt-1">Generate it in the API Key panel above. Requests without a valid key return 401.</p>
              </div>

              {/* Format options */}
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-600 mb-2">Response Format</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="bg-gray-50 border border-gray-200 rounded p-2">
                    <p className="text-xs font-semibold text-gray-700 mb-0.5">Query param (recommended)</p>
                    <code className="text-xs text-blue-700 font-mono">?format=json</code>
                    <span className="text-xs text-gray-400 mx-1">or</span>
                    <code className="text-xs text-purple-700 font-mono">?format=xml</code>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded p-2">
                    <p className="text-xs font-semibold text-gray-700 mb-0.5">Accept header</p>
                    <code className="text-xs text-purple-700 font-mono">Accept: application/xml</code>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded p-2">
                    <p className="text-xs font-semibold text-gray-700 mb-0.5">Default (no param)</p>
                    <span className="text-xs text-gray-500">Returns <code className="bg-gray-100 px-1 rounded">application/json</code></span>
                  </div>
                </div>
              </div>

              {/* Response examples */}
              <p className="text-xs font-medium text-gray-600 mb-2">Response Examples</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-1.5 py-0.5 rounded">JSON</span>
                    <span className="text-gray-400">?format=json</span>
                  </p>
                  <pre className="bg-gray-50 rounded p-2 text-xs text-gray-700 overflow-x-auto border border-gray-200">{`{
  "job_number": "VP-0042",
  "job_name": "Catalogue Print",
  "client": "Acme Corp",
  "status": "in_progress",
  "current_task": "Preflight",
  "current_task_status": "in_progress",
  "assigned_user": "John",
  "started_at": "2026-05-25T08:30:00Z",
  "tasks": [
    { "name": "Design", "status": "completed", "step_order": 1 },
    { "name": "Preflight", "status": "in_progress", "step_order": 2 }
  ]
}`}</pre>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-1.5 py-0.5 rounded">XML</span>
                    <span className="text-gray-400">?format=xml</span>
                  </p>
                  <pre className="bg-gray-50 rounded p-2 text-xs text-gray-700 overflow-x-auto border border-gray-200">{`<?xml version="1.0" encoding="UTF-8"?>
<job_status>
  <job_number>VP-0042</job_number>
  <job_name>Catalogue Print</job_name>
  <client>Acme Corp</client>
  <status>in_progress</status>
  <current_task>Preflight</current_task>
  <current_task_status>in_progress</current_task_status>
  <assigned_user>John</assigned_user>
  <started_at>2026-05-25T08:30:00Z</started_at>
  <tasks>
    <item><name>Design</name><status>completed</status><step_order>1</step_order></item>
    <item><name>Preflight</name><status>in_progress</status><step_order>2</step_order></item>
  </tasks>
</job_status>`}</pre>
                </div>
              </div>
            </div>

            {/* Endpoint 2: Complete Task */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">POST</span>
                  <h4 className="font-medium text-gray-800 text-sm">Complete Tasks (Batch)</h4>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(`${integrationConfig.api_base_url || window.location.origin}/complete_task/`)}
                  className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1"
                  title="Copy URL"
                >
                  <Copy size={13} /> Copy
                </button>
              </div>
              <code className="block bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm text-green-800 font-mono mb-3">
                {(integrationConfig.api_base_url || window.location.origin)}/complete_task/
              </code>
              <div className="mb-3 bg-indigo-50 border border-indigo-200 rounded p-2">
                <p className="text-xs font-semibold text-indigo-800 mb-1">Required headers</p>
                <pre className="text-xs text-indigo-900 font-mono overflow-x-auto">{`Content-Type: application/json
X-API-Key: ${integrationApiKey || '<your API key>'}`}</pre>
              </div>
              <div className="text-xs text-gray-500 space-y-2">
                <p><span className="font-medium text-gray-600">Request payload:</span></p>
                <pre className="bg-gray-50 rounded p-2 text-xs text-gray-700 overflow-x-auto">{`{
  "job_number": "VP-0042",
  "complete_until": "RIP QC"
}`}</pre>
                <p className="text-xs text-gray-400 mt-1">
                  Completes all tasks from the current active task up to (and including) <code className="bg-gray-100 px-1 rounded">complete_until</code>.
                  If a task is already started by a user, it will be closed under their name. The next task after the range will be auto-started.
                </p>
              </div>
            </div>

            {/* POST /book_job/ — create project from booking XML */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">POST</span>
                  <h4 className="font-medium text-gray-800 text-sm">Book Job (Create Project)</h4>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(`${integrationConfig.api_base_url || window.location.origin}/book_job/`)}
                  className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1"
                  title="Copy URL"
                >
                  <Copy size={13} /> Copy
                </button>
              </div>
              <code className="block bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm text-green-800 font-mono mb-3">
                {(integrationConfig.api_base_url || window.location.origin)}/book_job/
              </code>
              <div className="mb-3 bg-indigo-50 border border-indigo-200 rounded p-2">
                <p className="text-xs font-semibold text-indigo-800 mb-1">Required headers</p>
                <pre className="text-xs text-indigo-900 font-mono overflow-x-auto">{`Content-Type: application/xml
X-API-Key: ${integrationApiKey || '<your API key>'}`}</pre>
              </div>
              <div className="text-xs text-gray-500 space-y-2">
                <p><span className="font-medium text-gray-600">Request payload (XML):</span></p>
                <pre className="bg-gray-50 rounded p-2 text-xs text-gray-700 overflow-x-auto">{`<?xml version="1.0"?>
<root>
  <job_code>251168262</job_code>
  <customerid>6371</customerid>
  <job_location>hyderabad</job_location>
  <customer>MUKUND CORRUPACK PVT LTD</customer>
  <received_date>2025-11-05 12:25:00</received_date>
  <job_mode>Prepress regular</job_mode>
  <job_name>PARADIES CREAM CRACKER 300X36</job_name>
  <printing_type>PREPRESS DETAILS CORRUGATION</printing_type>
  <job_nature>Corrugation</job_nature>
  <job_type>New Artwork</job_type>
  <plate_thickness>VPR_4-7</plate_thickness>
  <pages>0</pages>
  <payment_mode>Credit</payment_mode>
  <category/>
  <created_by>Kavitha V</created_by>
</root>`}</pre>
                <p className="text-xs text-gray-400 mt-1">
                  Creates a project that runs the same workflow as <span className="font-medium">Active Projects → New Project</span>.
                  <code className="bg-gray-100 px-1 rounded">job_name</code> → title, <code className="bg-gray-100 px-1 rounded">customer</code> → client.
                  Idempotent on <code className="bg-gray-100 px-1 rounded">job_code</code> — re-sending the same code returns the existing job instead of duplicating it.
                  JSON with the same field names is also accepted. Add <code className="bg-gray-100 px-1 rounded">?format=xml</code> for an XML response.
                </p>
              </div>
            </div>

            {/* POST /start_task/ */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">POST</span>
                  <h4 className="font-medium text-gray-800 text-sm">Start Task</h4>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(`${integrationConfig.api_base_url || window.location.origin}/start_task/`)}
                  className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1"
                  title="Copy URL"
                >
                  <Copy size={13} /> Copy
                </button>
              </div>
              <code className="block bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm text-green-800 font-mono mb-3">
                {(integrationConfig.api_base_url || window.location.origin)}/start_task/
              </code>
              <div className="mb-3 bg-indigo-50 border border-indigo-200 rounded p-2">
                <p className="text-xs font-semibold text-indigo-800 mb-1">Required headers</p>
                <pre className="text-xs text-indigo-900 font-mono overflow-x-auto">{`Content-Type: application/json
X-API-Key: ${integrationApiKey || '<your API key>'}`}</pre>
              </div>
              <div className="text-xs text-gray-500 space-y-2">
                <p><span className="font-medium text-gray-600">Request payload:</span></p>
                <pre className="bg-gray-50 rounded p-2 text-xs text-gray-700 overflow-x-auto">{`{
  "job_number": "251168262",
  "task_name": "Preflight",
  "user_name": "Kavitha V"
}`}</pre>
                <p className="text-xs text-gray-400 mt-1">
                  Starts the named task at the current time, credited to <code className="bg-gray-100 px-1 rounded">user_name</code>.
                  If the name matches a user it is linked; otherwise the name is recorded as-is. Fails if the task is already in progress or completed.
                </p>
              </div>
            </div>

            {/* POST /complete_task_single/ */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">POST</span>
                  <h4 className="font-medium text-gray-800 text-sm">Complete Task (Single)</h4>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(`${integrationConfig.api_base_url || window.location.origin}/complete_task_single/`)}
                  className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1"
                  title="Copy URL"
                >
                  <Copy size={13} /> Copy
                </button>
              </div>
              <code className="block bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm text-green-800 font-mono mb-3">
                {(integrationConfig.api_base_url || window.location.origin)}/complete_task_single/
              </code>
              <div className="mb-3 bg-indigo-50 border border-indigo-200 rounded p-2">
                <p className="text-xs font-semibold text-indigo-800 mb-1">Required headers</p>
                <pre className="text-xs text-indigo-900 font-mono overflow-x-auto">{`Content-Type: application/json
X-API-Key: ${integrationApiKey || '<your API key>'}`}</pre>
              </div>
              <div className="text-xs text-gray-500 space-y-2">
                <p><span className="font-medium text-gray-600">Request payload:</span></p>
                <pre className="bg-gray-50 rounded p-2 text-xs text-gray-700 overflow-x-auto">{`{
  "job_number": "251168262",
  "task_name": "Preflight"
}`}</pre>
                <p className="text-xs text-gray-400 mt-1">
                  Completes the named task at the current time. If it is already in progress, it is closed under the <span className="font-medium">same user</span> who started it; otherwise it is started and completed now under "API".
                </p>
              </div>
            </div>

            {/* GET /insertable_tasks */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">GET</span>
                  <h4 className="font-medium text-gray-800 text-sm">Insertable Tasks</h4>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(`${integrationConfig.api_base_url || window.location.origin}/insertable_tasks`)}
                  className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1"
                  title="Copy URL"
                >
                  <Copy size={13} /> Copy
                </button>
              </div>
              <code className="block bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm text-blue-800 font-mono mb-3">
                {(integrationConfig.api_base_url || window.location.origin)}/insertable_tasks
              </code>
              <div className="mb-3 bg-indigo-50 border border-indigo-200 rounded p-2">
                <p className="text-xs font-semibold text-indigo-800 mb-1">Required headers</p>
                <pre className="text-xs text-indigo-900 font-mono overflow-x-auto">{`X-API-Key: ${integrationApiKey || '<your API key>'}`}</pre>
              </div>
              <div className="text-xs text-gray-500 space-y-2">
                <p><span className="font-medium text-gray-600">Response:</span></p>
                <pre className="bg-gray-50 rounded p-2 text-xs text-gray-700 overflow-x-auto">{`{
  "tasks": [
    { "name": "CMD",               "step_order": 3,  "team": "cmd" },
    { "name": "Die creation",      "step_order": 9,  "team": "preflight" },
    { "name": "Image Export",      "step_order": 13, "team": "prepress" },
    { "name": "Image Duplication", "step_order": 15, "team": "prepress" }
  ]
}`}</pre>
                <p className="text-xs text-gray-400 mt-1">
                  The tasks that can be added to a job with <span className="font-medium">Insert Task</span> below.
                  Call this rather than hardcoding names — the list grows as new steps are added.
                </p>
              </div>
            </div>

            {/* POST /insert_task/ */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">POST</span>
                  <h4 className="font-medium text-gray-800 text-sm">Insert Task</h4>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(`${integrationConfig.api_base_url || window.location.origin}/insert_task/`)}
                  className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1"
                  title="Copy URL"
                >
                  <Copy size={13} /> Copy
                </button>
              </div>
              <code className="block bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm text-green-800 font-mono mb-3">
                {(integrationConfig.api_base_url || window.location.origin)}/insert_task/
              </code>
              <div className="mb-3 bg-indigo-50 border border-indigo-200 rounded p-2">
                <p className="text-xs font-semibold text-indigo-800 mb-1">Required headers</p>
                <pre className="text-xs text-indigo-900 font-mono overflow-x-auto">{`Content-Type: application/json
X-API-Key: ${integrationApiKey || '<your API key>'}`}</pre>
              </div>
              <div className="text-xs text-gray-500 space-y-2">
                <p><span className="font-medium text-gray-600">Request payload:</span></p>
                <pre className="bg-gray-50 rounded p-2 text-xs text-gray-700 overflow-x-auto">{`{
  "job_number": "251168262",
  "task_name": "Image Duplication"
}`}</pre>
                <p><span className="font-medium text-gray-600">Response:</span></p>
                <pre className="bg-gray-50 rounded p-2 text-xs text-gray-700 overflow-x-auto">{`{
  "ok": true,
  "task_id": "...",
  "task_name": "Image Duplication",
  "step_order": 15,
  "job_number": "251168262"
}`}</pre>
                <p className="text-xs text-gray-400 mt-1">
                  Adds a flexible task to an existing job. The task is created <span className="font-medium">unassigned</span>,
                  for a lead or auto-assign to pick up. Task names are matched case-insensitively.
                  If the job already has that task open the call returns <span className="font-medium">400</span>,
                  so it is safe to retry.
                </p>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1">
                  Note the <span className="font-medium">trailing slash</span>. Posting to
                  <span className="font-mono"> /insert_task</span> returns a 308 redirect, which some HTTP
                  clients turn into a GET.
                </p>
              </div>
            </div>
          </div>

        </div>
      )}

      {activeTab === 6 && (
        <div>
          {/* Locations & Resources Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <div className="flex space-x-8">
              <button
                onClick={() => setResourcesSubTab(0)}
                className={`py-3 px-1 border-b-2 font-medium text-sm ${
                  resourcesSubTab === 0
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Production Locations
              </button>
              <button
                onClick={() => setResourcesSubTab(1)}
                className={`py-3 px-1 border-b-2 font-medium text-sm ${
                  resourcesSubTab === 1
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Job Nature
              </button>
              <button
                onClick={() => setResourcesSubTab(2)}
                className={`py-3 px-1 border-b-2 font-medium text-sm ${
                  resourcesSubTab === 2
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Job Type
              </button>
            </div>
          </div>

          {/* Production Locations Subtab */}
          {resourcesSubTab === 0 && (
            <div>
              <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <h2 className="text-lg font-semibold text-gray-800">Production Locations</h2>
                <button
                  onClick={() => handleOpenLocationDialog()}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Location
                </button>
              </div>

              <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider">
                        <th className="px-6 py-3 font-medium">Location Name</th>
                        <th className="px-6 py-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {productionLocations.map((loc) => {
                        const id = loc.id || loc._id;
                        return (
                          <tr key={id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="font-medium text-gray-900">{loc.name}</div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => handleOpenLocationDialog(loc)}
                                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteLocation(id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {productionLocations.length === 0 && (
                        <tr>
                          <td colSpan="2" className="px-6 py-8 text-center text-gray-500">
                            No production locations found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Job Nature Subtab */}
          {resourcesSubTab === 1 && (
            <div>
              <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <h2 className="text-lg font-semibold text-gray-800">Job Nature</h2>
                <button
                  onClick={() => handleOpenJobNatureDialog()}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Job Nature
                </button>
              </div>

              <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider">
                        <th className="px-6 py-3 font-medium">Job Nature Name</th>
                        <th className="px-6 py-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {jobNatureList.map((item) => {
                        const id = item.id || item._id;
                        return (
                          <tr key={id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="font-medium text-gray-900">{item.name}</div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => handleOpenJobNatureDialog(item)}
                                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteJobNature(id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {jobNatureList.length === 0 && (
                        <tr>
                          <td colSpan="2" className="px-6 py-8 text-center text-gray-500">
                            No job nature items found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Job Type Subtab */}
          {resourcesSubTab === 2 && (
            <div>
              <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <h2 className="text-lg font-semibold text-gray-800">Job Type</h2>
                <button
                  onClick={() => handleOpenJobTypeDialog()}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Job Type
                </button>
              </div>

              <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider">
                        <th className="px-6 py-3 font-medium">Job Type Name</th>
                        <th className="px-6 py-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {jobTypeList.map((item) => {
                        const id = item.id || item._id;
                        return (
                          <tr key={id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="font-medium text-gray-900">{item.name}</div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => handleOpenJobTypeDialog(item)}
                                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteJobType(id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {jobTypeList.length === 0 && (
                        <tr>
                          <td colSpan="2" className="px-6 py-8 text-center text-gray-500">
                            No job type items found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 7 && (
        <div>
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <h2 className="text-lg font-semibold text-gray-800">Error Categories</h2>
            <div className="flex flex-col md:flex-row gap-2 flex-1 max-w-xl items-center md:items-stretch">
              <input
                type="text"
                value={newErrorCategory}
                onChange={(e) => setNewErrorCategory(e.target.value)}
                placeholder="New category name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddErrorCategory();
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={newErrorCategoryType}
                onChange={(e) => setNewErrorCategoryType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
              >
                <option value="internal">Internal</option>
                <option value="external">External</option>
              </select>
              <button
                onClick={handleAddErrorCategory}
                disabled={categoriesLoading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {categoriesLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">
              <div className="overflow-x-auto">
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-800">Internal Error Categories</span>
                  <span className="text-xs text-gray-500">
                    {(errorCategories.filter(c => (c.type || 'internal') === 'internal')).length} items
                  </span>
                </div>
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-white text-gray-600 text-xs uppercase tracking-wider border-b border-gray-200">
                      <th className="px-6 py-2 font-medium">Name</th>
                      <th className="px-6 py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {errorCategories.filter(c => (c.type || 'internal') === 'internal').map((category) => {
                      const id = category.id || category._id;
                      return (
                        <tr key={id} className="hover:bg-gray-50">
                          <td className="px-6 py-3">
                            <div className="font-medium text-gray-900">{category.name}</div>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleEditErrorCategory(category)}
                                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                title="Edit"
                                disabled={categoriesLoading}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteErrorCategory(id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                title="Delete"
                                disabled={categoriesLoading}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {errorCategories.filter(c => (c.type || 'internal') === 'internal').length === 0 && (
                      <tr>
                        <td colSpan="2" className="px-6 py-8 text-center text-gray-500">
                          No internal error categories configured yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="overflow-x-auto">
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-800">External Error Categories</span>
                  <span className="text-xs text-gray-500">
                    {(errorCategories.filter(c => c.type === 'external')).length} items
                  </span>
                </div>
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-white text-gray-600 text-xs uppercase tracking-wider border-b border-gray-200">
                      <th className="px-6 py-2 font-medium">Name</th>
                      <th className="px-6 py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {errorCategories.filter(c => c.type === 'external').map((category) => {
                      const id = category.id || category._id;
                      return (
                        <tr key={id} className="hover:bg-gray-50">
                          <td className="px-6 py-3">
                            <div className="font-medium text-gray-900">{category.name}</div>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleEditErrorCategory(category)}
                                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                title="Edit"
                                disabled={categoriesLoading}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteErrorCategory(id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                title="Delete"
                                disabled={categoriesLoading}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {errorCategories.filter(c => c.type === 'external').length === 0 && (
                      <tr>
                        <td colSpan="2" className="px-6 py-8 text-center text-gray-500">
                          No external error categories configured yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Note:</p>
                <p>Error categories are used when rejecting approval or RIP QC tasks. Operators can select <span className="font-semibold">internal</span> (process) and <span className="font-semibold">external</span> (client/file) error categories to document what went wrong.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Dialog */}
      {openDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {currentUser._id ? 'Edit User' : 'Add User'}
              </h3>
              <button onClick={handleCloseDialog} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Two columns: identity on the left, what they're allowed to do on
                the right. Collapses to one column on narrow screens. */}
            <div className="p-4 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">User details</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={currentUser.name}
                    onChange={(e) => setCurrentUser({ ...currentUser, name: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="John Doe"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-gray-400 font-normal">(required for non-AD users)</span></label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="email"
                    value={currentUser.email}
                    onChange={(e) => setCurrentUser({ ...currentUser, email: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="john@veepee.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">AD Username (optional)</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={currentUser.ad_username}
                    onChange={(e) => setCurrentUser({ ...currentUser, ad_username: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="deskpack@VEEPEE or VEEPEE\\deskpack"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={currentUser.phone}
                    onChange={(e) => setCurrentUser({ ...currentUser, phone: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={currentUser.role === 'admin' && currentUser.super_admin === true ? 'super_admin' : currentUser.role}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'super_admin') {
                        setCurrentUser({ ...currentUser, role: 'admin', super_admin: true });
                      } else {
                        setCurrentUser({ ...currentUser, role: v, super_admin: false });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="preflight">Preflight</option>
                    <option value="preflight_lead">Preflight Lead</option>
                    <option value="preflight_prepress">Preflight & Prepress</option>
                    <option value="cmd">CMD</option>
                    <option value="cmd_lead">CMD Lead</option>
                    <option value="cmd_qc">CMD QC</option>
                    <option value="prepress">Prepress</option>
                    <option value="prepress_lead">Prepress Lead</option>
                    <option value="qc">QC</option>
                    <option value="qc_lead">QC Lead</option>
                    <option value="production">Production</option>
                    <option value="production_lead">Production Lead</option>
                    <option value="epson_lead">Epson Lead</option>
                    <option value="pm">Project Manager</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="cafeteria">Cafeteria Staff</option>
                    <option value="accounts">Accounts</option>
                    <option value="support">Support Team</option>
                    <option value="it">IT</option>
                    <option value="dispatch">Dispatch/Delivery</option>
                    <option value="receptionist">Receptionist</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={currentUser.status}
                    onChange={(e) => setCurrentUser({ ...currentUser, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="busy">Busy</option>
                    <option value="offline">Offline</option>
                    <option value="resigned">Resigned</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentUser._id ? 'New Password (leave blank to keep current)' : 'Password'}
                </label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="password"
                    value={currentUser.password}
                    onChange={(e) => setCurrentUser({ ...currentUser, password: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                </div>
              </div>
              </div>

              {/* ── Right column: permissions ────────────────────────────── */}
              <div className="space-y-4 md:border-l md:border-gray-100 md:pl-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Permissions</p>

                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <input
                    type="checkbox"
                    id="daily_report_enabled"
                    checked={currentUser.daily_report_enabled !== false}
                    onChange={(e) => setCurrentUser({ ...currentUser, daily_report_enabled: e.target.checked })}
                    className="w-4 h-4 accent-blue-600 cursor-pointer"
                  />
                  <label htmlFor="daily_report_enabled" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                    Send Daily Activity Report email to this user
                  </label>
                </div>

                <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <input
                    type="checkbox"
                    id="break_block_exempt"
                    checked={currentUser.break_block_exempt === true}
                    onChange={(e) => setCurrentUser({ ...currentUser, break_block_exempt: e.target.checked })}
                    className="w-4 h-4 accent-amber-500 cursor-pointer"
                  />
                  <label htmlFor="break_block_exempt" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                    Exempt from break overtime block
                    <span className="block text-xs font-normal text-gray-500 mt-0.5">This user will not be blocked even if their break time exceeds the limit</span>
                  </label>
                </div>

                {/* Meals are opted out one at a time: someone may eat lunch in
                    the canteen but go home for dinner. Ticked = they take it. */}
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
                  <p className="text-sm font-medium text-gray-700">Cafeteria meals</p>
                  <p className="text-xs text-gray-500 mt-0.5 mb-2">
                    Untick a meal the user does not take — they are left out of that meal&apos;s count and get no PIN for it. Unticking all three hides the Cafeteria page from them.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    {MEAL_KEYS.map((m) => (
                      <label key={m} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!(currentUser.cafeteria_meals_disabled || {})[m]}
                          onChange={(e) => setCurrentUser({
                            ...currentUser,
                            cafeteria_meals_disabled: {
                              ...(currentUser.cafeteria_meals_disabled || {}),
                              [m]: !e.target.checked,
                            },
                          })}
                          className="w-4 h-4 accent-orange-500 cursor-pointer"
                        />
                        {MEAL_LABELS[m]}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Off by default: most operators are given work by their TL
                    or PM, and that flow is unchanged for them. */}
                <div className="flex items-center gap-3 p-3 bg-violet-50 border border-violet-200 rounded-md">
                  <input
                    type="checkbox"
                    id="auto_job_assign_enabled"
                    checked={currentUser.auto_job_assign_enabled === true}
                    onChange={(e) => setCurrentUser({ ...currentUser, auto_job_assign_enabled: e.target.checked })}
                    className="w-4 h-4 accent-violet-600 cursor-pointer"
                  />
                  <label htmlFor="auto_job_assign_enabled" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                    Auto job assign
                    <span className="block text-xs font-normal text-gray-500 mt-0.5">This user can ask for their next job and the system picks it from their task and job-nature priorities. Off by default — leave it off and their TL/PM keeps assigning work as usual</span>
                  </label>
                </div>

                <div className="flex items-center gap-3 p-3 bg-sky-50 border border-sky-200 rounded-md">
                  <input
                    type="checkbox"
                    id="repeat_orders_enabled"
                    checked={currentUser.repeat_orders_disabled !== true}
                    onChange={(e) => setCurrentUser({ ...currentUser, repeat_orders_disabled: !e.target.checked })}
                    className="w-4 h-4 accent-sky-600 cursor-pointer"
                  />
                  <label htmlFor="repeat_orders_enabled" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                    Allow Repeat Orders
                    <span className="block text-xs font-normal text-gray-500 mt-0.5">This user can search the archive and extract files from an old job. On by default; unticking hides the Repeat Orders page from them</span>
                  </label>
                </div>

                {currentUser.role === 'admin' && currentUser.super_admin === true && (
                  <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-md text-xs text-indigo-700">
                    <strong>Super Admin</strong> — full access to every Settings tab, including Customers, Customer Accounts, API Requests, SMTP, Email &amp; Schedule, Notification Alerts, Cutoff Workflow, Job Auto-Assign, Shift Timings, Leaves, and Complete Projects.
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleCloseDialog}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User CSV Import Dialog */}
      {openUserCsvDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Import Users from CSV</h3>
              <button
                onClick={() => {
                  setOpenUserCsvDialog(false);
                  setUserCsvText('');
                  setUserCsvResult(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              <p className="text-sm text-gray-600">
                Paste CSV data with columns similar to your spreadsheet:
                <span className="font-mono ml-1">Full Name,Email,Phone,Role,Status,AD User Name</span>.
                Role values like <span className="font-semibold">QC, Preflight, Prepress, Data Entry</span> will be mapped to internal roles.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CSV Data</label>
                <textarea
                  rows={10}
                  value={userCsvText}
                  onChange={(e) => setUserCsvText(e.target.value)}
                  placeholder={
                    'Full Name,Email,Phone,Role,Status,AD User Name\n' +
                    'Abitha,abitha@veepeegraphics.com,080-46450500,QC,active,Abitha\n' +
                    'Ajay B,ajay.b@veepeegraphics.com,080-46450500,Data Entry,active,Ajay B'
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                />
              </div>

              {userCsvResult && userCsvResult.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-md p-3 bg-gray-50 max-h-40 overflow-y-auto">
                  <div className="text-sm font-semibold text-gray-800 mb-1">Import summary</div>
                  <ul className="text-xs text-gray-700 space-y-1">
                    {userCsvResult.map((r, idx) => (
                      <li key={idx}>
                        Row {r.row}: {r.status}
                        {r.reason ? ` - ${r.reason}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setOpenUserCsvDialog(false);
                  setUserCsvText('');
                  setUserCsvResult(null);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleImportUsersFromCsv}
                disabled={userCsvProcessing}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {userCsvProcessing ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Dialog */}
      {openTeamDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {currentTeam.id ? 'Edit Team' : 'Create Team'}
              </h3>
              <button onClick={handleCloseTeamDialog} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
                <input
                  type="text"
                  value={currentTeam.name}
                  onChange={(e) => setCurrentTeam({ ...currentTeam, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Team Alpha"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Team Type</label>
                <select
                  value={currentTeam.type}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setCurrentTeam((prev) => ({
                      ...prev,
                      type: nextType,
                      lead_ids: [],
                      members: [],
                      location_ids: nextType === 'production' ? (Array.isArray(prev.location_ids) ? prev.location_ids : []) : []
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="preflight">Preflight</option>
                  <option value="cmd">CMD</option>
                  <option value="prepress">Prepress</option>
                  <option value="qc">QC</option>
                  <option value="production">Production</option>
                </select>
              </div>

              {currentTeam.type === 'production' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Production Locations</label>
                  <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto">
                    {productionLocations.map((loc) => {
                      const id = loc.id || loc._id;
                      const checked = Array.isArray(currentTeam.location_ids) && currentTeam.location_ids.includes(id);
                      return (
                        <div key={id} className="flex items-center mb-2 last:mb-0">
                          <input
                            type="checkbox"
                            id={`location-${id}`}
                            checked={checked}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setCurrentTeam((prev) => {
                                const prevIds = Array.isArray(prev.location_ids) ? prev.location_ids : [];
                                const nextIds = isChecked
                                  ? Array.from(new Set([...prevIds, id]))
                                  : prevIds.filter((x) => x !== id);
                                return { ...prev, location_ids: nextIds };
                              });
                            }}
                            className="mr-2"
                          />
                          <label htmlFor={`location-${id}`} className="text-sm text-gray-700">
                            {loc.name}
                          </label>
                        </div>
                      );
                    })}
                    {productionLocations.length === 0 && (
                      <div className="text-sm text-gray-500">No locations configured yet</div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Team Leads</label>
                  <span className="text-xs text-gray-500">
                    {currentTeam.lead_ids.length}/2 selected
                  </span>
                </div>
                <div className="border border-gray-300 rounded-md p-3 max-h-32 overflow-y-auto">
                  {getAvailableLeads(currentTeam.type).map(user => (
                    <div key={user._id} className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id={`lead-${user._id}`}
                        checked={currentTeam.lead_ids.includes(user._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Only allow selection if less than 2 leads are selected
                            if (currentTeam.lead_ids.length < 2) {
                              setCurrentTeam({
                                ...currentTeam,
                                lead_ids: [...currentTeam.lead_ids, user._id]
                              });
                            }
                          } else {
                            setCurrentTeam({
                              ...currentTeam,
                              lead_ids: currentTeam.lead_ids.filter(id => id !== user._id)
                            });
                          }
                        }}
                        disabled={!currentTeam.lead_ids.includes(user._id) && currentTeam.lead_ids.length >= 2}
                        className="mr-2 disabled:opacity-50"
                      />
                      <label 
                        htmlFor={`lead-${user._id}`} 
                        className={`text-sm flex-1 ${
                          !currentTeam.lead_ids.includes(user._id) && currentTeam.lead_ids.length >= 2 
                            ? 'text-gray-400' 
                            : 'text-gray-700'
                        }`}
                      >
                        {user.name} ({getRoleDisplay(user.role)})
                      </label>
                    </div>
                  ))}
                  {getAvailableLeads(currentTeam.type).length === 0 && (
                    <div className="text-sm text-gray-500">No leads available for this team type</div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select up to 2 team leads. You can have primary and secondary leads.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Team Members</label>
                <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto">
                  {getAvailableMembers(currentTeam.type).map(user => (
                    <div key={user._id} className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id={`member-${user._id}`}
                        checked={currentTeam.members.includes(user._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCurrentTeam({
                              ...currentTeam,
                              members: [...currentTeam.members, user._id]
                            });
                          } else {
                            setCurrentTeam({
                              ...currentTeam,
                              members: currentTeam.members.filter(id => id !== user._id)
                            });
                          }
                        }}
                        className="mr-2"
                      />
                      <label htmlFor={`member-${user._id}`} className="text-sm text-gray-700">
                        {user.name} ({getRoleDisplay(user.role)})
                      </label>
                    </div>
                  ))}
                  {getAvailableMembers(currentTeam.type).length === 0 && (
                    <div className="text-sm text-gray-500">No users available for this team type</div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleCloseTeamDialog}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTeam}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Nature Dialog */}
      {openJobNatureDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {isEditingJobNature ? 'Edit Job Nature' : 'Add Job Nature'}
              </h3>
              <button onClick={handleCloseJobNatureDialog} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Nature Name</label>
                <input
                  type="text"
                  value={currentJobNature.name}
                  onChange={(e) => setCurrentJobNature({ ...currentJobNature, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="E.g., Offset Printing, Digital Printing"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleCloseJobNatureDialog}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveJobNature}
                disabled={jobNatureLoading}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {jobNatureLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Type Dialog */}
      {openJobTypeDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {isEditingJobType ? 'Edit Job Type' : 'Add Job Type'}
              </h3>
              <button onClick={handleCloseJobTypeDialog} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Type Name</label>
                <input
                  type="text"
                  value={currentJobType.name}
                  onChange={(e) => setCurrentJobType({ ...currentJobType, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="E.g., Brochure, Business Cards"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleCloseJobTypeDialog}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveJobType}
                disabled={jobTypeLoading}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {jobTypeLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Production Location Dialog */}
      {openLocationDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {isEditingLocation ? 'Edit Location' : 'Add Location'}
              </h3>
              <button onClick={handleCloseLocationDialog} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                <input
                  type="text"
                  value={currentLocation.name}
                  onChange={(e) => setCurrentLocation({ ...currentLocation, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Production Floor A"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleCloseLocationDialog}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLocation}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client Dialog */}
      {openClientDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {currentClient.id ? 'Edit Customer' : 'Add Customer'}
              </h3>
              <button onClick={handleCloseClientDialog} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                <input
                  type="text"
                  value={currentClient.name}
                  onChange={(e) => setCurrentClient({ ...currentClient, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ABC Corporation"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="email"
                    value={currentClient.email}
                    onChange={(e) => setCurrentClient({ ...currentClient, email: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="contact@abccorp.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={currentClient.phone}
                    onChange={(e) => setCurrentClient({ ...currentClient, phone: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  value={currentClient.address}
                  onChange={(e) => setCurrentClient({ ...currentClient, address: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="123 Main St, City, State, ZIP"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Team</label>
                <select
                  value={currentClient.team_id}
                  onChange={(e) => setCurrentClient({ ...currentClient, team_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Team</option>
                  {teams.filter(team => team.type === 'prepress').map(team => (
                    <option key={team.id} value={team.id}>{team.name} ({team.type})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={currentClient.preflight_enabled !== false}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setCurrentClient((prev) => ({
                        ...prev,
                        preflight_enabled: enabled,
                        // Single-up Approval depends on Preflight; disable together.
                        single_app_approval_enabled: enabled ? (prev.single_app_approval_enabled !== false) : false
                      }));
                    }}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Enable Preflight</span>
                </label>
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={currentClient.preflight_enabled !== false && currentClient.single_app_approval_enabled !== false}
                    disabled={currentClient.preflight_enabled === false}
                    onChange={(e) => setCurrentClient({ ...currentClient, single_app_approval_enabled: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300"
                  />
                  <span className={`text-sm font-medium ${currentClient.preflight_enabled === false ? 'text-gray-400' : 'text-gray-700'}`}>
                    Enable Single-up Approval
                  </span>
                </label>
              </div>

              <div>
                <label className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    checked={currentClient.epson_enabled === true}
                    onChange={(e) => setCurrentClient({ ...currentClient, epson_enabled: e.target.checked })}
                    className="w-4 h-4 mt-0.5 text-blue-600 rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Enable Epson Printout
                    <span className="block text-xs font-normal text-gray-500 mt-0.5">
                      Off by default. When on, new projects for this client include the Epson Printout
                      step automatically — a PM or Admin can still remove it on an individual project.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleCloseClientDialog}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveClient}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client CSV Import Dialog */}
      {openClientCsvDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Import Clients from CSV</h3>
              <button
                onClick={() => {
                  setOpenClientCsvDialog(false);
                  setClientCsvText('');
                  setClientCsvResult(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              <p className="text-sm text-gray-600">
                Paste CSV data with the following columns (header required):
                <span className="font-mono ml-1">name,email,phone,address,team,preflight_enabled,single_app_approval_enabled,epson_enabled</span>.
                The <span className="font-semibold">team</span> column should match an existing team name (optional).
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CSV Data</label>
                <textarea
                  rows={10}
                  value={clientCsvText}
                  onChange={(e) => setClientCsvText(e.target.value)}
                  placeholder={
                    'name,email,phone,address,team,preflight_enabled,single_app_approval_enabled,epson_enabled\n' +
                    'Client A,clienta@example.com,+1 555 111 1111,"123 Main St City",Prepress Team,true,true,false\n' +
                    'Client B,clientb@example.com,+1 555 222 2222,"456 Second St City",,true,false,true'
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                />
              </div>

              {clientCsvResult && clientCsvResult.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-md p-3 bg-gray-50 max-h-40 overflow-y-auto">
                  <div className="text-sm font-semibold text-gray-800 mb-1">Import summary</div>
                  <ul className="text-xs text-gray-700 space-y-1">
                    {clientCsvResult.map((r, idx) => (
                      <li key={idx}>
                        Row {r.row}: {r.status}
                        {r.reason ? ` - ${r.reason}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setOpenClientCsvDialog(false);
                  setClientCsvText('');
                  setClientCsvResult(null);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleImportClientsFromCsv}
                disabled={clientCsvProcessing}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {clientCsvProcessing ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complexity Row Edit Dialog */}
      {complexityRowEditDialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Edit {complexityRowEditDialog.complexity?.replace(/_/g, ' ')} Complexity
              </h3>
              <button 
                onClick={() => setComplexityRowEditDialog({ isOpen: false, complexity: null, values: {} })} 
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {TASK_NAMES.map((taskName) => (
                <div key={taskName} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <label className="text-sm font-medium text-gray-700 min-w-48">{taskName}</label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="number"
                      min="0"
                      max="480"
                      value={complexityRowEditDialog.values[taskName] || 0}
                      onChange={(e) => setComplexityRowEditDialog({
                        ...complexityRowEditDialog,
                        values: {
                          ...complexityRowEditDialog.values,
                          [taskName]: parseInt(e.target.value) || 0
                        }
                      })}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-500 font-medium min-w-12">min</span>
                    <span className="text-xs text-gray-500 font-mono min-w-16">
                      {(() => {
                        const mins = complexityRowEditDialog.values[taskName] || 0;
                        const hours = Math.floor(mins / 60);
                        const remainingMins = mins % 60;
                        if (hours > 0 && remainingMins > 0) {
                          return `${hours}h ${remainingMins}m`;
                        } else if (hours > 0) {
                          return `${hours}h`;
                        } else {
                          return `${remainingMins}m`;
                        }
                      })()}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setComplexityRowEditDialog({ isOpen: false, complexity: null, values: {} })}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setComplexityConfig(prev => ({
                    ...prev,
                    [complexityRowEditDialog.complexity]: complexityRowEditDialog.values
                  }));
                  setComplexityRowEditDialog({ isOpen: false, complexity: null, values: {} });
                }}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Category Edit Modal */}
      {isEditingCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Edit Error Category</h3>
              <button 
                onClick={() => {
                  setIsEditingCategory(false);
                  setEditingCategoryId(null);
                  setEditingCategoryName('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category Name</label>
                <input
                  type="text"
                  value={editingCategoryName}
                  onChange={(e) => setEditingCategoryName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Color mismatch, Misalignment"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setIsEditingCategory(false);
                  setEditingCategoryId(null);
                  setEditingCategoryName('');
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveErrorCategory}
                disabled={categoriesLoading}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {categoriesLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SMTP Settings Tab */}
      {activeTab === 8 && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-800">SMTP Settings</h2>
            <div className="flex gap-2">
              <button
                onClick={saveSmtpSettings}
                disabled={smtpLoading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {smtpLoading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-gray-600 mb-6">
              Configure the SMTP server connection used to send report emails. Report schedules and recipients are managed in the Email &amp; Schedule tab.
            </p>

            <div className="max-w-2xl">
              {/* SMTP Server Settings */}
              <div className="space-y-4">
                <h3 className="text-md font-semibold text-gray-700 border-b pb-2">Server Settings</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Host <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={smtpSettings.smtp_host}
                    onChange={(e) => setSmtpSettings({...smtpSettings, smtp_host: e.target.value})}
                    placeholder="smtp.gmail.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Port <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={smtpSettings.smtp_port}
                    onChange={(e) => setSmtpSettings({...smtpSettings, smtp_port: parseInt(e.target.value) || 587})}
                    placeholder="587"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={smtpSettings.smtp_user}
                    onChange={(e) => setSmtpSettings({...smtpSettings, smtp_user: e.target.value})}
                    placeholder="your-email@gmail.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showSmtpPassword ? "text" : "password"}
                      value={smtpSettings.smtp_password}
                      onChange={(e) => setSmtpSettings({...smtpSettings, smtp_password: e.target.value})}
                      placeholder="App password or SMTP password"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showSmtpPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">For Gmail, use an App Password</p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="smtp_use_tls"
                    checked={smtpSettings.smtp_use_tls}
                    onChange={(e) => setSmtpSettings({...smtpSettings, smtp_use_tls: e.target.checked})}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="smtp_use_tls" className="ml-2 text-sm text-gray-700">
                    Use TLS (recommended)
                  </label>
                </div>
              </div>

            </div>

            {/* Test Email Section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-700 mb-4">Manual Report Triggers</h3>
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 max-w-md">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test Email Address (optional)
                  </label>
                  <input
                    type="email"
                    value={smtpTestEmail}
                    onChange={(e) => setSmtpTestEmail(e.target.value)}
                    placeholder="Leave empty to send to all recipients"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <button
                  onClick={triggerDailyTimeTrackingReport}
                  disabled={smtpLoading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {smtpLoading ? 'Sending...' : (smtpTestEmail ? 'Send Test Report' : 'Send to All Recipients')}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Enter an email address to send a test report only to that email, or leave empty to send to all configured recipients.
              </p>
              {smtpTestResult && (
                <div className={`mt-4 p-3 rounded-md ${smtpTestResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {smtpTestResult.message}
                </div>
              )}
            </div>
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-700 mb-2">About Weekly Reports</h3>
              <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                <li><strong>Admin Users:</strong> Receive all-users time tracking report</li>
                <li><strong>Team Leads:</strong> Receive their team members' time tracking report</li>
                <li>Reports include: Shift hours, Job hours, Break hours, Idle hours</li>
                <li>Reports are sent as HTML email with CSV attachment</li>
                <li>The email service must be running separately: <code className="bg-gray-100 px-1 py-0.5 rounded">python3 email_service.py</code></li>
              </ul>
            </div>
          </div>
        </div>
      )}
      {/* Email & Schedule Tab */}
      {activeTab === 13 && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-800">Email & Schedule</h2>
            <div className="flex gap-2">
              <button
                onClick={saveSmtpSettings}
                disabled={smtpLoading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {smtpLoading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-gray-600 mb-6">
              Enable the email service and configure the scheduled report emails, their send times and recipients.
              The SMTP server connection is configured in the <span className="font-medium">SMTP Settings</span> tab.
            </p>

            <div className="max-w-2xl">
              {/* Email & Schedule Settings */}
              <div className="space-y-4">
                
                <div className="flex items-center pt-2">
                  <input
                    type="checkbox"
                    id="email_service_enabled"
                    checked={smtpSettings.email_service_enabled}
                    onChange={(e) => setSmtpSettings({...smtpSettings, email_service_enabled: e.target.checked})}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="email_service_enabled" className="ml-2 text-sm text-gray-700">
                    Enable Email Service
                  </label>
                </div>

                {/* ── Cafeteria Vendor Emails ── */}
                <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50/40 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        🍽️ Cafeteria Vendor Emails
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Email each meal's headcount (with names + CSV) to the vendor at the meal's cutoff time.
                      </p>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!mealSettings.meal_emails_enabled}
                        onChange={(e) => { setMealSettings({ ...mealSettings, meal_emails_enabled: e.target.checked }); setMealSettingsSaved(false); }}
                        className="h-4 w-4 accent-orange-600"
                      />
                      Enable
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {['lunch', 'dinner', 'snacks'].map((meal) => {
                      const cfg = (mealSettings.meal_emails || {})[meal] || {};
                      const cutoff = (mealSettings.cutoffs || {})[meal] || '';
                      return (
                        <div key={meal} className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-800 capitalize">{meal}</span>
                            <label className="inline-flex items-center gap-1 text-xs text-gray-600">
                              <input
                                type="checkbox"
                                checked={cfg.enabled !== false}
                                onChange={(e) => updateMealEmail(meal, 'enabled', e.target.checked)}
                                className="accent-orange-600"
                              /> on
                            </label>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Vendor email(s), comma-separated</label>
                            <textarea
                              rows={3}
                              value={cfg.recipients || ''}
                              onChange={(e) => updateMealEmail(meal, 'recipients', e.target.value)}
                              placeholder="vendor1@example.com, vendor2@example.com"
                              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm font-mono break-all resize-y"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Send time (blank = cutoff {cutoff || '—'})</label>
                            <input
                              type="time"
                              value={cfg.send_time || ''}
                              onChange={(e) => updateMealEmail(meal, 'send_time', e.target.value)}
                              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                            />
                          </div>
                          <button
                            onClick={() => sendMealTest(meal)}
                            className="text-xs text-blue-600 hover:underline"
                          >Send test now</button>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <input
                      type="email"
                      value={mealEmailTest}
                      onChange={(e) => setMealEmailTest(e.target.value)}
                      placeholder="test@example.com (for 'Send test now')"
                      className="border border-gray-300 rounded-md px-2 py-1.5 text-sm flex-1 min-w-[200px]"
                    />
                    <button
                      onClick={saveMealSettings}
                      disabled={mealSettingsSaving}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm"
                    >
                      {mealSettingsSaving ? 'Saving…' : mealSettingsSaved ? '✓ Saved' : 'Save Meal Emails'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Times are IST. Cutoffs themselves are set in <span className="font-medium">Shift Timings</span>.
                    Requires SMTP to be configured and Email Service enabled.
                  </p>
                </div>

                {/* ── Cafeteria Meal Collection Reports ── */}
                <div className="mt-4 rounded-xl border border-teal-200 bg-teal-50/40 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        📋 Cafeteria Meal Collection Reports
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        After each meal, email who collected, who did not, and any extra portions
                        given at the counter. One report per meal.
                      </p>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!mealSettings.collection_reports_enabled}
                        onChange={(e) => { setMealSettings({ ...mealSettings, collection_reports_enabled: e.target.checked }); setMealSettingsSaved(false); }}
                        className="h-4 w-4 accent-teal-600"
                      />
                      Enable
                    </label>
                  </div>

                  <div className="flex items-center gap-2 mb-3 text-sm text-gray-700">
                    <span>Send</span>
                    <input
                      type="number"
                      min="0"
                      max="12"
                      value={mealSettings.collection_report_offset_hours ?? 2}
                      onChange={(e) => {
                        setMealSettings({ ...mealSettings, collection_report_offset_hours: Math.max(0, Math.min(12, parseInt(e.target.value || '0', 10) || 0)) });
                        setMealSettingsSaved(false);
                      }}
                      className="w-16 border border-gray-300 rounded-md px-2 py-1 text-sm"
                    />
                    <span>hour(s) after each meal's cutoff, unless a time is set below.</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {['lunch', 'dinner', 'snacks'].map((meal) => {
                      const cfg = (mealSettings.collection_reports || {})[meal] || {};
                      const cutoff = (mealSettings.cutoffs || {})[meal] || '';
                      const offset = mealSettings.collection_report_offset_hours ?? 2;
                      let auto = '—';
                      if (cutoff && /^\d{1,2}:\d{2}$/.test(cutoff)) {
                        const [h, m] = cutoff.split(':').map(Number);
                        auto = `${String((h + offset) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                      }
                      return (
                        <div key={meal} className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-800 capitalize">{meal}</span>
                            <label className="inline-flex items-center gap-1 text-xs text-gray-600">
                              <input
                                type="checkbox"
                                checked={cfg.enabled !== false}
                                onChange={(e) => updateCollectionReport(meal, 'enabled', e.target.checked)}
                                className="accent-teal-600"
                              /> on
                            </label>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Recipient(s), comma-separated</label>
                            <textarea
                              rows={3}
                              value={cfg.recipients || ''}
                              onChange={(e) => updateCollectionReport(meal, 'recipients', e.target.value)}
                              placeholder="manager@example.com, hr@example.com"
                              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm font-mono break-all resize-y"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">
                              Send time (blank = {auto})
                            </label>
                            <input
                              type="time"
                              value={cfg.send_time || ''}
                              onChange={(e) => updateCollectionReport(meal, 'send_time', e.target.value)}
                              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <button
                      onClick={saveMealSettings}
                      disabled={mealSettingsSaving}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm"
                    >
                      {mealSettingsSaving ? 'Saving…' : mealSettingsSaved ? '✓ Saved' : 'Save Collection Reports'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Extra portions are added by the counter when the food is handed over, so they
                    appear here but not in the vendor headcount sent beforehand.
                  </p>
                </div>

                <div className="mt-4 p-4 border rounded-md  border-gray-300 bg-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Weekly User Time Tracking Report</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      From Email
                    </label>
                    <input
                      type="email"
                      value={smtpSettings.smtp_from_email}
                      onChange={(e) => setSmtpSettings({...smtpSettings, smtp_from_email: e.target.value})}
                      placeholder="Same as username if empty"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      From Name
                    </label>
                    <input
                      type="text"
                      value={smtpSettings.smtp_from_name}
                      onChange={(e) => setSmtpSettings({...smtpSettings, smtp_from_name: e.target.value})}
                      placeholder="VP Prepress Reports"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Weekly Report Day
                    </label>
                    <select
                      value={smtpSettings.weekly_report_day}
                      onChange={(e) => setSmtpSettings({...smtpSettings, weekly_report_day: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="monday">Monday</option>
                      <option value="tuesday">Tuesday</option>
                      <option value="wednesday">Wednesday</option>
                      <option value="thursday">Thursday</option>
                      <option value="friday">Friday</option>
                      <option value="saturday">Saturday</option>
                      <option value="sunday">Sunday</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Weekly Report Time
                    </label>
                    <input
                      type="time"
                      value={smtpSettings.weekly_report_time}
                      onChange={(e) => setSmtpSettings({...smtpSettings, weekly_report_time: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                

                <div className="mt-4 p-4 border rounded-md  border-gray-300 bg-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily User Time Tracking Report</h3>

                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="daily_user_time_tracking_report_enabled"
                      checked={smtpSettings.daily_user_time_tracking_report_enabled}
                      onChange={(e) => setSmtpSettings({
                        ...smtpSettings,
                        daily_user_time_tracking_report_enabled: e.target.checked
                      })}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="daily_user_time_tracking_report_enabled" className="ml-2 text-sm text-gray-700">
                      Enable daily user time tracking report
                    </label>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Send Time (IST)
                      </label>
                      <input
                        type="time"
                        value={smtpSettings.daily_user_time_tracking_report_time}
                        onChange={(e) => setSmtpSettings({
                          ...smtpSettings,
                          daily_user_time_tracking_report_time: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Subject
                      </label>
                      <input
                        type="text"
                        value={smtpSettings.daily_user_time_tracking_report_subject}
                        onChange={(e) => setSmtpSettings({
                          ...smtpSettings,
                          daily_user_time_tracking_report_subject: e.target.value
                        })}
                        placeholder="Daily All Users Time Tracking Report"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Recipient Emails
                      </label>
                      <textarea
                        rows={2}
                        value={smtpSettings.daily_user_time_tracking_report_recipients}
                        onChange={(e) => setSmtpSettings({
                          ...smtpSettings,
                          daily_user_time_tracking_report_recipients: e.target.value
                        })}
                        placeholder="user1@example.com, user2@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Separate multiple email addresses with commas, semicolons, or spaces. Leave empty to send only to admins and team leads.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Daily Non Efficient Users Report */}
                <div className="mt-4 p-4 border rounded-md border-gray-300 bg-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily Non Efficient Users Report</h3>

                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="non_efficient_users_report_enabled"
                      checked={smtpSettings.non_efficient_users_report_enabled}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, non_efficient_users_report_enabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="non_efficient_users_report_enabled" className="ml-2 text-sm text-gray-700">
                      Enable daily non-efficient users report (Total Shift &lt; 8h, or Job Hrs &lt; 8h, or No Jobs Hrs &gt; 2h)
                    </label>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Send Time (IST)</label>
                      <input
                        type="time"
                        value={smtpSettings.non_efficient_users_report_time}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, non_efficient_users_report_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject</label>
                      <input
                        type="text"
                        value={smtpSettings.non_efficient_users_report_subject}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, non_efficient_users_report_subject: e.target.value })}
                        placeholder="Daily Non Efficient Users Report"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Emails</label>
                      <textarea
                        rows={2}
                        value={smtpSettings.non_efficient_users_report_recipients}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, non_efficient_users_report_recipients: e.target.value })}
                        placeholder="manager1@example.com, manager2@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Lists only users flagged non-efficient for yesterday. HTML body + CSV attachment. Separate addresses with commas, semicolons, or spaces.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Break Block Cleared — event-driven notification */}
                <div className="mt-4 p-4 border rounded-md border-amber-300 bg-amber-50">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Break Block Cleared Alert</h3>

                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="break_override_email_enabled"
                      checked={!!smtpSettings.break_override_email_enabled}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, break_override_email_enabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="break_override_email_enabled" className="ml-2 text-sm text-gray-700">
                      Email when a break-time block is cleared, with the reason given
                    </label>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject</label>
                      <input
                        type="text"
                        value={smtpSettings.break_override_email_subject || ''}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, break_override_email_subject: e.target.value })}
                        placeholder="Break Block Cleared"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">The user's name is appended automatically.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Emails</label>
                      <textarea
                        rows={2}
                        value={smtpSettings.break_override_email_recipients || ''}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, break_override_email_recipients: e.target.value })}
                        placeholder="hr@example.com, manager@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Sent <span className="font-medium">immediately</span> when a block is cleared — there is no
                        send time. A blocked user enters the reason for the overrun and continues working;
                        this email tells you who was over, by how many minutes, and the reason they gave.
                        No action is needed from the recipient. Separate addresses with commas, semicolons, or spaces.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Late Shift Start — event-driven notification */}
                <div className="mt-4 p-4 border rounded-md border-amber-300 bg-amber-50">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Late Shift Start Alert</h3>

                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="late_login_email_enabled"
                      checked={!!smtpSettings.late_login_email_enabled}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, late_login_email_enabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="late_login_email_enabled" className="ml-2 text-sm text-gray-700">
                      Email when a user clocks in late, with the reason given
                    </label>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject</label>
                      <input
                        type="text"
                        value={smtpSettings.late_login_email_subject || ''}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, late_login_email_subject: e.target.value })}
                        placeholder="Late Shift Start"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">The user's name and how late they were are appended automatically.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Emails</label>
                      <textarea
                        rows={2}
                        value={smtpSettings.late_login_email_recipients || ''}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, late_login_email_recipients: e.target.value })}
                        placeholder="hr@example.com, manager@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Sent <span className="font-medium">immediately</span> when the user submits their reason —
                        there is no send time. A clock-in is late once it passes the shift start plus the
                        10-minute grace period, the same rule as the Late badge on the timesheet.
                        Separate addresses with commas, semicolons, or spaces.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Daily Late & Break Overrun digest — the scheduled summary,
                    separate from the two instant alerts above. */}
                <div className="mt-4 p-4 border rounded-md border-orange-300 bg-orange-50">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily Attendance Report</h3>

                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="attendance_report_enabled"
                      checked={!!smtpSettings.attendance_report_enabled}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, attendance_report_enabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="attendance_report_enabled" className="ml-2 text-sm text-gray-700">
                      Send one daily summary of yesterday's <strong>late starts</strong>,
                      {' '}<strong>break overruns</strong> and <strong>long shifts</strong>, each with the reason given
                    </label>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Send Time (IST)</label>
                      <input
                        type="time"
                        value={smtpSettings.attendance_report_time || '09:00'}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, attendance_report_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Covers <span className="font-medium">yesterday</span>, so a morning time gives a complete day.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject</label>
                      <input
                        type="text"
                        value={smtpSettings.attendance_report_subject || ''}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, attendance_report_subject: e.target.value })}
                        placeholder="Daily Late & Break Overrun Report"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">The date is appended automatically.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Emails</label>
                      <textarea
                        rows={2}
                        value={smtpSettings.attendance_report_recipients || ''}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, attendance_report_recipients: e.target.value })}
                        placeholder="hr@example.com, manager@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Its own list — independent of the instant Late Shift Start and Break Block alerts,
                        which keep their own recipients and stay on unless you switch them off.
                        Separate addresses with commas, semicolons, or spaces.
                      </p>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="attendance_report_send_when_empty"
                        checked={!!smtpSettings.attendance_report_send_when_empty}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, attendance_report_send_when_empty: e.target.checked })}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="attendance_report_send_when_empty" className="ml-2 text-sm text-gray-700">
                        Send even when nobody was late and nobody exceeded a break
                        <span className="block text-xs text-gray-500">Off by default — a daily "all clear" email is usually just noise.</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Today Job Information Email Settings */}
                <div className="mt-4 p-4 border rounded-md  border-gray-300 bg-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily Job Information Email</h3>

                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="today_job_info_email_enabled"
                      checked={smtpSettings.today_job_info_email_enabled}
                      onChange={(e) => setSmtpSettings({
                        ...smtpSettings,
                        today_job_info_email_enabled: e.target.checked
                      })}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="today_job_info_email_enabled" className="ml-2 text-sm text-gray-700">
                      Enable daily Job Information" CSV email
                    </label>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Send Time (IST)
                      </label>
                      <input
                        type="time"
                        value={smtpSettings.today_job_info_email_time}
                        onChange={(e) => setSmtpSettings({
                          ...smtpSettings,
                          today_job_info_email_time: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Subject
                      </label>
                      <input
                        type="text"
                        value={smtpSettings.today_job_info_email_subject}
                        onChange={(e) => setSmtpSettings({
                          ...smtpSettings,
                          today_job_info_email_subject: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Recipient Emails
                      </label>
                      <textarea
                        rows={2}
                        value={smtpSettings.today_job_info_email_recipients}
                        onChange={(e) => setSmtpSettings({
                          ...smtpSettings,
                          today_job_info_email_recipients: e.target.value
                        })}
                        placeholder="user1@example.com, user2@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Separate multiple email addresses with commas, semicolons, or spaces.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Daily User Completed Task Details Email Settings */}
                <div className="mt-4 p-4 border rounded-md  border-gray-300 bg-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily User Completed Task Details Email</h3>

                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="task_details_email_enabled"
                      checked={smtpSettings.task_details_email_enabled}
                      onChange={(e) => setSmtpSettings({
                        ...smtpSettings,
                        task_details_email_enabled: e.target.checked
                      })}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="task_details_email_enabled" className="ml-2 text-sm text-gray-700">
                      Enable daily "User Completed Task Details" CSV email
                    </label>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Send Time (IST)
                      </label>
                      <input
                        type="time"
                        value={smtpSettings.task_details_email_time}
                        onChange={(e) => setSmtpSettings({
                          ...smtpSettings,
                          task_details_email_time: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Subject
                      </label>
                      <input
                        type="text"
                        value={smtpSettings.task_details_email_subject}
                        onChange={(e) => setSmtpSettings({
                          ...smtpSettings,
                          task_details_email_subject: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Recipient Emails
                      </label>
                      <textarea
                        rows={2}
                        value={smtpSettings.task_details_email_recipients}
                        onChange={(e) => setSmtpSettings({
                          ...smtpSettings,
                          task_details_email_recipients: e.target.value
                        })}
                        placeholder="manager1@example.com, manager2@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Separate multiple email addresses with commas, semicolons, or spaces.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Daily External Rejection Report Email Settings */}
                <div className="mt-4 p-4 border rounded-md border-gray-300 bg-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily External Rejection Report Email</h3>

                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="external_error_report_enabled"
                      checked={smtpSettings.external_error_report_enabled}
                      onChange={(e) => setSmtpSettings({
                        ...smtpSettings,
                        external_error_report_enabled: e.target.checked
                      })}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="external_error_report_enabled" className="ml-2 text-sm text-gray-700">
                      Enable daily external rejection report email (yesterday&apos;s errors)
                    </label>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Send Time (IST)</label>
                      <input
                        type="time"
                        value={smtpSettings.external_error_report_time}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, external_error_report_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject</label>
                      <input
                        type="text"
                        value={smtpSettings.external_error_report_subject}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, external_error_report_subject: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Emails</label>
                      <textarea
                        rows={2}
                        value={smtpSettings.external_error_report_recipients}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, external_error_report_recipients: e.target.value })}
                        placeholder="manager1@example.com, manager2@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">Separate multiple addresses with commas, semicolons, or spaces.</p>
                    </div>
                  </div>
                </div>

                {/* Daily Pending Jobs Report Email Settings */}
                <div className="mt-4 p-4 border rounded-md border-gray-300 bg-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily Pending Jobs Report Email</h3>

                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="pending_jobs_email_enabled"
                      checked={smtpSettings.pending_jobs_email_enabled}
                      onChange={(e) => setSmtpSettings({
                        ...smtpSettings,
                        pending_jobs_email_enabled: e.target.checked
                      })}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="pending_jobs_email_enabled" className="ml-2 text-sm text-gray-700">
                      Enable daily pending jobs email (one mail with two CSVs: yesterday &amp; the day before)
                    </label>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Send Time (IST)</label>
                      <input
                        type="time"
                        value={smtpSettings.pending_jobs_email_time}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, pending_jobs_email_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject</label>
                      <input
                        type="text"
                        value={smtpSettings.pending_jobs_email_subject}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, pending_jobs_email_subject: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Emails</label>
                      <textarea
                        rows={2}
                        value={smtpSettings.pending_jobs_email_recipients}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, pending_jobs_email_recipients: e.target.value })}
                        placeholder="manager1@example.com, manager2@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">Separate multiple addresses with commas, semicolons, or spaces. The email includes two attachments: today's and yesterday's pending jobs.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Notification Alert Settings Tab */}
      {activeTab === 9 && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Notification Alert Settings</h2>
              <p className="text-sm text-gray-500 mt-1">
                Control which notification types trigger the blocking alert overlay.
                Disabled types are still recorded in the notification panel — users just won't see the pop-up alert.
              </p>
            </div>
            <button
              onClick={saveNotificationAlertSettings}
              disabled={notifSettingsLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {notifSettingsLoading ? 'Saving...' : notifSettingsSaved ? '\u2713 Saved' : 'Save Settings'}
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
            {[
              { key: 'task_hold',        label: 'Task Hold Alert',         desc: 'Fired when a task is placed on hold.',                      color: 'text-orange-600' },
              { key: 'task_pause',       label: 'Task Pause Alert',        desc: 'Fired when a task is paused.',                              color: 'text-yellow-600' },
              { key: 'no_jobs',          label: 'No Jobs Alert',           desc: 'Fired when an operator reports no available jobs.',         color: 'text-blue-600'   },
              { key: 'approval_rejected',label: 'Approval Rejection Alert',desc: 'Sent to the person who submitted the work when a PM, lead or client rejects it.', color: 'text-red-600'    },
              { key: 'approval_approved',label: 'Approval Approved Alert', desc: 'Sent to the person who submitted the work when a PM or client approves it.',    color: 'text-emerald-600'},
              { key: 'task_overtime',    label: 'Task Overtime Alert',     desc: 'Fired when a task exceeds its expected completion time.',   color: 'text-purple-600' },
              { key: 'manual',           label: 'Manual Notification',     desc: 'Manually sent notifications from privileged users.',        color: 'text-gray-600'   },
            ].map(({ key, label, desc, color }) => (
              <div key={key} className="flex items-center justify-between px-6 py-4">
                <div className="flex items-start gap-3">
                  <Bell className={`w-5 h-5 mt-0.5 flex-shrink-0 ${color}`} />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{label}</p>
                    <p className="text-xs text-gray-500">{desc}</p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    setNotificationAlertSettings(prev => ({ ...prev, [key]: !prev[key] }))
                  }
                  className="flex-shrink-0 ml-4"
                  title={notificationAlertSettings[key] ? 'Click to disable alert' : 'Click to enable alert'}
                >
                  {notificationAlertSettings[key] ? (
                    <ToggleRight className="w-9 h-9 text-blue-600" />
                  ) : (
                    <ToggleLeft className="w-9 h-9 text-gray-400" />
                  )}
                </button>
              </div>
            ))}
          </div>

          <p className="mt-4 text-xs text-gray-400">
            Changes take effect immediately after saving. The notification inbox always shows all entries regardless of this setting.
          </p>
        </div>
      )}

      {activeTab === 10 && (
        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Cutoff Workflow</h2>
              <p className="text-sm text-gray-500 mt-1">Configure daily client cutoff time. Jobs received after cutoff are treated as next-day cutoff delivery targets.</p>
            </div>
            <button
              onClick={saveAdvancedOptions}
              disabled={advancedOptionsSaving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {advancedOptionsSaving ? 'Saving...' : advancedOptionsSaved ? '✓ Saved' : 'Save Settings'}
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
            <h3 className="text-base font-semibold text-gray-800">Cutoff Time-Based Workflow</h3>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Global Cutoff Times</label>
                <div className="flex gap-2">
                  <input
                    type="time"
                    value={newGlobalCutoffTime}
                    onChange={(e) => setNewGlobalCutoffTime(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                  <button
                    onClick={addGlobalCutoffTime}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                  >
                    Add
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(workflowAutomationConfig.global_cutoff.cutoff_times || []).map((t) => (
                    <span key={t} className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200">
                      {t}
                      <button onClick={() => removeGlobalCutoffTime(t)} className="text-red-600 hover:text-red-800">x</button>
                    </span>
                  ))}
                  {(workflowAutomationConfig.global_cutoff.cutoff_times || []).length === 0 && (
                    <span className="text-xs text-gray-500">No global cutoff times added.</span>
                  )}
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              Timezone is fixed to IST/SLST (+05:30) for this app.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { key: 'enabled', label: 'Enable Cutoff Automation' },
                { key: 'allow_manual_priority_ranking', label: 'Allow Manual Priority Ranking' },
                { key: 'enable_cutoff_jobs_tab', label: 'Enable Cutoff Jobs Tab' },
                { key: 'active_job_badge_countdown', label: 'Show Cutoff Badge + Countdown in Active Jobs' }
              ].map((item) => (
                <div key={item.key} className="flex items-center gap-2">
                  <input
                    id={`cutoff-${item.key}`}
                    type="checkbox"
                    checked={!!workflowAutomationConfig.global_cutoff[item.key]}
                    onChange={(e) => setWorkflowAutomationConfig(prev => ({
                      ...prev,
                      global_cutoff: { ...prev.global_cutoff, [item.key]: e.target.checked }
                    }))}
                  />
                  <label htmlFor={`cutoff-${item.key}`} className="text-sm text-gray-700">{item.label}</label>
                </div>
              ))}
            </div>

            {/* ── Client-Level Cutoff Rules (all clients in one table) ── */}
            <div className="border border-gray-200 rounded-lg">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-sm font-medium text-gray-800">Client-Level Cutoff Rules</div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Every client is listed. Pick a cutoff time to give a client a rule; set it back to
                    “No cutoff” to remove one. Times come from Global Cutoff Times above.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={downloadCutoffCsv}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-white"
                    title="Download all clients and their current cutoff rules"
                  >Download CSV</button>
                  <input
                    ref={cutoffCsvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleCutoffCsvFile}
                    className="hidden"
                  />
                  <button
                    onClick={() => cutoffCsvInputRef.current?.click()}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-white"
                    title="Upload an edited CSV to bulk-update cutoff rules"
                  >Upload CSV</button>
                </div>
              </div>

              {cutoffCsvSummary && (
                <div className="mx-4 mt-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-blue-900">
                      <p className="font-semibold">
                        {cutoffCsvSummary.fileName} — {cutoffCsvSummary.rows} row{cutoffCsvSummary.rows !== 1 ? 's' : ''} read.
                      </p>
                      <p className="mt-1">
                        <span className="text-green-700 font-medium">{cutoffCsvSummary.created} added</span>{'  ·  '}
                        <span className="text-blue-700 font-medium">{cutoffCsvSummary.updated} updated</span>{'  ·  '}
                        <span className="text-red-700 font-medium">{cutoffCsvSummary.cleared} removed</span>{'  ·  '}
                        <span className="text-gray-600">{cutoffCsvSummary.unchanged} unchanged</span>
                      </p>
                      {cutoffCsvSummary.badTimesTotal > 0 && (
                        <p className="mt-1 text-amber-800">
                          {cutoffCsvSummary.badTimesTotal} row{cutoffCsvSummary.badTimesTotal !== 1 ? 's' : ''} skipped — cutoff time
                          is not one of the Global Cutoff Times ({cutoffCsvSummary.validTimes.join(', ') || 'none configured'}):{' '}
                          {cutoffCsvSummary.badTimes.join('; ')}{cutoffCsvSummary.badTimesTotal > cutoffCsvSummary.badTimes.length ? '…' : ''}
                        </p>
                      )}
                      {cutoffCsvSummary.blankNames > 0 && (
                        <p className="mt-1 text-amber-800">{cutoffCsvSummary.blankNames} row(s) skipped — blank client name.</p>
                      )}
                      {(cutoffCsvSummary.created > 0 || cutoffCsvSummary.updated > 0 || cutoffCsvSummary.cleared > 0) && (
                        <p className="mt-2 font-semibold text-blue-900">Not saved yet — review below, then click Save Settings.</p>
                      )}
                    </div>
                    <button onClick={() => setCutoffCsvSummary(null)} className="text-blue-400 hover:text-blue-700 shrink-0" title="Dismiss">×</button>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="p-4 flex flex-wrap items-end gap-3 border-b border-gray-100">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Client</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={cutoffClientFilter}
                      onChange={(e) => setCutoffClientFilter(e.target.value)}
                      placeholder="Search client…"
                      className="border border-gray-300 rounded-md px-2 py-2 text-sm w-56 pr-7"
                    />
                    {cutoffClientFilter && (
                      <button
                        onClick={() => setCutoffClientFilter('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        title="Clear"
                      >×</button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Status</label>
                  <select
                    value={cutoffStatusFilter}
                    onChange={(e) => setCutoffStatusFilter(e.target.value)}
                    className="border border-gray-300 rounded-md px-2 py-2 text-sm"
                  >
                    <option value="all">All clients</option>
                    <option value="configured">With a cutoff</option>
                    <option value="unconfigured">No cutoff set</option>
                    <option value="disabled">Cutoff disabled</option>
                  </select>
                </div>
                <div className="text-xs text-gray-500 pb-2">
                  {getCutoffRows().length} of {(clients || []).length} client{(clients || []).length !== 1 ? 's' : ''} shown
                  {' · '}
                  {(workflowAutomationConfig.client_cutoff_rules || []).filter(r => r.cutoff_time).length} configured
                </div>
              </div>

              {cutoffTimes().length === 0 ? (
                <div className="px-4 py-6 text-sm text-gray-500 text-center">
                  Add at least one time under <span className="font-medium">Global Cutoff Times</span> before setting client rules.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-center px-3 py-2 font-medium w-14 border-b border-gray-200">S.No</th>
                        <th className="text-left px-3 py-2 font-medium border-b border-gray-200">Client</th>
                        <th className="text-left px-3 py-2 font-medium border-b border-gray-200 w-44">Cutoff Time</th>
                        <th className="text-left px-3 py-2 font-medium border-b border-gray-200 w-32">Grace (mins)</th>
                        <th className="text-center px-3 py-2 font-medium border-b border-gray-200 w-24">Enabled</th>
                        <th className="text-right px-3 py-2 font-medium border-b border-gray-200 w-24">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {getCutoffRows().length === 0 ? (
                        <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">No clients match this filter.</td></tr>
                      ) : getCutoffRows().map((row, idx) => {
                        const rule = row.rule;
                        const has = !!rule?.cutoff_time;
                        return (
                          <tr key={`${row.name}-${idx}`} className={`even:bg-gray-50 bg-white ${has ? '' : 'text-gray-500'}`}>
                            <td className="px-3 py-2 text-center text-gray-500">{idx + 1}</td>
                            <td className="px-3 py-2 font-medium text-gray-800">
                              {row.name}
                              {row.orphan && (
                                <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                                  title="This rule refers to a client that is no longer in the customer list">
                                  NOT IN CLIENT LIST
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <select
                                value={rule?.cutoff_time || ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (!v) upsertClientRule(row.name, null);
                                  else upsertClientRule(row.name, { cutoff_time: v });
                                }}
                                className={`border rounded-md px-2 py-1.5 text-sm w-full ${has ? 'border-gray-300' : 'border-gray-200 text-gray-400'}`}
                              >
                                <option value="">No cutoff</option>
                                {cutoffTimes().map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="0"
                                disabled={!has}
                                value={has ? (rule.grace_minutes ?? 0) : ''}
                                onChange={(e) => upsertClientRule(row.name, {
                                  grace_minutes: Math.max(0, parseInt(e.target.value || '0', 10) || 0),
                                })}
                                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm w-24 disabled:bg-gray-100 disabled:border-gray-200"
                              />
                            </td>
                            <td className="px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                disabled={!has}
                                checked={has ? rule.enabled !== false : false}
                                onChange={(e) => upsertClientRule(row.name, { enabled: e.target.checked })}
                                className="w-4 h-4 accent-blue-600 disabled:opacity-40"
                              />
                            </td>
                            <td className="px-3 py-2 text-right">
                              {has && (
                                <button
                                  onClick={() => upsertClientRule(row.name, null)}
                                  className="text-red-600 hover:text-red-800 text-xs"
                                >Remove</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { key: 'cutoff_completed_before_deadline', label: 'Alert: Cutoff Job Completed Before Deadline' },
                { key: 'auto_priority_changed', label: 'Alert: Auto-Priority Changed (PM + Lead)' },
                { key: 'non_priority_started_while_priority_waiting', label: 'Alert: Non-Priority Started While Priority Waiting' },
                { key: 'priority_job_hold_or_paused', label: 'Alert: Priority Job Put On Hold / Paused' },
                { key: 'cutoff_missed', label: 'Alert: Cutoff Missed' },
                { key: 'delayed_past_deadline', label: 'Alert: Job Delayed Past Deadline' }
              ].map(item => (
                <label key={item.key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!workflowAutomationConfig.alerts[item.key]}
                    onChange={(e) => setWorkflowAutomationConfig(prev => ({
                      ...prev,
                      alerts: { ...prev.alerts, [item.key]: e.target.checked }
                    }))}
                  />
                  {item.label}
                </label>
              ))}
            </div>
          </div>

        </div>
      )}

      {activeTab === 11 && (
        <div className="space-y-8">
          {/* Job Priorities drives the new auto job assign. The Skill Matrix
              below still gates who may be given what by hand. */}
          <JobPriorities />

          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">User Skill Mapping</h2>
              <p className="text-sm text-gray-500 mt-1">Tick the task / complexity each operator can handle. Columns come from Complexity Config.</p>
            </div>
            <button
              onClick={saveAdvancedOptions}
              disabled={advancedOptionsSaving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {advancedOptionsSaving ? 'Saving...' : advancedOptionsSaved ? '✓ Saved' : 'Save Settings'}
            </button>
          </div>

          {/* ── Department Skill Matrix ── */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-base font-semibold text-gray-800">Skill Matrix</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Departments (tasks) and complexity columns come from{' '}
                  <span className="font-medium text-gray-700">Complexity Config</span>. Tick the box
                  where an operator can handle that task at that complexity. Use the row and column
                  controls to set or clear a whole department at once.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={downloadSkillMatrixCsv}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  title="Download every user's current skill settings as a CSV"
                >
                  Download CSV
                </button>
                <input
                  ref={skillCsvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleSkillCsvFile}
                  className="hidden"
                />
                <button
                  onClick={() => skillCsvInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  title="Upload an edited CSV to bulk-update the matrix"
                >
                  Upload CSV
                </button>
              </div>
            </div>

            <p className="text-xs text-gray-500 -mt-2">
              Download the CSV, edit it in Excel, then upload it back. Put <span className="font-medium">Yes</span> in a
              cell to grant a skill and leave it blank to remove one. Keep the <span className="font-mono">user_id</span> column
              intact — it is how rows are matched. Only the users and columns present in the file are changed.
            </p>

            {skillCsvSummary && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-blue-900">
                    <p className="font-semibold">
                      {skillCsvSummary.fileName} — {skillCsvSummary.matched} of {skillCsvSummary.rows} row
                      {skillCsvSummary.rows !== 1 ? 's' : ''} matched, {skillCsvSummary.columns} skill column
                      {skillCsvSummary.columns !== 1 ? 's' : ''} read.
                    </p>
                    <p className="mt-1">
                      <span className="text-green-700 font-medium">+{skillCsvSummary.added} skill{skillCsvSummary.added !== 1 ? 's' : ''} granted</span>
                      {'  ·  '}
                      <span className="text-red-700 font-medium">−{skillCsvSummary.removed} removed</span>
                      {skillCsvSummary.added === 0 && skillCsvSummary.removed === 0 && (
                        <span className="text-gray-600"> — nothing changed</span>
                      )}
                    </p>
                    {skillCsvSummary.notFoundTotal > 0 && (
                      <p className="mt-1 text-amber-800">
                        {skillCsvSummary.notFoundTotal} row{skillCsvSummary.notFoundTotal !== 1 ? 's' : ''} skipped — no matching
                        user: {skillCsvSummary.notFound.join(', ')}{skillCsvSummary.notFoundTotal > skillCsvSummary.notFound.length ? '…' : ''}
                      </p>
                    )}
                    {skillCsvSummary.unknownColsTotal > 0 && (
                      <p className="mt-1 text-amber-800">
                        {skillCsvSummary.unknownColsTotal} unrecognised column{skillCsvSummary.unknownColsTotal !== 1 ? 's' : ''} ignored:{' '}
                        {skillCsvSummary.unknownCols.join(', ')}{skillCsvSummary.unknownColsTotal > skillCsvSummary.unknownCols.length ? '…' : ''}
                      </p>
                    )}
                    {(skillCsvSummary.added > 0 || skillCsvSummary.removed > 0) && (
                      <p className="mt-2 font-semibold text-blue-900">
                        Not saved yet — review the table below, then click Save Settings.
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setSkillCsvSummary(null)}
                    className="text-blue-400 hover:text-blue-700 shrink-0"
                    title="Dismiss"
                  >×</button>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Department</label>
                <select
                  value={skillDeptFilter}
                  onChange={(e) => setSkillDeptFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-2 py-2 text-sm"
                >
                  <option value="all">All departments</option>
                  {getSkillDepartments().map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Role / Team</label>
                <select
                  value={skillRoleFilter}
                  onChange={(e) => setSkillRoleFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-2 py-2 text-sm"
                >
                  <option value="all">All roles</option>
                  {getUserRoles().map(r => <option key={r} value={r}>{prettyRole(r)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Operator Name</label>
                <div className="relative">
                  <input
                    type="text"
                    value={skillNameFilter}
                    onChange={(e) => setSkillNameFilter(e.target.value)}
                    placeholder="Search operator…"
                    className="border border-gray-300 rounded-md px-2 py-2 text-sm w-48 pr-7"
                  />
                  {skillNameFilter && (
                    <button
                      onClick={() => setSkillNameFilter('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Clear"
                    >×</button>
                  )}
                </div>
              </div>
            </div>

            {/* Matrix table */}
            {getMatrixComplexities().length === 0 ? (
              <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-200 rounded-lg">
                No complexity levels found. Add them in Settings &rarr; Complexity Config first.
              </div>
            ) : (() => {
              const visibleDepts = getSkillDepartments().filter(d => skillDeptFilter === 'all' || d === skillDeptFilter);
              const totalSkillCols = visibleDepts.reduce((a, d) => a + getSkillsForDept(d).length, 0);
              return (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th rowSpan={2} className="sticky left-0 z-10 bg-orange-500 text-white px-3 py-2 border border-orange-400 text-center">S.No</th>
                      <th rowSpan={2} className="sticky left-12 z-10 bg-orange-500 text-white px-3 py-2 border border-orange-400 text-left">Operator Name</th>
                      {visibleDepts.map(dept => (
                        <th key={dept} colSpan={getSkillsForDept(dept).length} className="bg-orange-300 text-gray-900 px-3 py-2 border border-orange-200 text-center font-semibold">
                          {dept}
                        </th>
                      ))}
                      <th rowSpan={2} className="bg-orange-500 text-white px-3 py-2 border border-orange-400 text-center whitespace-nowrap">All</th>
                    </tr>
                    <tr>
                      {visibleDepts.map(dept => getSkillsForDept(dept).map(s => (
                        <th key={s.id} className="bg-orange-100 text-gray-800 px-3 py-2 border border-orange-200 text-center whitespace-nowrap">
                          {s.name}
                        </th>
                      )))}
                    </tr>
                  </thead>
                  <tbody>
                    {getMatrixUsers().length === 0 ? (
                      <tr>
                        <td colSpan={3 + totalSkillCols} className="px-3 py-4 text-center text-gray-500">
                          No users match this filter.
                        </td>
                      </tr>
                    ) : (
                      getMatrixUsers().map((u, idx) => (
                        <tr key={u._id} className="even:bg-gray-50 bg-white">
                          <td className="sticky left-0 z-10 bg-inherit px-3 py-2 border border-gray-200 even:bg-gray-50 bg-white text-center text-gray-600">{idx + 1}</td>
                          <td className="sticky left-12 z-10 bg-inherit px-3 py-2 border border-gray-200 font-medium text-gray-800 whitespace-nowrap">{u.name}</td>
                          {visibleDepts.map(dept => getSkillsForDept(dept).map((s, sIdx) => {
                            const checked = isMatrixCellChecked(u._id, s.id);
                            const firstInDept = sIdx === 0;
                            return (
                              <td
                                key={s.id}
                                className={`px-1 py-1 border border-gray-200 text-center ${checked ? 'bg-green-50' : ''} ${firstInDept ? 'border-l-2 border-l-orange-200' : ''}`}
                                onClick={() => toggleMatrixCell(u._id, s.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => { e.stopPropagation(); setMatrixCell(u._id, s.id, e.target.checked); }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-4 h-4 accent-green-600 cursor-pointer"
                                  title={`${u.name} / ${dept} / ${s.name}`}
                                />
                              </td>
                            );
                          }))}
                          <td className="px-2 py-1 border border-gray-200 text-center whitespace-nowrap">
                            <button
                              onClick={() => setUserAllSkills(u._id, true)}
                              className="text-xs text-green-700 hover:underline mr-1"
                              title="Tick every visible skill"
                            >All</button>
                            <span className="text-gray-300">|</span>
                            <button
                              onClick={() => setUserAllSkills(u._id, false)}
                              className="text-xs text-gray-500 hover:underline ml-1"
                              title="Clear every visible skill"
                            >None</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {getMatrixUsers().length > 0 && visibleDepts.length > 0 && (
                    <tfoot>
                      <tr className="bg-gray-50">
                        <td colSpan={2} className="sticky left-0 z-10 bg-gray-50 px-3 py-2 border border-gray-200 text-xs font-medium text-gray-600 text-right">
                          Fill department:
                        </td>
                        {visibleDepts.map(dept => (
                          <td key={dept} colSpan={getSkillsForDept(dept).length} className="px-2 py-2 border border-gray-200 text-center whitespace-nowrap">
                            <button
                              onClick={() => getMatrixUsers().forEach(u => setUserDeptSkills(u._id, dept, true))}
                              className="text-xs text-green-700 hover:underline mr-1"
                              title={`Tick all ${dept} skills for every user shown`}
                            >Set all</button>
                            <span className="text-gray-300">|</span>
                            <button
                              onClick={() => getMatrixUsers().forEach(u => setUserDeptSkills(u._id, dept, false))}
                              className="text-xs text-gray-500 hover:underline ml-1"
                              title={`Clear all ${dept} skills for every user shown`}
                            >Clear</button>
                          </td>
                        ))}
                        <td className="border border-gray-200" />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              );
            })()}
          </div>
        </div>
      )}

      {activeTab === 12 && (
        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Job Auto-Assign</h2>
              <p className="text-sm text-gray-500 mt-1">Configure intelligent task distribution, request queue behavior, and scoring logic.</p>
            </div>
            <button
              onClick={saveAdvancedOptions}
              disabled={advancedOptionsSaving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {advancedOptionsSaving ? 'Saving...' : advancedOptionsSaved ? '✓ Saved' : 'Save Settings'}
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
            <h3 className="text-base font-semibold text-gray-800">Job Auto-Assign Settings</h3>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 md:col-span-2">
                <input
                  type="checkbox"
                  checked={!!skillAutoAssignConfig.auto_assign_enabled}
                  onChange={(e) => setSkillAutoAssignConfig(prev => ({ ...prev, auto_assign_enabled: e.target.checked }))}
                />
                Enable Auto-Assign Algorithm
              </label>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Minimum Proficiency</label>
                <select
                  value={skillAutoAssignConfig.minimum_proficiency}
                  onChange={(e) => setSkillAutoAssignConfig(prev => ({ ...prev, minimum_proficiency: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="expert">Expert</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={!!skillAutoAssignConfig.fallback_alert_notify_tl_pm}
                  onChange={(e) => setSkillAutoAssignConfig(prev => ({ ...prev, fallback_alert_notify_tl_pm: e.target.checked }))}
                />
                Fallback Alert to TL + PM
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={!!skillAutoAssignConfig.audit_log_enabled}
                  onChange={(e) => setSkillAutoAssignConfig(prev => ({ ...prev, audit_log_enabled: e.target.checked }))}
                />
                Auto-Assign Audit Log
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={!!skillAutoAssignConfig.request_job_enabled}
                  onChange={(e) => setSkillAutoAssignConfig(prev => ({ ...prev, request_job_enabled: e.target.checked }))}
                />
                Enable "Request Job" Button for Operators
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={!!skillAutoAssignConfig.request_queue_enabled}
                  onChange={(e) => setSkillAutoAssignConfig(prev => ({ ...prev, request_queue_enabled: e.target.checked }))}
                />
                Enable Job Request Queue
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={!!skillAutoAssignConfig.cutoff_aware_matching}
                  onChange={(e) => setSkillAutoAssignConfig(prev => ({ ...prev, cutoff_aware_matching: e.target.checked }))}
                />
                Cutoff-Aware Matching (nearest deadline first)
              </label>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-sm font-medium text-gray-800 mb-3">Auto-Assign Scoring Weights</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { key: 'proficiency', label: 'Proficiency' },
                  { key: 'workload', label: 'Workload' },
                  { key: 'cutoff_urgency', label: 'Cutoff Urgency' }
                ].map(item => (
                  <div key={item.key}>
                    <label className="block text-xs text-gray-600 mb-1">{item.label} Weight</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={skillAutoAssignConfig.scoring_weights[item.key]}
                      onChange={(e) => setSkillAutoAssignConfig(prev => ({
                        ...prev,
                        scoring_weights: {
                          ...prev.scoring_weights,
                          [item.key]: Math.max(0, Math.min(100, parseInt(e.target.value || '0', 10)))
                        }
                      }))}
                      className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Customer Accounts & Portal Settings (tab 3) ── */}
      {activeTab === 3 && (
        <div className="space-y-8">

          {/* Portal Settings card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Customer Portal Settings</h2>
                <p className="text-sm text-gray-500 mt-1">Global settings for the external customer-facing portal.</p>
              </div>
              <button
                onClick={savePortalSettings}
                disabled={portalSettingsSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {portalSettingsSaving ? 'Saving…' : portalSettingsSaved ? '✓ Saved' : 'Save Settings'}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setPortalSettings(p => ({ ...p, portal_enabled: !p.portal_enabled }))}
                >
                  {portalSettings.portal_enabled
                    ? <ToggleRight className="w-9 h-9 text-blue-600" />
                    : <ToggleLeft className="w-9 h-9 text-gray-400" />}
                </button>
                <span className="text-sm font-medium text-gray-700">Portal Enabled</span>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Approval Reminder Interval (minutes)</label>
                <input
                  type="number"
                  min="5"
                  max="1440"
                  value={portalSettings.reminder_interval_minutes}
                  onChange={e => setPortalSettings(p => ({ ...p, reminder_interval_minutes: parseInt(e.target.value) || 30 }))}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm w-32"
                />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-xs font-medium text-gray-600 mb-2">Status Badge Colors</p>
              <div className="flex flex-row flex-wrap gap-4">
                {Object.entries(portalSettings.status_colors || {}).map(([key, color]) => (
                  <div key={key} className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 capitalize">{key.replace('_', ' ')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={color}
                        onChange={e => setPortalSettings(p => ({ ...p, status_colors: { ...p.status_colors, [key]: e.target.value } }))}
                        className="w-8 h-8 rounded cursor-pointer border border-gray-300"
                      />
                      <span className="text-xs text-gray-600">{color}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Customer Accounts card */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Customer Accounts</h2>
                <p className="text-sm text-gray-500 mt-1">Manage login accounts for external customers accessing the portal.</p>
              </div>
              <button
                onClick={() => handleOpenCustomerDialog()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" /> New Customer
              </button>
            </div>

            {/* Search / filter row */}
            <div className="flex flex-wrap gap-3 mb-4">
              <input
                type="text"
                value={customerSearchTerm}
                onChange={e => setCustomerSearchTerm(e.target.value)}
                placeholder="Search by name, email or company…"
                className="flex-1 min-w-[220px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {customerSearchTerm && (
                <button
                  onClick={() => setCustomerSearchTerm('')}
                  className="px-3 py-2 text-sm text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Clear
                </button>
              )}
            </div>

            {customerLoading ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : customerAccounts.length === 0 ? (
              <p className="text-sm text-gray-400 py-8 text-center">No customer accounts yet. Click "New Customer" to create one.</p>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Name</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Email</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Client</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {customerAccounts.filter(cust => {
                      const q = customerSearchTerm.toLowerCase();
                      if (!q) return true;
                      return (
                        cust.name?.toLowerCase().includes(q) ||
                        cust.email?.toLowerCase().includes(q) ||
                        cust.client_name?.toLowerCase().includes(q)
                      );
                    }).map(cust => (
                      <tr key={cust._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-800">{cust.name}</td>
                        <td className="px-4 py-3 text-gray-600">{cust.email}</td>
                        <td className="px-4 py-3 text-gray-600">{cust.client_name || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cust.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {cust.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right space-x-2">
                          <button onClick={() => handleOpenCustomerDialog(cust)} className="text-blue-600 hover:text-blue-800">
                            <Edit className="w-4 h-4 inline" />
                          </button>
                          <button onClick={() => handleDeleteCustomer(cust.id)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-4 h-4 inline" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {customerAccounts.length > 0 && customerAccounts.filter(cust => {
                      const q = customerSearchTerm.toLowerCase();
                      if (!q) return true;
                      return cust.name?.toLowerCase().includes(q) || cust.email?.toLowerCase().includes(q) || cust.client_name?.toLowerCase().includes(q);
                    }).length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-gray-400 text-sm">
                          No customers match your search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 14 && (
        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Shift Timings</h2>
              <p className="text-sm text-gray-500 mt-1">
                Define the named shift windows used across the app. Assign them to users
                on the Shifts page.
              </p>
            </div>
            <button
              onClick={saveShiftTemplates}
              disabled={shiftTemplatesSaving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {shiftTemplatesSaving ? 'Saving...' : shiftTemplatesSaved ? '✓ Saved' : 'Save Shift Timings'}
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Shift Name</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Start (24h)</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">End (24h)</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Colour</th>
                    <th className="px-4 py-2 text-center font-semibold text-gray-700">Meals (eligible)</th>
                    <th className="px-4 py-2 text-center font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {shiftTemplates.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                        No shifts defined. Click "Add Shift" to create one.
                      </td>
                    </tr>
                  ) : (
                    shiftTemplates.map((t, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={t.name}
                            onChange={(e) => updateShiftTemplate(idx, 'name', e.target.value)}
                            placeholder="e.g. Morning"
                            className="border border-gray-300 rounded-md px-2 py-1 text-sm w-40"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="time"
                            value={t.start}
                            onChange={(e) => updateShiftTemplate(idx, 'start', e.target.value)}
                            className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="time"
                            value={t.end}
                            onChange={(e) => updateShiftTemplate(idx, 'end', e.target.value)}
                            className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="color"
                            value={t.color || '#93c5fd'}
                            onChange={(e) => updateShiftTemplate(idx, 'color', e.target.value)}
                            className="w-10 h-8 border border-gray-300 rounded cursor-pointer"
                            title="Badge colour"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center justify-center gap-3">
                            {['lunch', 'dinner', 'snacks'].map((meal) => (
                              <label key={meal} className="flex items-center gap-1 text-xs text-gray-600 capitalize cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={!!(t.meals || {})[meal]}
                                  onChange={() => toggleTemplateMeal(idx, meal)}
                                  className="accent-blue-600"
                                />
                                {meal}
                              </label>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            onClick={() => removeShiftTemplate(idx)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >Remove</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <button
              onClick={addShiftTemplate}
              className="px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 text-sm"
            >+ Add Shift</button>
            <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-xs text-blue-800">
              Night shifts that cross midnight are fine — e.g. start 22:00, end 06:00.
              Tick which company meals each shift's people are eligible for.
            </div>
          </div>

          {/* Meal cutoffs (cafeteria) */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-base font-semibold text-gray-800">Meal Cutoff Times</h3>
                <p className="text-sm text-gray-500 mt-1">
                  The daily deadline to lock each meal's headcount for the vendor.
                </p>
              </div>
              <button
                onClick={saveMealSettings}
                disabled={mealSettingsSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {mealSettingsSaving ? 'Saving...' : mealSettingsSaved ? '✓ Saved' : 'Save Cutoffs'}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {['lunch', 'dinner', 'snacks'].map((meal) => (
                <div key={meal}>
                  <label className="block text-xs text-gray-600 mb-1 capitalize">{meal} cutoff</label>
                  <input
                    type="time"
                    value={(mealSettings.cutoffs || {})[meal] || ''}
                    onChange={(e) => updateMealCutoff(meal, e.target.value)}
                    className="border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </div>
              ))}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-xs text-amber-800">
              After a meal's cutoff, that day's count is locked. Users on approved full-day
              leave (approved before the cutoff) and those who opted out are not counted.
            </div>
          </div>

          {/* Break allowances */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-base font-semibold text-gray-800">Break Allowances</h3>
                <p className="text-sm text-gray-500 mt-1">
                  How long each break may last before a user is blocked from resuming work.
                </p>
              </div>
              <button
                onClick={saveBreakLimits}
                disabled={breakLimitsSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {breakLimitsSaving ? 'Saving...' : breakLimitsSaved ? '✓ Saved' : 'Save Allowances'}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {['lunch', 'dinner', 'snacks'].map((bt) => (
                <div key={bt}>
                  <label className="block text-xs text-gray-600 mb-1 capitalize">{bt} (minutes)</label>
                  <input
                    type="number"
                    min="1"
                    max="480"
                    value={(breakLimits || {})[bt] ?? ''}
                    onChange={(e) => {
                      setBreakLimits((prev) => ({ ...prev, [bt]: e.target.value }));
                      setBreakLimitsSaved(false);
                    }}
                    className="border border-gray-300 rounded-md px-2 py-2 text-sm w-32"
                  />
                </div>
              ))}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-xs text-amber-800">
              Each break type is checked on its own — going over <span className="font-medium">any single</span> allowance
              blocks the user; the total across types is not checked. Restroom breaks are not limited.
              A blocked user can be released for the day from User Management, or exempted permanently
              with “Exempt from break overtime block”.
            </div>

            {/* Long-shift threshold lives here because it is the same kind of
                setting and saves through the same endpoint. */}
            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-sm font-semibold text-gray-800">Long Shift Limit</h4>
              <p className="text-sm text-gray-500 mt-1 mb-3">
                A shift longer than this asks the worker for a reason when they clock out,
                and appears in the next morning's Late &amp; Break Overrun report.
              </p>
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Hours</label>
                  <input
                    type="number" min="1" max="24"
                    value={Math.floor((Number(longShiftMinutes) || 0) / 60)}
                    onChange={(e) => {
                      const h = Math.max(0, Number(e.target.value) || 0);
                      setLongShiftMinutes(h * 60 + ((Number(longShiftMinutes) || 0) % 60));
                      setBreakLimitsSaved(false);
                    }}
                    className="border border-gray-300 rounded-md px-2 py-2 text-sm w-24"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Minutes</label>
                  <input
                    type="number" min="0" max="59"
                    value={(Number(longShiftMinutes) || 0) % 60}
                    onChange={(e) => {
                      const m = Math.min(59, Math.max(0, Number(e.target.value) || 0));
                      setLongShiftMinutes(Math.floor((Number(longShiftMinutes) || 0) / 60) * 60 + m);
                      setBreakLimitsSaved(false);
                    }}
                    className="border border-gray-300 rounded-md px-2 py-2 text-sm w-24"
                  />
                </div>
                <span className="text-xs text-gray-500 pb-2">
                  = {longShiftMinutes} minutes. Saved with the allowances above.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 15 && (
        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Leaves</h2>
              <p className="text-sm text-gray-500 mt-1">
                Manage leave types, employee categories and their annual quotas.
              </p>
            </div>
          </div>

          {/* Leave Types */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-semibold text-gray-800">Leave Types</h3>
              <button
                onClick={saveLeaveTypes}
                disabled={leaveTypesSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {leaveTypesSaving ? 'Saving...' : leaveTypesSaved ? '✓ Saved' : 'Save Leave Types'}
              </button>
            </div>
            <p className="text-sm text-gray-500 -mt-1">The categories users choose from when requesting time off.</p>
            <div className="flex flex-wrap items-center gap-2">
              {leaveTypes.length === 0 ? (
                <span className="text-sm text-gray-500">No leave types yet. Add one below.</span>
              ) : (
                leaveTypes.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 rounded-full px-3 py-1 text-sm text-gray-700">
                    {t}
                    <button onClick={() => removeLeaveType(t)} className="text-gray-400 hover:text-red-600" title="Remove">×</button>
                  </span>
                ))
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newLeaveType}
                onChange={(e) => setNewLeaveType(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addLeaveType(); }}
                placeholder="e.g. Maternity"
                className="border border-gray-300 rounded-md px-3 py-2 text-sm w-56"
              />
              <button onClick={addLeaveType} className="px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 text-sm">Add Type</button>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-base font-semibold text-gray-800">Entitlements</h3>
              <p className="text-sm text-gray-500 mt-1">
                Define employee categories with an annual quota per leave type, then assign a
                category to each user. Balances reset each calendar year.
              </p>
            </div>
            <button
              onClick={saveLeaveCategories}
              disabled={leaveCategoriesSaving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {leaveCategoriesSaving ? 'Saving...' : leaveCategoriesSaved ? '✓ Saved' : 'Save Categories'}
            </button>
          </div>

          {/* Categories with per-type quotas */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <h4 className="text-sm font-semibold text-gray-700">Categories &amp; Annual Quotas</h4>
            {leaveTypes.length === 0 ? (
              <div className="text-sm text-gray-500">Add leave types above first.</div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-2 text-left font-semibold text-gray-700 sticky left-0 bg-gray-50">Category</th>
                      {leaveTypes.map((t) => (
                        <th key={t} className="px-3 py-2 text-center font-semibold text-gray-700 whitespace-nowrap">{t}</th>
                      ))}
                      <th className="px-3 py-2 text-center font-semibold text-gray-700">Total</th>
                      <th className="px-3 py-2 text-center font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {leaveCategories.length === 0 ? (
                      <tr>
                        <td colSpan={leaveTypes.length + 3} className="px-4 py-6 text-center text-gray-500">
                          No categories yet. Click "Add Category".
                        </td>
                      </tr>
                    ) : (
                      leaveCategories.map((c, idx) => {
                        const total = leaveTypes.reduce((sum, t) => sum + (Number(c.quotas?.[t]) || 0), 0);
                        return (
                          <tr key={idx}>
                            <td className="px-4 py-2 sticky left-0 bg-white">
                              <input
                                type="text"
                                value={c.name}
                                onChange={(e) => updateLeaveCategoryName(idx, e.target.value)}
                                placeholder="e.g. Permanent"
                                className="border border-gray-300 rounded-md px-2 py-1 text-sm w-36"
                              />
                            </td>
                            {leaveTypes.map((t) => (
                              <td key={t} className="px-3 py-2 text-center">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  value={c.quotas?.[t] ?? ''}
                                  onChange={(e) => updateLeaveCategoryQuota(idx, t, e.target.value)}
                                  placeholder="0"
                                  className="border border-gray-300 rounded-md px-2 py-1 text-sm w-16 text-center"
                                />
                              </td>
                            ))}
                            <td className="px-3 py-2 text-center font-medium text-gray-800">{total}</td>
                            <td className="px-3 py-2 text-center">
                              <button onClick={() => removeLeaveCategory(idx)} className="text-red-600 hover:text-red-800 text-sm">Remove</button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <button onClick={addLeaveCategory} className="px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 text-sm">+ Add Category</button>
            <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-xs text-blue-800">
              Leave types with no quota for a category (blank/0) are treated as not allowed for that
              category. Types like "Unpaid" can be left blank — they are never counted against a quota.
            </div>
          </div>

          {/* User → category assignment */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <h3 className="text-base font-semibold text-gray-800">Assign Category to Users</h3>
            <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[28rem] overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0">
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">User</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Role</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Category</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {usersLeaveCat.length === 0 ? (
                    <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-500">No users.</td></tr>
                  ) : (
                    usersLeaveCat.map((u) => (
                      <tr key={u.id}>
                        <td className="px-4 py-2 font-medium text-gray-800">{u.name}</td>
                        <td className="px-4 py-2 text-gray-600 capitalize">{(u.role || '').replace(/_/g, ' ')}</td>
                        <td className="px-4 py-2">
                          <select
                            value={u.leave_category || ''}
                            onChange={(e) => assignUserLeaveCategory(u.id, e.target.value)}
                            className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                          >
                            <option value="">— None —</option>
                            {leaveCategories.map((c) => (
                              <option key={c.id || c.name} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500">Users with no category have no quota limit (leaves are not counted).</p>
          </div>
        </div>
      )}

      {activeTab === 17 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Complete Projects (Bulk)</h2>
            <p className="text-sm text-gray-500 mt-1">
              Force-complete active projects by job number. Each active task is completed and
              attributed to the user who started it (completed just after their start time), then
              the project is moved to Completed Projects.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
            <span className="font-medium">Caution:</span> this is irreversible. <span className="font-medium">All</span>
            active tasks are force-completed — started ones credited to the worker who started them, never-started
            ones credited to their assignee (or you). Job numbers are matched by job code.
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="text-sm font-medium text-gray-800">Job numbers</label>
              <label className="text-sm text-blue-600 hover:underline cursor-pointer">
                Upload CSV
                <input type="file" accept=".csv,.txt" onChange={onBulkCsvFile} className="hidden" />
              </label>
            </div>
            <textarea
              value={bulkJobText}
              onChange={(e) => setBulkJobText(e.target.value)}
              rows={6}
              placeholder="Paste job numbers — one per line, or comma-separated. CSV is also accepted."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
            />
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">{parseJobCodes(bulkJobText).length} job number(s) detected</span>
              <button
                onClick={runBulkComplete}
                disabled={bulkRunning}
                className="ml-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
              >
                {bulkRunning ? 'Completing…' : 'Complete & Archive'}
              </button>
            </div>
          </div>

          {bulkResult && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
              <h3 className="text-base font-semibold text-gray-800">Result</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-center">
                {[
                  ['Completed', bulkResult.summary?.completed, 'text-green-700 bg-green-50'],
                  ['Skipped (unstarted)', bulkResult.summary?.skipped_unstarted, 'text-amber-700 bg-amber-50'],
                  ['Not found', bulkResult.summary?.not_found, 'text-gray-700 bg-gray-50'],
                  ['Already archived', bulkResult.summary?.already_archived, 'text-blue-700 bg-blue-50'],
                  ['No active tasks', bulkResult.summary?.no_active_tasks, 'text-gray-700 bg-gray-50'],
                  ['Errors', bulkResult.summary?.errors, 'text-red-700 bg-red-50'],
                ].map(([label, val, cls]) => (
                  <div key={label} className={`rounded-lg p-3 ${cls}`}>
                    <div className="text-2xl font-bold">{val || 0}</div>
                    <div className="text-[11px]">{label}</div>
                  </div>
                ))}
              </div>

              {bulkResult.skipped_unstarted?.length > 0 && (
                <details>
                  <summary className="text-sm text-amber-700 cursor-pointer">Skipped — had unstarted tasks ({bulkResult.skipped_unstarted.length})</summary>
                  <div className="text-xs text-gray-600 mt-2 break-words">{bulkResult.skipped_unstarted.join(', ')}</div>
                </details>
              )}
              {bulkResult.not_found?.length > 0 && (
                <details>
                  <summary className="text-sm text-gray-700 cursor-pointer">Not found ({bulkResult.not_found.length})</summary>
                  <div className="text-xs text-gray-600 mt-2 break-words">{bulkResult.not_found.join(', ')}</div>
                </details>
              )}
              {bulkResult.errors?.length > 0 && (
                <details>
                  <summary className="text-sm text-red-700 cursor-pointer">Errors ({bulkResult.errors.length})</summary>
                  <ul className="text-xs text-gray-600 mt-2 space-y-0.5">
                    {bulkResult.errors.map((er, i) => <li key={i}>{er.job_code}: {er.error}</li>)}
                  </ul>
                </details>
              )}
              {bulkResult.completed?.length > 0 && (
                <details>
                  <summary className="text-sm text-green-700 cursor-pointer">Completed ({bulkResult.completed.length})</summary>
                  <ul className="text-xs text-gray-600 mt-2 space-y-0.5 max-h-60 overflow-y-auto">
                    {bulkResult.completed.map((c, i) => (
                      <li key={i}>#{c.job_code} — {c.tasks_completed} task(s) → {c.status}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}

        </div> {/* end tab content */}
      </div> {/* end sidebar+content flex */}

      {/* ── Customer Account Dialog ── */}
      {openCustomerDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {isEditingCustomer ? 'Edit Customer Account' : 'New Customer Account'}
              </h3>
              <button onClick={() => setOpenCustomerDialog(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {customerSaveError && (
                <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-2 text-sm">{customerSaveError}</div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                  <input
                    type="text"
                    value={currentCustomer.name}
                    onChange={e => setCurrentCustomer(p => ({ ...p, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Customer name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Primary Email *</label>
                  <input
                    type="email"
                    value={currentCustomer.email}
                    onChange={e => setCurrentCustomer(p => ({ ...p, email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="login@example.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {isEditingCustomer ? 'New Password (leave blank to keep)' : 'Password *'}
                  </label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="password"
                      value={currentCustomer.password}
                      onChange={e => setCurrentCustomer(p => ({ ...p, password: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md pl-9 pr-3 py-2 text-sm"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Linked Client</label>
                  <select
                    value={currentCustomer.client_name}
                    onChange={e => setCurrentCustomer(p => ({ ...p, client_name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">— None —</option>
                    {clients.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-3">
                <button onClick={() => setCurrentCustomer(p => ({ ...p, active: !p.active }))}>
                  {currentCustomer.active
                    ? <ToggleRight className="w-9 h-9 text-blue-600" />
                    : <ToggleLeft className="w-9 h-9 text-gray-400" />}
                </button>
                <span className="text-sm text-gray-700">Account Active</span>
              </div>

              {/* Secondary emails */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Additional Notification Emails</label>
                <div className="space-y-2">
                  {(currentCustomer.secondary_emails || []).map((em, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="flex-1 border border-gray-200 rounded-md px-3 py-1.5 text-sm bg-gray-50 text-gray-700">{em}</span>
                      <button
                        onClick={() => setCurrentCustomer(p => ({ ...p, secondary_emails: p.secondary_emails.filter((_, idx) => idx !== i) }))}
                        className="text-red-400 hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <input
                      type="email"
                      value={customerNewSecEmail}
                      onChange={e => setCustomerNewSecEmail(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && customerNewSecEmail.trim()) {
                          setCurrentCustomer(p => ({ ...p, secondary_emails: [...p.secondary_emails, customerNewSecEmail.trim()] }));
                          setCustomerNewSecEmail('');
                        }
                      }}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="extra@example.com"
                    />
                    <button
                      onClick={() => {
                        if (customerNewSecEmail.trim()) {
                          setCurrentCustomer(p => ({ ...p, secondary_emails: [...p.secondary_emails, customerNewSecEmail.trim()] }));
                          setCustomerNewSecEmail('');
                        }
                      }}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm text-gray-700"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Notification preferences */}
              <div>
                <p className="text-xs font-medium text-gray-600 mb-2">Email Notification Preferences</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { key: 'stage_change', label: 'Stage Changes' },
                    { key: 'completed', label: 'Project Completed' },
                    { key: 'on_hold', label: 'Project On Hold' },
                    { key: 'approval_waiting', label: 'Approval Waiting' },
                    { key: 'approval_result', label: 'Approval Result' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!!(currentCustomer.notification_preferences || {})[key]}
                        onChange={e => setCurrentCustomer(p => ({
                          ...p,
                          notification_preferences: { ...p.notification_preferences, [key]: e.target.checked }
                        }))}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setOpenCustomerDialog(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSaveCustomer} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                {isEditingCustomer ? 'Save Changes' : 'Create Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;